import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Modal, Form, InputGroup, Dropdown, Collapse, ListGroup, Offcanvas, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Plus, Globe, Shield, CheckCircle, XCircle, ArrowClockwise, Gear, Search, ChevronDown, ChevronRight, CheckSquare, Square, ExclamationTriangle, InfoCircle, QuestionCircle, Envelope, Clipboard } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getApiUrl, useGetDomainsQuery, useCreateDomainMutation, useDeleteDomainMutation, useHardDeleteDomainMutation, useGetHiddenDomainsQuery, useDomainConfirmationMutation, useEnableDKIMMutation, useConfigureMailFromMutation, useConfigureTrackingDomainMutation, useLazyGetTrackingDomainStatusQuery, useUpdateDMARCMutation, useGetEmailAccountsQuery, useDomainDiscoveryQuery, useRoute53OwnershipQuery, useSetupBounceComplaintHandlingMutation, useSetupReceivingForDomainMutation, useGetSendPoolsQuery, useGetSendPoolProvidersQuery, useGetDomainRoutingPolicyQuery, useSetDomainRouteMutation, useUpdateDomainTypeMutation, useLazyGetDomainConnectionInfoQuery, useUpdateDomainSmtpCredentialsMutation, useUpdateDomainImapCredentialsMutation, useRevealDomainConnectionSecretMutation, useLazyGetOutboundSmtpComCredentialsQuery, usePutOutboundSmtpComCredentialsMutation, useRevealOutboundSmtpComCredentialsMutation, useLazyGetOutboundMailgunCredentialsQuery, usePutOutboundMailgunCredentialsMutation, useRevealOutboundMailgunCredentialsMutation, useConfigureSendgridDnsMutation, useLazyGetPoolCredentialsQuery, usePutPoolCredentialsMutation, useRevealPoolCredentialsMutation, useGetUserPreferencesQuery, useGetUnifiedEmailMonitoringQuery, marketingApi } from '../services/api';
import { useAccount } from '../contexts/AccountContext';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';
import DNSSetup from './DNSSetup';
import HelpSidebar from './HelpSidebar';
import EmailAuthWizardModal from './EmailAuthWizardModal';
import DomainForwardingModal from './DomainForwardingModal';
import EMMSectionHeader from './EMMSectionHeader';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
  toLocaleStringInTimezone,
} from '../utils/tenantTimezone';
import { resolveHostingAccountIdForDomain } from '../utils/hostingAccountDomainMapping';

const TRACKING_PROVIDER_DEFAULT = 'instantly';
const TRACKING_PROVIDER_OPTIONS = [
  { value: 'instantly', label: 'Instantly.ai', cnameTarget: 'prox.itrackly.com' },
  { value: 'apollo', label: 'Apollo.io', cnameTarget: 'track.apollo.io' },
];

function normalizeTrackingProvider(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'apollo' || raw === 'apollo.io' || raw === 'apollo_io') return 'apollo';
  return TRACKING_PROVIDER_DEFAULT;
}

function getTrackingProviderLabel(value) {
  const normalized = normalizeTrackingProvider(value);
  const option = TRACKING_PROVIDER_OPTIONS.find((item) => item.value === normalized);
  return option?.label || 'Instantly.ai';
}

function getTrackingCnameTargetForProvider(value) {
  const normalized = normalizeTrackingProvider(value);
  const option = TRACKING_PROVIDER_OPTIONS.find((item) => item.value === normalized);
  return option?.cnameTarget || 'prox.itrackly.com';
}

function normalizeRelayProviderKey(provider) {
  const normalized = String(provider || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  if (normalized === 'smtpcom') return 'smtp_com';
  if (normalized === 'twilio' || normalized === 'twilio_sendgrid') return 'sendgrid';
  if (normalized === 'elasticemail') return 'elastic_email';
  return normalized;
}

const RELAY_ACCOUNT_SUFFIX_PATTERN = /^[A-Za-z0-9]{4}$/;

function normalizeRelaySuffixInput(value) {
  return String(value || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 4);
}

const Domains = () => {
  if (typeof window !== 'undefined') {
    window.__DOMAINS_BUILD = 'domains-group-by-hosting-account-20260409';
  }
  const dispatch = useDispatch();
  const { selectedAccount, isAllAccountsSelected, accounts: allAccountsFromCtx, workspaceAwsAccounts } = useAccount();
  const allAccounts =
    workspaceAwsAccounts && workspaceAwsAccounts.length > 0 ? workspaceAwsAccounts : allAccountsFromCtx;
  /** Full linked hosting accounts — use for Domain Management grouping (never collapse by companyTenantId). */
  const accountsForGrouping =
    allAccountsFromCtx && allAccountsFromCtx.length > 0 ? allAccountsFromCtx : allAccounts;
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAllTenantDomainsModal, setShowAllTenantDomainsModal] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [selectedAccountForDomain, setSelectedAccountForDomain] = useState('');
  const [selectedHostProvider, setSelectedHostProvider] = useState('aws'); // 'aws', 'mailgun', 'mailreach'
  // Inbound is standardized on Postmark (managed by Kross Global) and is not user-configurable.
  const INBOUND_PROVIDER = 'postmark';
  const [mxAcknowledged, setMxAcknowledged] = useState(false);
  // Removed unused isAdding state
  const [showDNSSetup, setShowDNSSetup] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState('');
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState('');
  const [isSearchingDomain, setIsSearchingDomain] = useState(false);
  const [domainSearchResult, setDomainSearchResult] = useState(null);
  const [route53Result, setRoute53Result] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(null); // Track which domain is currently being verified (during API call)
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showHelpSidebar, setShowHelpSidebar] = useState(false);
  const [showExistingRecordsModal, setShowExistingRecordsModal] = useState(false);
  const [existingRecordsData, setExistingRecordsData] = useState(null);
  const [showNameserverUpdateModal, setShowNameserverUpdateModal] = useState(false);
  const [nameserverUpdateData, setNameserverUpdateData] = useState(null);
  // Track domains that are being verified (domain -> true) to persist processing state until verified
  const [verifyingDomains, setVerifyingDomains] = useState(() => {
    // Restore from localStorage on mount
    // Store as {domain: timestamp} to track when verification started
    try {
      const stored = localStorage.getItem('verifyingDomains');
      if (stored) {
        const data = JSON.parse(stored);
        // Handle both old format (array of strings) and new format (object with timestamps)
        let restoredMap = new Map();
        if (Array.isArray(data)) {
          // Old format - convert to new format with current timestamp
          data.forEach(domain => {
            restoredMap.set(domain, Date.now());
          });
        } else if (typeof data === 'object') {
          // New format - restore with timestamps
          Object.entries(data).forEach(([domain, timestamp]) => {
            restoredMap.set(domain, timestamp);
          });
        }
        console.log('🔄 Restored verifyingDomains from localStorage:', Array.from(restoredMap.keys()));
        return restoredMap;
      }
    } catch (error) {
      console.error('Error restoring verifyingDomains from localStorage:', error);
    }
    return new Map();
  });
  // Removed unused verificationStatus state
  const [isAddingToManager, setIsAddingToManager] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [enablingDKIMForDomain, setEnablingDKIMForDomain] = useState(null);
  const [showSandboxVerificationModal, setShowSandboxVerificationModal] = useState(false);
  const [sandboxVerificationDomain, setSandboxVerificationDomain] = useState(null);
  const [sandboxVerificationResults, setSandboxVerificationResults] = useState(null);
  const [showDMARCModal, setShowDMARCModal] = useState(false);
  const [dmarcEditingDomain, setDmarcEditingDomain] = useState(null);
  const [dmarcEmail, setDmarcEmail] = useState('');
  // Safe default: monitor-only (p=none) in BOTH staging + production initially.
  const [dmarcPolicy, setDmarcPolicy] = useState('none');
  // Email Auth wizard (per-domain only)
  const [showEmailAuthWizard, setShowEmailAuthWizard] = useState(false);
  const [emailAuthWizardDomain, setEmailAuthWizardDomain] = useState(null);
  // Outbound routing (per-domain)
  const [showOutboundModal, setShowOutboundModal] = useState(false);
  const [outboundDomainObj, setOutboundDomainObj] = useState(null);
  const [showForwardingModal, setShowForwardingModal] = useState(false);
  const [forwardingDomainObj, setForwardingDomainObj] = useState(null);
  const [outboundProviderType, setOutboundProviderType] = useState('');
  const [smtpComChannelLoginDraft, setSmtpComChannelLoginDraft] = useState('');
  const [smtpComChannelPasswordDraft, setSmtpComChannelPasswordDraft] = useState('');
  const [relayAccountSuffixDraft, setRelayAccountSuffixDraft] = useState('');
  const [revealedSmtpComPassword, setRevealedSmtpComPassword] = useState(null);
  const [outboundPrimaryPoolId, setOutboundPrimaryPoolId] = useState('');
  const [outboundApplySpf, setOutboundApplySpf] = useState(true);
  const [outboundError, setOutboundError] = useState('');
  const [outboundDomainType, setOutboundDomainType] = useState('business');
  const [trackingSubdomainDraft, setTrackingSubdomainDraft] = useState('inst');
  const [trackingProviderDraft, setTrackingProviderDraft] = useState(TRACKING_PROVIDER_DEFAULT);
  const TRACKING_AUTO_RECHECK_DELAYS_MS = useMemo(() => [10000, 20000, 40000, 60000, 120000, 300000], []);
  const [trackingAutoRecheckState, setTrackingAutoRecheckState] = useState({
    active: false,
    attempt: 0,
    maxAttempts: 0,
    nextDelayMs: 0,
    stopped: false,
    reason: '',
    domain: '',
  });
  const trackingAutoRecheckTimerRef = React.useRef(null);
  const trackingAutoRecheckRunRef = React.useRef(0);

  // Connection info (SMTP/IMAP) for tools like Instantly / iOS Mail
  const [triggerGetDomainConnectionInfo, domainConnectionInfoQuery] = useLazyGetDomainConnectionInfoQuery();
  const [updateDomainSmtpCredentials, { isLoading: isSavingSmtpCreds }] = useUpdateDomainSmtpCredentialsMutation();
  const [updateDomainImapCredentials, { isLoading: isSavingImapCreds }] = useUpdateDomainImapCredentialsMutation();
  const [revealDomainConnectionSecret, { isLoading: isRevealingSecret }] = useRevealDomainConnectionSecretMutation();
  const [triggerGetOutboundSmtpComCredentials, outboundSmtpComCredentialsQuery] = useLazyGetOutboundSmtpComCredentialsQuery();
  const [putOutboundSmtpComCredentials, { isLoading: isSavingSmtpComCreds }] = usePutOutboundSmtpComCredentialsMutation();
  const [revealOutboundSmtpComCredentials, { isLoading: isRevealingSmtpComCreds }] = useRevealOutboundSmtpComCredentialsMutation();
  const [triggerGetOutboundMailgunCredentials, outboundMailgunCredentialsQuery] = useLazyGetOutboundMailgunCredentialsQuery();
  const [putOutboundMailgunCredentials, { isLoading: isSavingMailgunCreds }] = usePutOutboundMailgunCredentialsMutation();
  const [revealOutboundMailgunCredentials, { isLoading: isRevealingMailgunCreds }] = useRevealOutboundMailgunCredentialsMutation();
  const [triggerGetPoolCredentials, poolCredentialsQuery] = useLazyGetPoolCredentialsQuery();
  const [putPoolCredentials, { isLoading: isSavingPoolCreds }] = usePutPoolCredentialsMutation();
  const [revealPoolCredentials, { isLoading: isRevealingPoolCreds }] = useRevealPoolCredentialsMutation();
  const [configureSendgridDns, { isLoading: isSavingSendgridDns }] = useConfigureSendgridDnsMutation();
  const [sendgridDnsRecords, setSendgridDnsRecords] = useState(() => [
    { type: 'CNAME', host: '', value: '' },
    { type: 'CNAME', host: '', value: '' },
    { type: 'CNAME', host: '', value: '' },
    { type: 'TXT', host: '', value: '' },
  ]);
  const [sendgridDnsSaved, setSendgridDnsSaved] = useState(false);
  const [sendgridDnsSavedRecords, setSendgridDnsSavedRecords] = useState([]);
  const [sendgridDnsSavedAt, setSendgridDnsSavedAt] = useState(null);
  const [sendgridDnsEditOpen, setSendgridDnsEditOpen] = useState(false);
  const [revealedSmtpPassword, setRevealedSmtpPassword] = useState(null);
  const [revealedImapPassword, setRevealedImapPassword] = useState(null);

  const [smtpEditOpen, setSmtpEditOpen] = useState(false);
  const [smtpUsernameDraft, setSmtpUsernameDraft] = useState('');
  const [smtpPasswordDraft, setSmtpPasswordDraft] = useState('');
  const [imapEditOpen, setImapEditOpen] = useState(false);
  const [imapPasswordDraft, setImapPasswordDraft] = useState('');
  const [smtpComCredsEditOpen, setSmtpComCredsEditOpen] = useState(false);

  React.useEffect(() => {
    // Auto-hide revealed secrets after 30s (defense-in-depth).
    if (!revealedSmtpPassword && !revealedImapPassword && !revealedSmtpComPassword) return;
    const t = setTimeout(() => {
      setRevealedSmtpPassword(null);
      setRevealedImapPassword(null);
      setRevealedSmtpComPassword(null);
    }, 30000);
    return () => clearTimeout(t);
  }, [revealedSmtpPassword, revealedImapPassword, revealedSmtpComPassword]);

  const copyToClipboard = async (label, value) => {
    try {
      await navigator.clipboard.writeText(String(value || ''));
      toast.success(`Copied ${label}`);
    } catch (e) {
      console.warn('Clipboard copy failed:', e);
      toast.error('Copy failed (browser blocked clipboard)');
    }
  };

  const promptConfirmDomain = (domain) => {
    const typed = window.prompt(`Type the domain to reveal the password:\n\n${domain}`);
    return String(typed || '').trim().toLowerCase() === String(domain || '').trim().toLowerCase();
  };

  const handleRevealSecret = async (kind) => {
    const domain = outboundDomainObj?.domain;
    if (!domain) return;
    if (!promptConfirmDomain(domain)) {
      toast.info('Cancelled (domain confirmation did not match)');
      return;
    }
    try {
      const res = await revealDomainConnectionSecret({ domain, kind, confirmDomain: domain }).unwrap();
      const value = res?.data?.value;
      if (!value) throw new Error('No value returned');
      if (kind === 'smtp') setRevealedSmtpPassword(value);
      if (kind === 'imap') setRevealedImapPassword(value);
      toast.success(`${kind.toUpperCase()} password revealed (auto-hides in 30s)`);
    } catch (e) {
      console.error('Reveal failed:', e);
      toast.error('Could not reveal password (not allowed or missing)');
    }
  };

  const handleRevealRelaySecret = async ({ usePoolCreds, providerType, poolId, domain }) => {
    if (!domain) return;
    if (!promptConfirmDomain(domain)) {
      toast.info('Cancelled (domain confirmation did not match)');
      return;
    }
    try {
      const providerTypeUpper = String(providerType || '').toUpperCase();
      let password = '';
      if (usePoolCreds && poolId) {
        const res = await revealPoolCredentials(poolId).unwrap();
        password = res?.data?.password || '';
      } else if (providerTypeUpper === 'MAILGUN') {
        const res = await revealOutboundMailgunCredentials({ domain }).unwrap();
        password = res?.data?.password || '';
      } else {
        const res = await revealOutboundSmtpComCredentials({ domain }).unwrap();
        password = res?.data?.password || '';
      }
      if (!password) throw new Error('No password returned');
      setRevealedSmtpComPassword(password);
      toast.success('Relay password revealed (auto-hides in 30s)');
    } catch (e) {
      toast.error(e?.data?.message || e?.message || 'Not authorized to reveal');
    }
  };

  const beginEditSmtpCreds = () => {
    const domain = outboundDomainObj?.domain;
    const smtp = domainConnectionInfoQuery?.data?.data?.smtp;
    const currentUsername =
      smtp?.username?.kind === 'fixed'
        ? smtp.username.value
        : smtp?.username?.example || (domain ? `admin@${domain}` : '');
    setSmtpUsernameDraft(String(currentUsername || ''));
    setSmtpPasswordDraft('');
    setSmtpEditOpen(true);
    // Never keep secrets open while editing
    setRevealedSmtpPassword(null);
  };

  const cancelEditSmtpCreds = () => {
    setSmtpEditOpen(false);
    setSmtpPasswordDraft('');
  };

  const beginEditImapCreds = () => {
    setImapPasswordDraft('');
    setImapEditOpen(true);
    setRevealedImapPassword(null);
  };

  const cancelEditImapCreds = () => {
    setImapEditOpen(false);
    setImapPasswordDraft('');
  };

  const saveImapCreds = async () => {
    try {
      const domain = outboundDomainObj?.domain;
      if (!domain) throw new Error('Domain is required');
      const password = String(imapPasswordDraft || '').trim();
      if (!password) {
        toast.error('IMAP password is required');
        return;
      }
      await updateDomainImapCredentials({ domain, imap_password: password }).unwrap();
      toast.success('IMAP shared password updated. To apply to the mail server, re-save Domain Purpose with Force re-provision if available.');
      setImapEditOpen(false);
      setImapPasswordDraft('');
      setRevealedImapPassword(null);
      triggerGetDomainConnectionInfo(domain);
    } catch (e) {
      console.error('IMAP creds save failed:', e);
      toast.error(e?.data?.message || e?.message || 'Failed to update IMAP credentials');
    }
  };

  const saveSmtpCreds = async () => {
    try {
      const domain = outboundDomainObj?.domain;
      if (!domain) throw new Error('Domain is required');
      const username = String(smtpUsernameDraft || '').trim();
      const password = String(smtpPasswordDraft || '').trim();

      // Allow username-only updates. Only send password if user typed one (avoid accidental rotation).
      const payload = {
        domain,
        smtp_username: username,
        ...(password ? { smtp_password: password } : {})
      };

      await updateDomainSmtpCredentials(payload).unwrap();
      toast.success('SMTP credentials updated');
      setSmtpEditOpen(false);
      setSmtpPasswordDraft('');
      setRevealedSmtpPassword(null);
      triggerGetDomainConnectionInfo(domain);
    } catch (e) {
      console.error('SMTP creds save failed:', e);
      toast.error(e?.data?.message || e?.message || 'Failed to update SMTP credentials');
    }
  };

  // (Domain Purpose is edited inside the Outbound modal; no separate Purpose editor modal.)
  // Track domains that are enabling DKIM (domain -> true) to persist processing state until verified
  const [enablingDKIMDomains, setEnablingDKIMDomains] = useState(() => {
    // Restore from localStorage on mount
    try {
      const stored = localStorage.getItem('enablingDKIMDomains');
      if (stored) {
        const domainsArray = JSON.parse(stored);
        return new Map(domainsArray.map(domain => [domain, true]));
      }
    } catch (error) {
      console.error('Error restoring enablingDKIMDomains from localStorage:', error);
    }
    return new Map();
  });
  const [bulkSettingUpGroup, setBulkSettingUpGroup] = useState(null);
  const [bulkSetupProgress, setBulkSetupProgress] = useState({ current: 0, total: 0, domain: '' });
  const [configuringMailFromForDomain, setConfiguringMailFromForDomain] = useState(null);
  // Track configured MAIL FROM domains (domain -> mailFromDomain) to persist processing state
  const [configuredMailFromDomains, setConfiguredMailFromDomains] = useState(() => {
    // Restore from localStorage on mount
    try {
      const stored = localStorage.getItem('configuredMailFromDomains');
      if (stored) {
        const domainsArray = JSON.parse(stored);
        return new Map(domainsArray);
      }
    } catch (error) {
      console.error('Error restoring configuredMailFromDomains from localStorage:', error);
    }
    return new Map();
  });
  
  // Hierarchical domain management state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  // Email Auth is now a per-domain wizard modal (no inline expand/collapse UI).
  const [selectedDomains, setSelectedDomains] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  const { 
    data: domainsData, 
    error, 
    isLoading: loading, 
    refetch: loadDomains 
  } = useGetDomainsQuery();

  // Debug: Log the actual data being received
  React.useEffect(() => {
    if (domainsData) {
      console.log('🔍 Frontend received domains data:', domainsData);
      if (domainsData.data && domainsData.data.domains) {
        domainsData.data.domains.forEach(domain => {
          console.log(`🔍 Domain: ${domain.domain}, accountId: ${domain.accountId || 'NOT SET'}, SPF: ${domain.spfStatus}, DKIM: ${domain.dkimStatus}, DMARC: ${domain.dmarcStatus}`);
        });
      }
    }
  }, [domainsData]);

  /** Every domain name from the API (all AWS accounts), deduped and sorted — not filtered by account picker. */
  const allTenantDomainNames = useMemo(() => {
    const list = domainsData?.data?.domains || [];
    const seen = new Set();
    const out = [];
    for (const row of list) {
      const name = String(row?.domain || row?.id || '').trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
    out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return out;
  }, [domainsData]);

  const allTenantDomainsPlainText = useMemo(
    () => allTenantDomainNames.join('\n'),
    [allTenantDomainNames]
  );

  const copyAllTenantDomainsToClipboard = React.useCallback(async () => {
    if (!allTenantDomainsPlainText) return;
    try {
      await navigator.clipboard.writeText(allTenantDomainsPlainText);
      toast.success('Copied all domain names to clipboard');
    } catch (err) {
      console.error(err);
      toast.error('Could not copy to clipboard');
    }
  }, [allTenantDomainsPlainText]);

  const { 
    data: hiddenDomainsData
  } = useGetHiddenDomainsQuery();

  // Fetch email accounts to count per domain
  const { data: emailAccountsData } = useGetEmailAccountsQuery();

  // Domain health (per-domain SES identity / inbound MX / outbound routing).
  // Single source of truth shared with the nav-bar Domain Health Monitor icon
  // and the Inbound Provider modal. Cached + polled so multiple consumers share data.
  const { data: domainHealthMonitoringData } = useGetUnifiedEmailMonitoringQuery(
    { timeRange: '24h' },
    {
      pollingInterval: 60_000,
      refetchOnFocus: true,
      skipPollingIfUnfocused: true,
    }
  );
  const domainHealthMap = useMemo(() => {
    const map = new Map();
    const identities = domainHealthMonitoringData?.data?.identities || [];
    for (const identity of identities) {
      const key = String(identity?.domain || identity?.name || '').trim().toLowerCase();
      if (key) map.set(key, identity);
    }
    return map;
  }, [domainHealthMonitoringData]);

  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );

  // API-only providers are out of scope for Kross Global (SMTP relay + send pools architecture).

  const [createDomain] = useCreateDomainMutation();
  const [deleteDomain, { isLoading: isDeleting }] = useDeleteDomainMutation();
  const [hardDeleteDomain, { isLoading: isHardDeleting }] = useHardDeleteDomainMutation();
  const [domainConfirmation] = useDomainConfirmationMutation();
  const [enableDKIM, { isLoading: isEnablingDKIM }] = useEnableDKIMMutation();
  const [configureMailFrom, { isLoading: isConfiguringMailFrom }] = useConfigureMailFromMutation();
  const [configureTrackingDomain, { isLoading: isConfiguringTrackingDomain }] = useConfigureTrackingDomainMutation();
  const [triggerGetTrackingDomainStatus, trackingDomainStatusQuery] = useLazyGetTrackingDomainStatusQuery();

  // Send Pools (for outbound selector)
  const { data: sendPoolsData } = useGetSendPoolsQuery();
  const { data: sendPoolProvidersData } = useGetSendPoolProvidersQuery(undefined, {
    // If the backend endpoint isn't deployed yet, fall back to pool inference.
    // RTK Query will surface an error, but we won't block the modal.
    skip: false
  });
  const pools = sendPoolsData?.data?.pools || [];

  const providerRegistry = useMemo(() => {
    // Always seed known relay providers so options remain visible even if /send-pools/providers
    // does not yet include a newly added provider on a given backend deploy.
    const known = [
      { providerType: 'MAILCHANNELS', displayName: 'MailChannels', status: 'PENDING' },
      { providerType: 'SMTP_COM', displayName: 'SMTP.com', status: 'DISABLED' },
      { providerType: 'MAILGUN', displayName: 'Mailgun', status: 'DISABLED' },
      { providerType: 'SENDGRID', displayName: 'Twilio SendGrid', status: 'DISABLED' },
      { providerType: 'ELASTIC_EMAIL', displayName: 'Elastic Email', status: 'DISABLED' },
    ];
    const map = new Map(known.map((p) => [p.providerType, { ...p, poolIds: [] }]));

    const providersFromApi = Array.isArray(sendPoolProvidersData?.data?.providers)
      ? sendPoolProvidersData.data.providers
      : [];
    providersFromApi.forEach((provider) => {
      const pt = String(provider?.providerType || '').toUpperCase();
      if (!pt || pt === 'SES') return;
      const current = map.get(pt) || { providerType: pt, displayName: pt, status: 'DISABLED', poolIds: [] };
      const apiPoolIds = Array.isArray(provider?.poolIds) ? provider.poolIds.filter(Boolean) : [];
      map.set(pt, {
        ...current,
        ...provider,
        providerType: pt,
        displayName: provider?.displayName || current.displayName || pt,
        status: String(provider?.status || current.status || 'DISABLED').toUpperCase(),
        poolIds: Array.from(new Set([...(current.poolIds || []), ...apiPoolIds])),
      });
    });

    pools.forEach((p) => {
      const pt = String(p.providerType || '').toUpperCase();
      if (!pt) return;
      if (pt === 'SES') return;
      if (!map.has(pt)) map.set(pt, { providerType: pt, displayName: pt, status: 'ACTIVE', poolIds: [] });
      const entry = map.get(pt);
      entry.poolIds = Array.from(new Set([...(entry.poolIds || []), p.poolId].filter(Boolean)));
      if (String(p.status || '').toUpperCase() === 'ACTIVE') entry.status = 'ACTIVE';
    });
    return Array.from(map.values()).sort((a, b) => String(a.displayName).localeCompare(String(b.displayName)));
  }, [sendPoolProvidersData, pools]);

  const activeColdOutreachPools = useMemo(() => {
    return pools
      .filter((p) => String(p.status || '').toUpperCase() === 'ACTIVE')
      .filter((p) => {
        const allowed = p.policy?.allowedMessageClasses;
        return !Array.isArray(allowed) || allowed.length === 0 || allowed.includes('cold_outreach');
      });
  }, [pools]);

  const activeTransactionalPools = useMemo(() => {
    return pools
      .filter((p) => String(p.status || '').toUpperCase() === 'ACTIVE')
      .filter((p) => {
        const allowed = p.policy?.allowedMessageClasses;
        return !Array.isArray(allowed) || allowed.length === 0 || allowed.includes('transactional') || allowed.includes('newsletter');
      });
  }, [pools]);

  const activePoolsForPurpose = useMemo(() => {
    const purpose = String(outboundDomainType || '').toLowerCase();
    return purpose === 'cold_outreach' ? activeColdOutreachPools : activeTransactionalPools;
  }, [outboundDomainType, activeColdOutreachPools, activeTransactionalPools]);

  const poolsForSelectedProvider = useMemo(() => {
    if (!outboundProviderType) return [];
    return activePoolsForPurpose.filter(
      (p) => String(p.providerType || '').toUpperCase() === String(outboundProviderType).toUpperCase()
    );
  }, [activePoolsForPurpose, outboundProviderType]);

  const { data: domainRoutingData } = useGetDomainRoutingPolicyQuery(outboundDomainObj?.domain || '', {
    skip: !outboundDomainObj?.domain
  });

  const [setDomainRoute, { isLoading: isSavingOutboundRoute }] = useSetDomainRouteMutation();
  const [updateDomainType, { isLoading: isSavingDomainType }] = useUpdateDomainTypeMutation();

  // Transform database data to our domain format with account filtering
  const allDomains = useMemo(() => {
    const domains = domainsData?.data?.domains || [];
    
    // Filter domains by selected account using AccountContext domains list
    let filteredDomains = domains;
    if (!isAllAccountsSelected && selectedAccount && selectedAccount.id !== 'all') {
      // Get account's known domains from AccountContext
      const accountDomains = selectedAccount.domains || [];
      const accountDomainNames = accountDomains.map(d => d.toLowerCase());
      
      // Filter API domains to match account's known domains
      filteredDomains = domains.filter(domain => {
        const domainName = domain.domain?.toLowerCase();
        // Match if domain is in the account's domain list
        return accountDomainNames.some(accDomain => 
          domainName === accDomain ||
          domainName?.endsWith(`.${accDomain}`) ||
          accDomain.includes(domainName) ||
          domainName?.includes(accDomain)
        );
      });
    }
    
    // Get email accounts for counting
    const emailAccounts = emailAccountsData?.data?.emailAccounts || [];
    
    // Debug logging
    console.log('🔍 Domains - Email accounts data:', emailAccounts);
    console.log('🔍 Domains - Filtered domains:', filteredDomains);
    
    // Filter email accounts by selected account if not "All Accounts"
    let filteredEmailAccounts = emailAccounts;
    if (!isAllAccountsSelected && selectedAccount && selectedAccount.id !== 'all') {
      const accountDomains = selectedAccount.domains || [];
      const accountDomainNames = accountDomains.map(d => d.toLowerCase());
      filteredEmailAccounts = emailAccounts.filter(account => {
        // Email accounts have a 'domain' field directly, or we can extract from email
        const accountDomain = (account.domain || account.email?.split('@')[1])?.toLowerCase();
        return accountDomain && accountDomainNames.some(accDomain => 
          accountDomain === accDomain ||
          accountDomain?.endsWith(`.${accDomain}`) ||
          accDomain.includes(accountDomain) ||
          accountDomain?.includes(accDomain)
        );
      });
      console.log('🔍 Domains - Filtered email accounts by account:', filteredEmailAccounts);
    }
    
    return filteredDomains.map((domain, index) => {
      // Use the DynamoDB primary key as canonical identity whenever available.
      // This prevents UI/action mismatches if `domain` and `id` ever drift in stored rows.
      const canonicalDomain = String(domain.id || domain.domain || '').trim().toLowerCase();
      const fallbackDomain = String(domain.domain || '').trim().toLowerCase();
      const domainName = canonicalDomain || fallbackDomain;

      // Count email accounts for this domain
      const matchingEmailAccounts = filteredEmailAccounts.filter(account => {
        // Email accounts have a 'domain' field directly, or we can extract from email
        const accountDomain = (account.domain || account.email?.split('@')[1])?.toLowerCase();
        if (!accountDomain) return false;
        
        // Exact match
        if (accountDomain === domainName) {
          console.log(`✅ Domains - Exact match: ${accountDomain} === ${domainName}`);
          return true;
        }
        
        // Subdomain match (e.g., mail.krossglobalmarketing.com matches krossglobalmarketing.com)
        if (accountDomain.endsWith(`.${domainName}`)) {
          console.log(`✅ Domains - Subdomain match: ${accountDomain} ends with .${domainName}`);
          return true;
        }
        
        // Root domain match (e.g., krossglobalmarketing.com matches krossglobalmarketing.com from sub.domain.com)
        const accountRootDomain = accountDomain.split('.').slice(-2).join('.');
        const domainRootDomain = domainName.split('.').slice(-2).join('.');
        if (accountRootDomain === domainRootDomain) {
          console.log(`✅ Domains - Root domain match: ${accountRootDomain} === ${domainRootDomain}`);
          return true;
        }
        
        return false;
      });

      const emailCount = matchingEmailAccounts.length;

      // Last Used: newest activity across email accounts for this domain.
      // Domains table doesn't reliably persist a domain-level lastUsed.
      const lastUsedFromAccounts = (() => {
        let maxMs = 0;
        for (const acct of matchingEmailAccounts) {
          const iso = acct?.lastUsed || acct?.last_used || null;
          if (!iso) continue;
          const ms = Date.parse(String(iso));
          if (!Number.isFinite(ms)) continue;
          if (ms > maxMs) maxMs = ms;
        }
        return maxMs ? new Date(maxMs).toISOString() : null;
      })();
      
      console.log(`🔍 Domains - Domain ${domainName} has ${emailCount} email accounts`);
      
      // Merge local state for MAIL FROM configuration
      const localMailFromDomain =
        configuredMailFromDomains.get(domainName)
        ?? configuredMailFromDomains.get(String(domain.domain || '').trim());
      const hasLocalMailFrom = localMailFromDomain !== undefined;
      
      const dbAuthProvider = String(
        domain.authProvider
        || domain.dkimProvider
        || domain.spfProvider
        || ((domain.mailgunDkimSelector && String(domain.mailgunDkimSelector).trim()) ? 'mailgun' : '')
      ).toLowerCase() || undefined;

      const trackingProvider = normalizeTrackingProvider(domain.trackingProvider || TRACKING_PROVIDER_DEFAULT);
      const trackingCnameTarget = String(
        domain.trackingCnameTarget ||
        domain.trackingTarget ||
        getTrackingCnameTargetForProvider(trackingProvider)
      ).trim().toLowerCase();

      return {
        id: domainName || `domain-${index + 1}`,
        domain: domainName,
        accountId: domain.accountId, // CRITICAL: Preserve accountId for grouping
        domainType: (domain.domainType || domain.domain_type || 'business'),
        // Control-plane lifecycle status (provider-agnostic). This is what the Status badge uses.
        lifecycleStatus: (domain.lifecycleStatus || 'connected').toLowerCase(),
        // Preserve legacy provider status for debugging/compat only (do not show as primary Status).
        legacyStatus: domain.status === 'pending_verification' ? 'pending' : domain.status?.toLowerCase() || 'pending',
        // Keep `status` mapped to lifecycleStatus to avoid legacy UI logic accidentally showing SES-coupled states.
        status: (domain.lifecycleStatus || 'connected').toLowerCase(),
        createdAt: domain.createdAt || null,
        // Use the actual status values from backend instead of checking boolean fields
        dkimStatus: domain.dkimStatus || 'pending',
        spfStatus: domain.spfStatus || 'pending', 
        dmarcStatus: domain.dmarcStatus || 'pending',
        emailCount: emailCount,
        // Last Used: last time a send from this domain was accepted by the relay (not delivery confirmation)
        lastUsed: lastUsedFromAccounts ?? domain.lastUsed ?? domain.last_used ?? null,
        // Merge MAIL FROM data: prefer API data, but use local state if API doesn't have it yet
        mailFromDomain: domain.mailFromDomain || (hasLocalMailFrom ? localMailFromDomain : null),
        mailFromStatus: domain.mailFromStatus || (hasLocalMailFrom ? 'Pending' : undefined),
        mailFromConfigured: domain.mailFromConfigured || false,
        // Hosting and receiving provider information
        hostProvider: domain.hostProvider || 'aws',
        // Inbound provider selection (per-domain): postmark (default) | ses (optional).
        receivingProvider:
          domain.receivingProvider && String(domain.receivingProvider).toLowerCase() !== 'microsoft-acs'
            ? domain.receivingProvider
            : 'postmark',
        // Inbound MX health (backend DNS check): do not show "Inbound set" unless MX matches expected.
        receivingNeedsAttention:
          typeof domain.receivingNeedsAttention === 'boolean' ? domain.receivingNeedsAttention : undefined,
        mxMatchesExpectedPublic:
          typeof domain.mxMatchesExpectedPublic === 'boolean' ? domain.mxMatchesExpectedPublic : undefined,
        mxRecordsObservedPublic: Array.isArray(domain.mxRecordsObservedPublic) ? domain.mxRecordsObservedPublic : [],
        mxValueExpected: domain.mxValueExpected || null,
        mxRecordExpected: domain.mxRecordExpected || null,
        mxPriorityExpected: domain.mxPriorityExpected || null,
        // Route53 metadata (used for safe, auto-SPF switching when outbound provider activates)
        isInRoute53: Boolean(domain.isInRoute53),
        hostedZoneId: domain.hostedZoneId || null,
        // Preserve auth provider fields from backend; Domains UI label must be DB-driven.
        authProvider: dbAuthProvider,
        dkimProvider: domain.dkimProvider,
        spfProvider: domain.spfProvider,
        authVerifiedAt: domain.authVerifiedAt || null,
        relayProvider: domain.relayProvider || dbAuthProvider || null,
        relayAccountLabel: domain.relayAccountLabel || null,
        relayAccountSuffix: domain.relayAccountSuffix || null,
        relayAccountIdentifier: domain.relayAccountIdentifier || null,
        relayAccountSuffixOverrides: domain.relayAccountSuffixOverrides || null,
        mailgunDkimSelector: domain.mailgunDkimSelector,
        elasticEmailDkimSelector: domain.elasticEmailDkimSelector || null,
        mailchannelsDkimSelector: domain.mailchannelsDkimSelector || null,
        mailchannelsDkimTxtValue: domain.mailchannelsDkimTxtValue || '',
        mailchannelsAuthId: domain.mailchannelsAuthId || '',
        // Custom tracking domain (per-domain)
        trackingEnabled: domain.trackingEnabled === true,
        trackingSubdomain: domain.trackingSubdomain || 'inst',
        trackingDomain: domain.trackingDomain || `inst.${domainName}`,
        trackingProvider,
        trackingStatus: domain.trackingStatus || (domain.trackingEnabled === true ? 'missing' : 'not_configured'),
        trackingCnameTarget: trackingCnameTarget || getTrackingCnameTargetForProvider(trackingProvider),
        trackingObservedValues: Array.isArray(domain.trackingObservedValues)
          ? domain.trackingObservedValues
          : (Array.isArray(domain.trackingObservedCname) ? domain.trackingObservedCname : []),
        trackingLastCheckedAt: domain.trackingLastCheckedAt || null,
        // Persisted SendGrid auth DNS records (used to rehydrate Outbound modal state)
        sendgridDnsRecords: domain.sendgridDnsRecords || null,
        sendgridDnsConfiguredAt: domain.sendgridDnsConfiguredAt || null,
        // Domain forwarding (v1)
        forwardingEnabled: Boolean(domain.forwardingEnabled),
        forwardingDestinationId: domain.forwardingDestinationId || null,
        forwardingPreservePath: domain.forwardingPreservePath !== false,
        forwardingPreserveQuery: domain.forwardingPreserveQuery !== false,
        forwardingStatusCode: Number(domain.forwardingStatusCode) === 302 ? 302 : 301
      };
    });
  }, [domainsData, selectedAccount, isAllAccountsSelected, emailAccountsData, configuredMailFromDomains]);

  const [domainRoutingByDomain, setDomainRoutingByDomain] = useState({});
  const [outboundPrefetchDone, setOutboundPrefetchDone] = useState(false);

  // Prefer Domains API for outbound badge (domain.outboundConfigured / domain.coldOutreachPrimaryPoolId when backend adds it).
  // Until then, prefetch routing once when domain list loads (stable key to avoid loop) so badge reflects reality on first render.
  const domainListKey = useMemo(
    () => JSON.stringify((domainsData?.data?.domains ?? []).map((d) => d.domain).filter(Boolean).sort()),
    [domainsData?.data?.domains]
  );
  const PREFETCH_CONCURRENCY = 5;
  React.useEffect(() => {
    if (!domainListKey || domainListKey === '[]') {
      setOutboundPrefetchDone(true);
      return;
    }
    let cancelled = false;
    const domains = JSON.parse(domainListKey);
    const runBatch = (batch) =>
      Promise.allSettled(
        batch.map((d) =>
          dispatch(marketingApi.endpoints.getDomainRoutingPolicy.initiate(d))
            .unwrap()
            .then((res) => ({ domain: d, policy: res?.data?.policy ?? null }))
            .catch((err) => {
              console.warn('[kg:outbound-prefetch]', d, err?.message || err);
              return { domain: d, policy: null };
            })
        )
      );
    (async () => {
      try {
        const next = {};
        for (let i = 0; i < domains.length; i += PREFETCH_CONCURRENCY) {
          if (cancelled) break;
          const batch = domains.slice(i, i + PREFETCH_CONCURRENCY);
          const results = await runBatch(batch);
          results.forEach((r) => {
            const p = r.status === 'fulfilled' ? r.value?.policy : null;
            const hasPrimary =
              Boolean(p?.routes?.cold_outreach?.primaryPoolId) ||
              Boolean(p?.routes?.transactional?.primaryPoolId) ||
              Boolean(p?.routes?.newsletter?.primaryPoolId);
            if (r.status === 'fulfilled' && hasPrimary) {
              next[r.value.domain] = r.value.policy;
            }
          });
        }
        if (!cancelled) {
          setDomainRoutingByDomain((prev) => ({ ...prev, ...next }));
        }
      } finally {
        setOutboundPrefetchDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [domainListKey, dispatch]);

  // Phase 1: UI-only domain purpose model (infrastructure class)
  // Canonical UI purposes: business (Transactional), newsletter, cold_outreach.
  // Back-compat: transactional -> business in UI.
  function uiPurposeFromStoredDomainType(v) {
    const s = String(v || '').trim().toLowerCase();
    if (s === 'cold_outreach') return 'cold_outreach';
    if (s === 'newsletter') return 'newsletter';
    if (s === 'transactional') return 'business';
    return 'business';
  }

  function uiPurposeLabel(v) {
    const s = String(v || '').trim().toLowerCase();
    if (s === 'cold_outreach') return 'Cold Outreach';
    if (s === 'newsletter') return 'Newsletter';
    return 'Transactional';
  }

  function normalizeTrackingSubdomainDraft(value) {
    return String(value || '').trim().toLowerCase();
  }

  function isValidTrackingSubdomain(value) {
    return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(String(value || '').trim().toLowerCase());
  }

  function formatTrackingLastCheckedAt(value) {
    const raw = String(value || '').trim();
    if (!raw) return 'Not yet checked';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return 'Not yet checked';
    return toLocaleStringInTimezone(date, undefined, undefined, tenantTimezone);
  }

  function shouldAutoRecheckTrackingStatus(value) {
    const status = String(value || '').trim().toLowerCase();
    return status === 'pending' || status === 'missing';
  }

  function extractApiErrorMessage(error, fallbackMessage) {
    const fallback = String(fallbackMessage || 'Request failed.');
    if (!error) return fallback;

    const fromDataMessage = error?.data?.message;
    if (typeof fromDataMessage === 'string' && fromDataMessage.trim()) {
      return fromDataMessage.trim();
    }

    const fromErrorMessage = error?.error;
    if (typeof fromErrorMessage === 'string' && fromErrorMessage.trim()) {
      return fromErrorMessage.trim();
    }

    const fromStringData = typeof error?.data === 'string' ? error.data.trim() : '';
    if (fromStringData) return fromStringData;

    const fromMessage = typeof error?.message === 'string' ? error.message.trim() : '';
    if (fromMessage) return fromMessage;

    return fallback;
  }

  function isNetworkLoadFailureMessage(message) {
    const m = String(message || '').trim().toLowerCase();
    if (!m) return false;
    return (
      m.includes('typeerror: load failed') ||
      m.includes('load failed') ||
      m.includes('failed to fetch') ||
      m.includes('networkerror')
    );
  }

  const syncTrackingStatusInDomainsCache = React.useCallback((payload) => {
    const normalizedDomain = String(payload?.domain || '').trim().toLowerCase();
    if (!normalizedDomain) return;

    dispatch(
      marketingApi.util.updateQueryData('getDomains', undefined, (draft) => {
        const list = draft?.data?.domains;
        if (!Array.isArray(list)) return;

        for (const domainRow of list) {
          if (String(domainRow?.domain || '').trim().toLowerCase() !== normalizedDomain) continue;

          if (typeof payload?.trackingEnabled === 'boolean') {
            domainRow.trackingEnabled = payload.trackingEnabled === true;
          }

          const nextSubdomain = String(payload?.trackingSubdomain || '').trim().toLowerCase();
          if (nextSubdomain) domainRow.trackingSubdomain = nextSubdomain;

          const nextTrackingDomain = String(payload?.trackingDomain || '').trim().toLowerCase();
          if (nextTrackingDomain) domainRow.trackingDomain = nextTrackingDomain;

          const nextStatus = String(payload?.trackingStatus || '').trim().toLowerCase();
          if (nextStatus) domainRow.trackingStatus = nextStatus;

          const nextProvider = normalizeTrackingProvider(payload?.trackingProvider || domainRow?.trackingProvider || TRACKING_PROVIDER_DEFAULT);
          if (nextProvider) domainRow.trackingProvider = nextProvider;

          const nextTarget = String(
            payload?.trackingCnameTarget ||
            payload?.trackingTarget ||
            getTrackingCnameTargetForProvider(nextProvider)
          ).trim().toLowerCase();
          if (nextTarget) {
            domainRow.trackingCnameTarget = nextTarget;
            domainRow.trackingTarget = nextTarget;
          }

          const nextCheckedAt = String(payload?.trackingLastCheckedAt || '').trim();
          if (nextCheckedAt) domainRow.trackingLastCheckedAt = nextCheckedAt;

          const nextObserved = Array.isArray(payload?.trackingObservedValues)
            ? payload.trackingObservedValues
            : (Array.isArray(payload?.trackingObservedCname) ? payload.trackingObservedCname : null);
          if (Array.isArray(nextObserved)) {
            domainRow.trackingObservedValues = nextObserved;
            domainRow.trackingObservedCname = nextObserved;
          }
        }
      })
    );
  }, [dispatch]);

  // Outbound modal still loads routing on open; prefetch above populates domainRoutingByDomain so badge is correct on first render.

  const openForwardingModal = (domainObj) => {
    setForwardingDomainObj(domainObj);
    setShowForwardingModal(true);
  };

  const openOutboundModal = (domainObj) => {
    setOutboundDomainObj(domainObj);
    setOutboundError('');
    setOutboundProviderType('');
    setOutboundPrimaryPoolId('');
    setRelayAccountSuffixDraft('');
    setOutboundDomainType(uiPurposeFromStoredDomainType(domainObj?.domainType || domainObj?.domain_type || 'business'));
    setTrackingSubdomainDraft(normalizeTrackingSubdomainDraft(domainObj?.trackingSubdomain || 'inst'));
    setTrackingProviderDraft(normalizeTrackingProvider(domainObj?.trackingProvider || TRACKING_PROVIDER_DEFAULT));
    const canAutoSpf = Boolean(domainObj?.isInRoute53 && domainObj?.hostedZoneId);
    setOutboundApplySpf(canAutoSpf);
    setShowOutboundModal(true);
    // Load Instantly/iOS connection info immediately (non-blocking)
    if (domainObj?.domain) {
      triggerGetDomainConnectionInfo(domainObj.domain);
      triggerGetTrackingDomainStatus({ domain: domainObj.domain, accountId: domainObj.accountId });
      setRevealedSmtpPassword(null);
      setRevealedImapPassword(null);
    }

    // If the domain is in Route53 but we don't yet have hostedZoneId populated on the domain record,
    // attempt to resolve it (cheap) so Auto-SPF can be enabled without extra user steps.
    if (domainObj?.isInRoute53 && !domainObj?.hostedZoneId) {
      (async () => {
        try {
          const res = await dispatch(
            marketingApi.endpoints.route53Ownership.initiate(
              { domain: domainObj.domain, accountId: domainObj.accountId },
              { forceRefetch: true }
            )
          ).unwrap();
          // RTK Query unwrap() returns the standard API shape: { success, data: {...} }
          const rawHz = res?.data?.hostedZoneId || res?.data?.data?.hostedZoneId || null;
          const hz = rawHz ? String(rawHz).replace('/hostedzone/', '').split('/').pop() : null;
          if (hz) {
            setOutboundDomainObj((prev) => (prev ? { ...prev, hostedZoneId: hz, isInRoute53: true } : prev));
            setOutboundApplySpf(true);
          }
        } catch (e) {
          // Silent: we keep Auto-SPF disabled if we can't prove hosted zone control.
          console.log('Route53 ownership lookup failed for outbound modal:', e);
        }
      })();
    }
  };

  React.useEffect(() => {
    const payload =
      trackingDomainStatusQuery?.data?.data ||
      trackingDomainStatusQuery?.data ||
      null;
    const statusDomain = String(payload?.domain || '').trim().toLowerCase();
    const activeDomain = String(outboundDomainObj?.domain || '').trim().toLowerCase();
    if (!statusDomain || !activeDomain || statusDomain !== activeDomain) return;

    setTrackingProviderDraft(
      normalizeTrackingProvider(payload?.trackingProvider || outboundDomainObj?.trackingProvider || TRACKING_PROVIDER_DEFAULT)
    );
    syncTrackingStatusInDomainsCache(payload);

    setOutboundDomainObj((prev) => {
      if (!prev || String(prev.domain || '').trim().toLowerCase() !== statusDomain) return prev;
      return {
        ...prev,
        trackingEnabled: payload.trackingEnabled === true,
        trackingSubdomain: payload.trackingSubdomain || prev.trackingSubdomain || 'inst',
        trackingDomain: payload.trackingDomain || prev.trackingDomain || `inst.${prev.domain}`,
        trackingProvider: normalizeTrackingProvider(payload.trackingProvider || prev.trackingProvider || TRACKING_PROVIDER_DEFAULT),
        trackingStatus: payload.trackingStatus || prev.trackingStatus || 'missing',
        trackingCnameTarget:
          payload.trackingCnameTarget ||
          payload.trackingTarget ||
          prev.trackingCnameTarget ||
          getTrackingCnameTargetForProvider(payload.trackingProvider || prev.trackingProvider || TRACKING_PROVIDER_DEFAULT),
        trackingObservedValues: Array.isArray(payload.trackingObservedValues)
          ? payload.trackingObservedValues
          : (Array.isArray(payload.trackingObservedCname) ? payload.trackingObservedCname : (prev.trackingObservedValues || [])),
        trackingLastCheckedAt: payload.trackingLastCheckedAt || prev.trackingLastCheckedAt || null,
      };
    });
  }, [trackingDomainStatusQuery?.data, outboundDomainObj?.domain, syncTrackingStatusInDomainsCache]);

  // Prefill current primary pool (cold_outreach or transactional) when modal opens
  React.useEffect(() => {
    if (!showOutboundModal) return;
    const policy = domainRoutingData?.data?.policy;
    const purpose = String(outboundDomainType || '').toLowerCase();
    const current =
      purpose === 'cold_outreach'
        ? policy?.routes?.cold_outreach?.primaryPoolId || ''
        : policy?.routes?.transactional?.primaryPoolId || '';
    if (current) {
      setOutboundPrimaryPoolId(current);
      const pool = pools.find((p) => p.poolId === current);
      if (pool?.providerType) setOutboundProviderType(String(pool.providerType).toUpperCase());
    }

    // Back-compat UX: if the domain already has a cold_outreach route but doesn't yet have a stored domainType,
    // default the selector to cold_outreach so users aren't forced to “discover” the purpose rule.
    const hasColdRoute = Boolean(policy?.routes?.cold_outreach?.primaryPoolId);
    const storedType = String(outboundDomainObj?.domainType || outboundDomainObj?.domain_type || '').toLowerCase();
    if (hasColdRoute && !storedType) {
      setOutboundDomainType('cold_outreach');
    }
  }, [showOutboundModal, domainRoutingData, outboundDomainType]);

  // When provider changes, default the pool to the first ACTIVE pool for that provider (if any)
  React.useEffect(() => {
    if (!showOutboundModal) return;
    if (!outboundProviderType) return;
    // If current pool matches provider, keep it.
    const currentPool = pools.find((p) => p.poolId === outboundPrimaryPoolId);
    if (currentPool && String(currentPool.providerType || '').toUpperCase() === String(outboundProviderType).toUpperCase()) return;
    const first = poolsForSelectedProvider[0];
    setOutboundPrimaryPoolId(first?.poolId || '');
  }, [showOutboundModal, outboundProviderType, poolsForSelectedProvider]);

  // MailChannels/SendGrid/Elastic Email: one set of credentials per pool (shared across domains).
  // SMTP.com/Mailgun: credentials per domain in SSM.
  // When modal opens or provider/domain changes, fetch the right credentials so "Credentials set" shows for domains already configured.
  React.useEffect(() => {
    if (!showOutboundModal) return;
    setRevealedSmtpComPassword(null);
    setSmtpComCredsEditOpen(false);
    const pt = String(outboundProviderType || '').toUpperCase();
    if ((pt === 'MAILCHANNELS' || pt === 'SENDGRID' || pt === 'ELASTIC_EMAIL') && outboundPrimaryPoolId) {
      triggerGetPoolCredentials(outboundPrimaryPoolId);
    } else if (pt === 'SMTP_COM' && outboundDomainObj?.domain) {
      triggerGetOutboundSmtpComCredentials(outboundDomainObj.domain);
    } else if (pt === 'MAILGUN' && outboundDomainObj?.domain) {
      triggerGetOutboundMailgunCredentials(outboundDomainObj.domain);
    }
  }, [showOutboundModal, outboundDomainObj?.domain, outboundPrimaryPoolId, outboundProviderType]);

  const parseSendgridDnsRecords = React.useCallback((domainObj) => {
    try {
      const raw = domainObj?.sendgridDnsRecords;
      if (!raw) return [];
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : (Array.isArray(raw) ? raw : []);
      return parsed.filter(r => r && r.host && r.value);
    } catch (_) { return []; }
  }, []);

  // Rehydrate SendGrid DNS state when opening outbound modal for a domain
  React.useEffect(() => {
    if (!showOutboundModal || !outboundDomainObj?.domain) return;
    const persistedRecords = parseSendgridDnsRecords(outboundDomainObj);
    const persistedAt = outboundDomainObj?.sendgridDnsConfiguredAt || null;

    if (persistedRecords.length > 0 && persistedAt) {
      setSendgridDnsSavedRecords(persistedRecords);
      setSendgridDnsSavedAt(persistedAt);
      setSendgridDnsSaved(true);
      setSendgridDnsEditOpen(false);
      setSendgridDnsRecords(persistedRecords.map(r => ({ ...r })));
    } else {
      setSendgridDnsRecords([
        { type: 'CNAME', host: '', value: '' },
        { type: 'CNAME', host: '', value: '' },
        { type: 'CNAME', host: '', value: '' },
        { type: 'TXT', host: '', value: '' },
      ]);
      setSendgridDnsSaved(false);
      setSendgridDnsSavedRecords([]);
      setSendgridDnsSavedAt(null);
      setSendgridDnsEditOpen(false);
    }
  }, [showOutboundModal, outboundDomainObj?.domain, parseSendgridDnsRecords]);

  // Catch late-arriving persisted data (e.g. domains refetch after save)
  React.useEffect(() => {
    if (!showOutboundModal || !outboundDomainObj?.domain) return;
    if (sendgridDnsSaved) return;
    const persistedRecords = parseSendgridDnsRecords(outboundDomainObj);
    const persistedAt = outboundDomainObj?.sendgridDnsConfiguredAt || null;
    if (persistedRecords.length > 0 && persistedAt) {
      setSendgridDnsSavedRecords(persistedRecords);
      setSendgridDnsSavedAt(persistedAt);
      setSendgridDnsSaved(true);
      setSendgridDnsEditOpen(false);
      setSendgridDnsRecords(persistedRecords.map(r => ({ ...r })));
    }
  }, [showOutboundModal, outboundDomainObj?.sendgridDnsConfiguredAt, sendgridDnsSaved, parseSendgridDnsRecords, outboundDomainObj]);

  React.useEffect(() => {
    if (!showOutboundModal || smtpComCredsEditOpen) return;
    const pt = String(outboundProviderType || '').toUpperCase();
    const usePoolCreds = (pt === 'MAILCHANNELS' || pt === 'SENDGRID' || pt === 'ELASTIC_EMAIL') && Boolean(outboundPrimaryPoolId);
    const poolData = poolCredentialsQuery?.data?.data ?? poolCredentialsQuery?.data ?? {};
    const smtpData = outboundSmtpComCredentialsQuery?.data?.data ?? outboundSmtpComCredentialsQuery?.data ?? {};
    const mailgunData = outboundMailgunCredentialsQuery?.data?.data ?? outboundMailgunCredentialsQuery?.data ?? {};
    const data = usePoolCreds ? poolData : (pt === 'MAILGUN' ? mailgunData : smtpData);
    const selectedProviderKey = normalizeRelayProviderKey(pt);
    const domainProviderKey = normalizeRelayProviderKey(outboundDomainObj?.relayProvider || outboundDomainObj?.authProvider || '');
    const domainFallbackSuffix = selectedProviderKey && selectedProviderKey === domainProviderKey
      ? String(outboundDomainObj?.relayAccountSuffix || '').trim()
      : '';
    const raw = String(data?.relayAccountSuffix || domainFallbackSuffix || '').trim();
    setRelayAccountSuffixDraft(RELAY_ACCOUNT_SUFFIX_PATTERN.test(raw) ? raw : '');
  }, [
    showOutboundModal,
    smtpComCredsEditOpen,
    outboundProviderType,
    outboundPrimaryPoolId,
    outboundDomainObj?.relayAccountSuffix,
    poolCredentialsQuery?.data,
    outboundSmtpComCredentialsQuery?.data,
    outboundMailgunCredentialsQuery?.data,
  ]);

  const refreshTrackingDomainStatus = async (options = {}) => {
    const { silentErrors = false, force = false } = options;
    const domain = outboundDomainObj?.domain;
    if (!domain) return { ok: false, errorMessage: 'Domain is required' };
    try {
      const result = await triggerGetTrackingDomainStatus({
        domain,
        accountId: outboundDomainObj?.accountId,
        force: force === true,
      }).unwrap();
      const payload = result?.data || result || {};
      const statusDomain = String(payload?.domain || domain).trim().toLowerCase();
      const normalizedStatus = String(payload?.trackingStatus || '').trim().toLowerCase();
      const payloadProvider = normalizeTrackingProvider(payload?.trackingProvider || TRACKING_PROVIDER_DEFAULT);
      setTrackingProviderDraft(payloadProvider);
      syncTrackingStatusInDomainsCache({
        ...payload,
        domain: payload?.domain || domain,
      });
      setOutboundDomainObj((prev) => {
        if (!prev) return prev;
        if (String(prev.domain || '').trim().toLowerCase() !== statusDomain) return prev;
        const nextSubdomain =
          payload.trackingSubdomain ||
          normalizeTrackingSubdomainDraft(trackingSubdomainDraft || prev.trackingSubdomain || 'inst') ||
          'inst';
        const nextDomain = payload.trackingDomain || `${nextSubdomain}.${prev.domain}`;
        return {
          ...prev,
          trackingEnabled:
            typeof payload?.trackingEnabled === 'boolean'
              ? payload.trackingEnabled === true
              : prev.trackingEnabled,
          trackingSubdomain: nextSubdomain,
          trackingDomain: nextDomain,
          trackingProvider: payloadProvider,
          trackingStatus: payload.trackingStatus || prev.trackingStatus || 'missing',
          trackingCnameTarget:
            payload.trackingCnameTarget ||
            payload.trackingTarget ||
            prev.trackingCnameTarget ||
            getTrackingCnameTargetForProvider(payloadProvider),
          trackingObservedValues: Array.isArray(payload.trackingObservedValues)
            ? payload.trackingObservedValues
            : (Array.isArray(payload.trackingObservedCname) ? payload.trackingObservedCname : (prev.trackingObservedValues || [])),
          trackingLastCheckedAt: payload.trackingLastCheckedAt || prev.trackingLastCheckedAt || new Date().toISOString(),
        };
      });
      if (!shouldAutoRecheckTrackingStatus(normalizedStatus)) {
        setTrackingAutoRecheckState((prev) => {
          if (String(prev.domain || '').trim().toLowerCase() !== statusDomain) return prev;
          return {
            ...prev,
            active: false,
            nextDelayMs: 0,
            stopped: false,
            reason: '',
          };
        });
      }
      return { ok: true, payload };
    } catch (error) {
      console.warn('Failed to refresh tracking domain status:', error);
      const errorMessage = extractApiErrorMessage(error, 'Failed to refresh tracking domain status.');
      if (!silentErrors) {
        if (isNetworkLoadFailureMessage(errorMessage)) {
          toast.error('Unable to recheck tracking DNS right now (network/CORS). Please retry.');
        } else if (/unauthorized/i.test(errorMessage) || /forbidden/i.test(errorMessage)) {
          toast.error('Unable to recheck tracking DNS due to auth/session issue. Refresh and sign in again.');
        } else {
          toast.error(errorMessage);
        }
      }
      return { ok: false, errorMessage };
    }
  };

  const clearTrackingAutoRecheckTimer = () => {
    if (trackingAutoRecheckTimerRef.current) {
      clearTimeout(trackingAutoRecheckTimerRef.current);
      trackingAutoRecheckTimerRef.current = null;
    }
  };

  const stopTrackingAutoRecheck = (options = {}) => {
    const { paused = false, reason = '' } = options;
    trackingAutoRecheckRunRef.current += 1;
    clearTrackingAutoRecheckTimer();
    setTrackingAutoRecheckState((prev) => ({
      ...prev,
      active: false,
      nextDelayMs: 0,
      stopped: paused,
      reason: paused ? (reason || 'Automatic checks paused. Click Recheck to check again.') : '',
    }));
  };

  const startTrackingAutoRecheck = (domainToCheck) => {
    const normalizedDomain = String(domainToCheck || '').trim().toLowerCase();
    if (!normalizedDomain) return;

    trackingAutoRecheckRunRef.current += 1;
    clearTrackingAutoRecheckTimer();
    const runId = trackingAutoRecheckRunRef.current;
    const maxAttempts = TRACKING_AUTO_RECHECK_DELAYS_MS.length;

    const runAttempt = (attemptIndex) => {
      if (runId !== trackingAutoRecheckRunRef.current) return;
      if (attemptIndex >= maxAttempts) {
        setTrackingAutoRecheckState((prev) => ({
          ...prev,
          active: false,
          attempt: maxAttempts,
          maxAttempts,
          nextDelayMs: 0,
          stopped: true,
          reason: 'Automatic checks paused. Click Recheck to check again.',
          domain: normalizedDomain,
        }));
        return;
      }

      const delayMs = TRACKING_AUTO_RECHECK_DELAYS_MS[attemptIndex];
      setTrackingAutoRecheckState({
        active: true,
        attempt: attemptIndex + 1,
        maxAttempts,
        nextDelayMs: delayMs,
        stopped: false,
        reason: '',
        domain: normalizedDomain,
      });

      trackingAutoRecheckTimerRef.current = setTimeout(async () => {
        if (runId !== trackingAutoRecheckRunRef.current) return;

        const response = await refreshTrackingDomainStatus({ silentErrors: true });
        if (runId !== trackingAutoRecheckRunRef.current) return;

        if (!response?.ok) {
          setTrackingAutoRecheckState((prev) => ({
            ...prev,
            active: false,
            nextDelayMs: 0,
            stopped: true,
            reason: 'Automatic checks paused due to a temporary error. Click Recheck to try again.',
            domain: normalizedDomain,
          }));
          return;
        }

        const nextStatus = String(response?.payload?.trackingStatus || '').trim().toLowerCase();
        if (nextStatus === 'verified' || nextStatus === 'not_configured') {
          setTrackingAutoRecheckState((prev) => ({
            ...prev,
            active: false,
            nextDelayMs: 0,
            stopped: false,
            reason: '',
            domain: normalizedDomain,
          }));
          return;
        }

        if (!shouldAutoRecheckTrackingStatus(nextStatus)) {
          setTrackingAutoRecheckState((prev) => ({
            ...prev,
            active: false,
            nextDelayMs: 0,
            stopped: true,
            reason: 'Automatic checks paused. Click Recheck to check again.',
            domain: normalizedDomain,
          }));
          return;
        }

        runAttempt(attemptIndex + 1);
      }, delayMs);
    };

    runAttempt(0);
  };

  React.useEffect(() => {
    if (showOutboundModal) return;
    trackingAutoRecheckRunRef.current += 1;
    clearTrackingAutoRecheckTimer();
    setTrackingAutoRecheckState((prev) => ({
      ...prev,
      active: false,
      nextDelayMs: 0,
      stopped: false,
      reason: '',
    }));
  }, [showOutboundModal]);

  React.useEffect(() => {
    const autoDomain = String(trackingAutoRecheckState.domain || '').trim().toLowerCase();
    const currentDomain = String(outboundDomainObj?.domain || '').trim().toLowerCase();
    if (!autoDomain || !currentDomain || autoDomain === currentDomain) return;
    trackingAutoRecheckRunRef.current += 1;
    clearTrackingAutoRecheckTimer();
    setTrackingAutoRecheckState((prev) => ({
      ...prev,
      active: false,
      nextDelayMs: 0,
      stopped: false,
      reason: '',
    }));
  }, [outboundDomainObj?.domain, trackingAutoRecheckState.domain]);

  React.useEffect(() => {
    return () => {
      trackingAutoRecheckRunRef.current += 1;
      clearTrackingAutoRecheckTimer();
    };
  }, []);

  const refreshOutboundRelayCredentialsStatus = async () => {
    const domain = outboundDomainObj?.domain;
    const provider = String(outboundProviderType || '').toUpperCase();
    try {
      if ((provider === 'MAILCHANNELS' || provider === 'SENDGRID' || provider === 'ELASTIC_EMAIL') && outboundPrimaryPoolId) {
        await triggerGetPoolCredentials(outboundPrimaryPoolId).unwrap();
        return;
      }
      if (provider === 'MAILGUN' && domain) {
        await triggerGetOutboundMailgunCredentials(domain).unwrap();
        return;
      }
      if (provider === 'SMTP_COM' && domain) {
        await triggerGetOutboundSmtpComCredentials(domain).unwrap();
      }
    } catch (error) {
      console.warn('Failed to refresh outbound relay credentials status:', error);
    }
  };

  const handleEnableTrackingDomain = async () => {
    const domain = outboundDomainObj?.domain;
    if (!domain) {
      toast.error('Domain is required');
      return;
    }

    const normalizedSubdomain = normalizeTrackingSubdomainDraft(trackingSubdomainDraft || 'inst');
    const normalizedTrackingProvider = normalizeTrackingProvider(
      trackingProviderDraft || outboundDomainObj?.trackingProvider || TRACKING_PROVIDER_DEFAULT
    );
    if (!isValidTrackingSubdomain(normalizedSubdomain)) {
      toast.error('Tracking subdomain must be a single DNS label (letters, numbers, hyphen only), for example "inst".');
      return;
    }

    try {
      const result = await configureTrackingDomain({
        domain,
        trackingSubdomain: normalizedSubdomain,
        trackingProvider: normalizedTrackingProvider,
        accountId: outboundDomainObj?.accountId,
        autoApply: true,
        enabled: true,
      }).unwrap();

      const payload = result?.data || result || {};
      syncTrackingStatusInDomainsCache({
        ...payload,
        domain: payload?.domain || domain,
      });
      setTrackingSubdomainDraft(payload.trackingSubdomain || normalizedSubdomain);
      setTrackingProviderDraft(normalizeTrackingProvider(payload.trackingProvider || normalizedTrackingProvider));
      setOutboundDomainObj((prev) => (prev ? {
        ...prev,
        trackingEnabled: payload.trackingEnabled === true,
        trackingSubdomain: payload.trackingSubdomain || normalizedSubdomain,
        trackingDomain: payload.trackingDomain || `${normalizedSubdomain}.${domain}`,
        trackingProvider: normalizeTrackingProvider(payload.trackingProvider || normalizedTrackingProvider),
        trackingStatus: payload.trackingStatus || 'pending',
        trackingCnameTarget:
          payload.trackingCnameTarget ||
          payload.trackingTarget ||
          getTrackingCnameTargetForProvider(payload.trackingProvider || normalizedTrackingProvider),
        trackingObservedValues: Array.isArray(payload.trackingObservedValues)
          ? payload.trackingObservedValues
          : (Array.isArray(payload.trackingObservedCname) ? payload.trackingObservedCname : []),
        trackingLastCheckedAt: payload.trackingLastCheckedAt || new Date().toISOString(),
      } : prev));

      if (payload.manualSetupRequired) {
        toast.warn(`Tracking domain saved for ${domain}. Add the CNAME record manually to verify.`);
      } else if (payload.trackingStatus === 'verified') {
        toast.success(`Tracking domain verified for ${domain}.`);
      } else {
        toast.success(`Tracking domain (${getTrackingProviderLabel(normalizedTrackingProvider)}) enabled for ${domain}. DNS propagation is in progress.`);
      }

      const refreshed = await refreshTrackingDomainStatus({ silentErrors: true });
      const effectiveStatus = String(
        (refreshed?.ok ? refreshed?.payload?.trackingStatus : '') || payload.trackingStatus || ''
      ).trim().toLowerCase();
      if (shouldAutoRecheckTrackingStatus(effectiveStatus)) {
        startTrackingAutoRecheck(domain);
      } else {
        stopTrackingAutoRecheck({ paused: false });
      }
    } catch (error) {
      console.error('Failed to enable tracking domain:', error);
      const errorMessage = extractApiErrorMessage(error, 'Failed to enable tracking domain.');
      if (/missing authentication token/i.test(errorMessage)) {
        toast.error('Tracking endpoint is not deployed in this environment yet. Deploy backend API and try again.');
      } else if (isNetworkLoadFailureMessage(errorMessage)) {
        toast.error('Request blocked by CORS/network. Allow http://localhost:3002 in staging API CORS and try again.');
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleDisableTrackingDomain = async () => {
    const domain = outboundDomainObj?.domain;
    if (!domain) {
      toast.error('Domain is required');
      return;
    }
    try {
      const result = await configureTrackingDomain({
        domain,
        trackingSubdomain: normalizeTrackingSubdomainDraft(trackingSubdomainDraft || 'inst'),
        trackingProvider: normalizeTrackingProvider(
          trackingProviderDraft || outboundDomainObj?.trackingProvider || TRACKING_PROVIDER_DEFAULT
        ),
        accountId: outboundDomainObj?.accountId,
        enabled: false,
      }).unwrap();
      const payload = result?.data || result || {};
      setTrackingProviderDraft(
        normalizeTrackingProvider(payload.trackingProvider || trackingProviderDraft || outboundDomainObj?.trackingProvider || TRACKING_PROVIDER_DEFAULT)
      );
      syncTrackingStatusInDomainsCache({
        ...payload,
        domain: payload?.domain || domain,
      });
      setOutboundDomainObj((prev) => (prev ? {
        ...prev,
        trackingEnabled: false,
        trackingStatus: payload.trackingStatus || 'missing',
        trackingSubdomain: payload.trackingSubdomain || prev.trackingSubdomain || 'inst',
        trackingDomain: payload.trackingDomain || prev.trackingDomain || `inst.${domain}`,
        trackingProvider: normalizeTrackingProvider(payload.trackingProvider || prev.trackingProvider || TRACKING_PROVIDER_DEFAULT),
        trackingCnameTarget:
          payload.trackingCnameTarget ||
          payload.trackingTarget ||
          prev.trackingCnameTarget ||
          getTrackingCnameTargetForProvider(payload.trackingProvider || prev.trackingProvider || TRACKING_PROVIDER_DEFAULT),
        trackingLastCheckedAt: payload.trackingLastCheckedAt || new Date().toISOString(),
      } : prev));
      stopTrackingAutoRecheck({ paused: false });
      toast.info(`Tracking domain disabled for ${domain}.`);
    } catch (error) {
      console.error('Failed to disable tracking domain:', error);
      const errorMessage = extractApiErrorMessage(error, 'Failed to disable tracking domain.');
      if (/missing authentication token/i.test(errorMessage)) {
        toast.error('Tracking endpoint is not deployed in this environment yet. Deploy backend API and try again.');
      } else if (isNetworkLoadFailureMessage(errorMessage)) {
        toast.error('Request blocked by CORS/network. Allow http://localhost:3002 in staging API CORS and try again.');
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const saveOutboundRoute = async () => {
    try {
      setOutboundError('');
      if (!outboundDomainObj?.domain) throw new Error('Domain is required');

      // Persist Domain Purpose as source-of-truth (guardrail)
      const currentTypeStored = uiPurposeFromStoredDomainType(outboundDomainObj?.domainType || outboundDomainObj?.domain_type || 'business');
      const nextType = String(outboundDomainType).toLowerCase();
      // Site-wide rule: if Cold Outreach is selected, shared IMAP creds must be applied immediately.
      // We always forceProvision on cold_outreach to avoid “already cold_outreach (no transition)” skips.
      if (currentTypeStored !== nextType || nextType === 'cold_outreach') {
        await updateDomainType({
          domain: outboundDomainObj.domain,
          domainType: nextType,
          forceProvision: nextType === 'cold_outreach'
        }).unwrap();
      }

      // When purpose is newsletter (not cold_outreach and not business), save purpose only and close.
      const purpose = String(outboundDomainType).toLowerCase();
      if (purpose === 'newsletter') {
        toast.success(`Purpose updated to ${uiPurposeLabel(outboundDomainType)} for ${outboundDomainObj.domain}.`);
        setShowOutboundModal(false);
        setOutboundDomainObj(null);
        return;
      }

      if (!outboundProviderType) throw new Error('Please select an Outbound Provider');
      if (!outboundPrimaryPoolId) throw new Error('Please select a Send Pool');

      const messageClass = purpose === 'cold_outreach' ? 'cold_outreach' : 'transactional';
      await setDomainRoute({
        domain: outboundDomainObj.domain,
        messageClass,
        primaryPoolId: outboundPrimaryPoolId,
        applySpf: outboundApplySpf
      }).unwrap();

      // Persist upstream relay credentials:
      // - MailChannels/Twilio SendGrid/Elastic Email = pool-level
      // - SMTP.com/Mailgun = per-domain in SSM.
      // Credentials save is optional, but this also persists manual relay account suffix overrides.
      // smtpComChannelPasswordDraft is the shared password field for the credentials form (all providers use it).
      const hasPassword = Boolean(smtpComChannelPasswordDraft && String(smtpComChannelPasswordDraft).trim());
      const draftedRelayAccountSuffix = String(relayAccountSuffixDraft || '').trim();
      if (draftedRelayAccountSuffix && !RELAY_ACCOUNT_SUFFIX_PATTERN.test(draftedRelayAccountSuffix)) {
        setOutboundError('Relay account suffix must be exactly 4 letters/numbers (example: 083c).');
        return;
      }
      const currentProviderType = String(outboundProviderType || '').toUpperCase();
      const selectedProviderKey = normalizeRelayProviderKey(currentProviderType);
      const domainProviderKey = normalizeRelayProviderKey(outboundDomainObj?.relayProvider || outboundDomainObj?.authProvider || '');
      const domainFallbackSuffix =
        selectedProviderKey && selectedProviderKey === domainProviderKey
          ? String(outboundDomainObj?.relayAccountSuffix || '').trim()
          : '';
      const smtpCredsData = outboundSmtpComCredentialsQuery?.data?.data ?? outboundSmtpComCredentialsQuery?.data ?? {};
      const mailgunCredsData = outboundMailgunCredentialsQuery?.data?.data ?? outboundMailgunCredentialsQuery?.data ?? {};
      const poolCredsData = poolCredentialsQuery?.data?.data ?? poolCredentialsQuery?.data ?? {};
      const selectedCredsData =
        currentProviderType === 'MAILCHANNELS' || currentProviderType === 'SENDGRID' || currentProviderType === 'ELASTIC_EMAIL'
          ? poolCredsData
          : currentProviderType === 'MAILGUN'
            ? mailgunCredsData
            : smtpCredsData;
      const currentRelayAccountSuffixRaw = String(selectedCredsData?.relayAccountSuffix || domainFallbackSuffix || '').trim();
      const currentRelayAccountSuffix = RELAY_ACCOUNT_SUFFIX_PATTERN.test(currentRelayAccountSuffixRaw) ? currentRelayAccountSuffixRaw : '';
      const hasRelayAccountSuffixChanged = draftedRelayAccountSuffix !== currentRelayAccountSuffix;
      const relayAccountSuffixForSave = hasRelayAccountSuffixChanged ? (draftedRelayAccountSuffix || null) : undefined;
      if (hasPassword || hasRelayAccountSuffixChanged) {
        try {
          const pt = String(outboundProviderType || '').toUpperCase();
          const draftedUsername = String(smtpComChannelLoginDraft || '').trim();
          if (pt === 'MAILCHANNELS' && draftedUsername && !isValidMailChannelsAccountId(draftedUsername)) {
            setOutboundError('MailChannels SMTP username must be your Account ID (for example ee9aefhbcj99), not an email address.');
            return;
          }
          if ((pt === 'MAILCHANNELS' || pt === 'SENDGRID' || pt === 'ELASTIC_EMAIL') && outboundPrimaryPoolId) {
            await putPoolCredentials({
              poolId: outboundPrimaryPoolId,
              username: draftedUsername || undefined,
              password: smtpComChannelPasswordDraft || undefined,
              domain: outboundDomainObj.domain,
              relayAccountSuffix: relayAccountSuffixForSave,
            }).unwrap();
            triggerGetPoolCredentials(outboundPrimaryPoolId);
          } else if (pt === 'MAILGUN') {
            await putOutboundMailgunCredentials({
              domain: outboundDomainObj.domain,
              username: smtpComChannelLoginDraft?.trim() || undefined,
              password: smtpComChannelPasswordDraft || undefined,
              relayAccountSuffix: relayAccountSuffixForSave,
            }).unwrap();
            triggerGetOutboundMailgunCredentials(outboundDomainObj.domain);
          } else {
            await putOutboundSmtpComCredentials({
              domain: outboundDomainObj.domain,
              username: smtpComChannelLoginDraft?.trim() || undefined,
              password: smtpComChannelPasswordDraft || undefined,
              scope: 'domain',
              relayAccountSuffix: relayAccountSuffixForSave,
            }).unwrap();
            triggerGetOutboundSmtpComCredentials(outboundDomainObj.domain);
          }
          setSmtpComChannelPasswordDraft('');
        } catch (credsErr) {
          setOutboundError(credsErr?.data?.message || credsErr?.message || 'Outbound route saved, but upstream relay credentials failed to save. Use "Save credentials" or try again.');
          return;
        }
      }

      // Update routing cache immediately so the outbound badge flips to "Outbound set" without waiting for the next prefetch
      try {
        const res = await dispatch(
          marketingApi.endpoints.getDomainRoutingPolicy.initiate(outboundDomainObj.domain, { forceRefetch: true })
        ).unwrap();
        setDomainRoutingByDomain((prev) => ({ ...prev, [outboundDomainObj.domain]: res?.data?.policy ?? null }));
      } catch (_) {
        // Non-fatal: badge may stay "Set up outbound" until next prefetch or refresh
      }

      toast.success(
        messageClass === 'transactional'
          ? `Transactional routing (${providerCredsLabel(outboundProviderType)}) saved for ${outboundDomainObj.domain}.`
          : `Outbound routing saved for ${outboundDomainObj.domain}.`
      );
      setShowOutboundModal(false);
      setOutboundDomainObj(null);
    } catch (e) {
      setOutboundError(e?.data?.message || e?.message || 'Failed to save outbound route');
    }
  };

  // Clean up local state when MAIL FROM is verified (status becomes 'Success')
  // Also persist configuredMailFromDomains in localStorage to survive page refreshes
  React.useEffect(() => {
    if (allDomains.length > 0 && configuredMailFromDomains.size > 0) {
      setConfiguredMailFromDomains(prev => {
        const newMap = new Map(prev);
        let changed = false;
        
        allDomains.forEach(domain => {
          // Check multiple possible success status values
          const isComplete = domain.mailFromConfigured || 
                            domain.mailFromStatus === 'Success' ||
                            domain.mailFromStatus === 'success' ||
                            domain.mailFromStatus === 'Verified' ||
                            domain.mailFromStatus === 'verified' ||
                            domain.mailFromStatus === 'SUCCESS';
          
          // Also check if status is PENDING and has been for a while (DNS records may be missing)
          const isStuckPending = domain.mailFromStatus === 'Pending' || 
                                 domain.mailFromStatus === 'PENDING' ||
                                 (domain.mailFromStatus === null && domain.mailFromDomain === null);
          
          if (newMap.has(domain.domain) && isComplete) {
            console.log(`✅ Mail From complete for ${domain.domain}: mailFromConfigured=${domain.mailFromConfigured}, mailFromStatus=${domain.mailFromStatus}`);
            newMap.delete(domain.domain);
            changed = true;
          } else if (newMap.has(domain.domain) && isStuckPending) {
            // If status is PENDING, it means DNS records are likely missing
            // Clear the processing state so user can see the actual status
            console.log(`⚠️  Mail From stuck in PENDING for ${domain.domain} - DNS records may be missing. Clearing processing state.`);
            newMap.delete(domain.domain);
            changed = true;
          } else if (newMap.has(domain.domain)) {
            // Log current status for debugging stuck states
            console.log(`⏳ Mail From still processing for ${domain.domain}: mailFromConfigured=${domain.mailFromConfigured}, mailFromStatus=${domain.mailFromStatus}`);
          }
        });
        
        // Persist to localStorage
        if (changed || newMap.size > 0) {
          const domainsArray = Array.from(newMap.entries());
          localStorage.setItem('configuredMailFromDomains', JSON.stringify(domainsArray));
        } else {
          // Clear localStorage if map is empty
          localStorage.removeItem('configuredMailFromDomains');
        }
        
        return changed ? newMap : prev;
      });
    } else if (configuredMailFromDomains.size > 0) {
      // Persist even if no domains are loaded yet
      const domainsArray = Array.from(configuredMailFromDomains.entries());
      localStorage.setItem('configuredMailFromDomains', JSON.stringify(domainsArray));
    }
  }, [allDomains, configuredMailFromDomains]);

  // Clean up local state when DKIM is verified (status becomes 'verified' or 'success')
  // Also persist enablingDKIMDomains in localStorage to survive page refreshes
  React.useEffect(() => {
    if (allDomains.length > 0 && enablingDKIMDomains.size > 0) {
      setEnablingDKIMDomains(prev => {
        const newMap = new Map(prev);
        let changed = false;
        
        allDomains.forEach(domain => {
          // Remove from tracking if DKIM is verified (check multiple possible status values)
          const isVerified = domain.dkimStatus === 'verified' || 
                            domain.dkimStatus === 'Verified' || 
                            domain.dkimStatus === 'success' || 
                            domain.dkimStatus === 'Success';
          
          // Also check if status is PENDING (DNS records may be missing)
          const isStuckPending = domain.dkimStatus === 'pending' || 
                                 domain.dkimStatus === 'Pending' ||
                                 domain.dkimStatus === 'PENDING';
          
          if (newMap.has(domain.domain) && isVerified) {
            newMap.delete(domain.domain);
            changed = true;
          } else if (newMap.has(domain.domain) && isStuckPending) {
            // If status is PENDING, it means DNS records are likely missing
            // Clear the processing state so user can see the actual status
            console.log(`⚠️  DKIM stuck in PENDING for ${domain.domain} - DNS records may be missing. Clearing processing state.`);
            newMap.delete(domain.domain);
            changed = true;
          }
        });
        
        // Persist to localStorage
        if (changed || newMap.size > 0) {
          const domainsArray = Array.from(newMap.keys());
          localStorage.setItem('enablingDKIMDomains', JSON.stringify(domainsArray));
        } else {
          // Clear localStorage if map is empty
          localStorage.removeItem('enablingDKIMDomains');
        }
        
        return changed ? newMap : prev;
      });
    } else if (enablingDKIMDomains.size > 0) {
      // Persist even if no domains are loaded yet
      const domainsArray = Array.from(enablingDKIMDomains.keys());
      localStorage.setItem('enablingDKIMDomains', JSON.stringify(domainsArray));
    }
  }, [allDomains, enablingDKIMDomains]);

  // Clean up local state when domain verification is complete
  React.useEffect(() => {
    if (allDomains.length > 0 && verifyingDomains.size > 0) {
      setVerifyingDomains(prev => {
        const newMap = new Map(prev);
        let changed = false;
        
        allDomains.forEach(domain => {
          // Domain "verification" was legacy SES-coupled. Status is now control-plane lifecycle only.
          // We no longer keep domains in a long-running "verifying" state in the UI.
          const lifecycle = String(domain.lifecycleStatus || domain.status || '').toLowerCase();
          const isConnected = lifecycle === 'connected';
          
          // Log status for debugging (especially for krosskastchannels.com)
          if (newMap.has(domain.domain) || domain.domain === 'krosskastchannels.com') {
            console.log(`🔍 Checking lifecycle status for ${domain.domain}: lifecycleStatus="${domain.lifecycleStatus}", status="${domain.status}", isConnected=${isConnected}, inVerifyingDomains=${newMap.has(domain.domain)}`);
            console.log(`🔍 Full domain object:`, JSON.stringify(domain, null, 2));
            
            // If domain is in verifyingDomains but not connected, it's stuck; we'll clear automatically.
            if (newMap.has(domain.domain) && !isConnected) {
              console.warn(`⚠️ STUCK VERIFICATION DETECTED for ${domain.domain}: lifecycleStatus="${domain.lifecycleStatus}" status="${domain.status}" - clearing automatically.`);
            }
          }
          
          if (newMap.has(domain.domain)) {
            console.log(`🧹 Clearing legacy verification tracking for ${domain.domain} (lifecycleStatus=${domain.lifecycleStatus || domain.status})`);
            newMap.delete(domain.domain);
            changed = true;
          }
        });
        
        // Persist to localStorage (as object with timestamps)
        if (changed || newMap.size > 0) {
          const domainsObj = Object.fromEntries(newMap);
          localStorage.setItem('verifyingDomains', JSON.stringify(domainsObj));
        } else {
          // Clear localStorage if map is empty
          localStorage.removeItem('verifyingDomains');
        }
        
        return changed ? newMap : prev;
      });
    } else if (verifyingDomains.size > 0) {
      // Persist even if no domains are loaded yet (as object with timestamps)
      const domainsObj = Object.fromEntries(verifyingDomains);
      localStorage.setItem('verifyingDomains', JSON.stringify(domainsObj));
    }
  }, [allDomains, verifyingDomains]);

  // Poll for status updates every 30 seconds when there are domains being processed
  React.useEffect(() => {
    if (enablingDKIMDomains.size > 0 || configuredMailFromDomains.size > 0 || verifyingDomains.size > 0) {
      const interval = setInterval(() => {
        console.log('🔄 Polling for status updates...');
        loadDomains();
      }, 30000); // Poll every 30 seconds

      return () => clearInterval(interval);
    }
  }, [enablingDKIMDomains.size, configuredMailFromDomains.size, verifyingDomains.size, loadDomains]);

  // Clear stuck processing states on mount if status is PENDING
  React.useEffect(() => {
    if (allDomains.length > 0) {
      // Clear any domains that are stuck in processing but have PENDING status
      setEnablingDKIMDomains(prev => {
        const newMap = new Map(prev);
        let changed = false;
        allDomains.forEach(domain => {
          if (newMap.has(domain.domain)) {
            // Clear if status is pending OR if it's been in processing for a while
            const isPending = domain.dkimStatus === 'pending' || domain.dkimStatus === 'Pending' || domain.dkimStatus === 'PENDING';
            if (isPending) {
              console.log(`🧹 Clearing stuck DKIM processing state for ${domain.domain} (status is PENDING)`);
              newMap.delete(domain.domain);
              changed = true;
            }
          }
        });
        if (changed) {
          const domainsArray = Array.from(newMap.keys());
          if (domainsArray.length > 0) {
            localStorage.setItem('enablingDKIMDomains', JSON.stringify(domainsArray));
          } else {
            localStorage.removeItem('enablingDKIMDomains');
          }
        }
        return changed ? newMap : prev;
      });

      setConfiguredMailFromDomains(prev => {
        const newMap = new Map(prev);
        let changed = false;
        allDomains.forEach(domain => {
          if (newMap.has(domain.domain)) {
            // Clear if status is pending OR if mailFromDomain is null (not configured)
            const isPending = domain.mailFromStatus === 'Pending' || 
                             domain.mailFromStatus === 'PENDING' || 
                             domain.mailFromStatus === 'pending' ||
                             (domain.mailFromStatus === null && domain.mailFromDomain === null) ||
                             (!domain.mailFromConfigured && !domain.mailFromDomain);
            if (isPending) {
              console.log(`🧹 Clearing stuck Mail From processing state for ${domain.domain} (status is PENDING or not configured)`);
              newMap.delete(domain.domain);
              changed = true;
            }
          }
        });
        if (changed) {
          const domainsArray = Array.from(newMap.entries());
          if (domainsArray.length > 0) {
            localStorage.setItem('configuredMailFromDomains', JSON.stringify(domainsArray));
          } else {
            localStorage.removeItem('configuredMailFromDomains');
          }
        }
        return changed ? newMap : prev;
      });

      setVerifyingDomains(prev => {
        const newMap = new Map(prev);
        let changed = false;
        allDomains.forEach(domain => {
          if (newMap.has(domain.domain)) {
            // Clear if status is pending
            const isPending = (domain.status === 'pending' || domain.status === 'Pending' || domain.status === 'PENDING') ||
                             (domain.verificationStatus === 'pending' || domain.verificationStatus === 'Pending' || domain.verificationStatus === 'PENDING');
            if (isPending) {
              console.log(`🧹 Clearing stuck verification processing state for ${domain.domain} (status is PENDING)`);
              newMap.delete(domain.domain);
              changed = true;
            }
          }
        });
        if (changed) {
          const domainsArray = Array.from(newMap.keys());
          if (domainsArray.length > 0) {
            localStorage.setItem('verifyingDomains', JSON.stringify(domainsArray));
          } else {
            localStorage.removeItem('verifyingDomains');
          }
        }
        return changed ? newMap : prev;
      });
    }
  }, [allDomains]);

  // Group domains by account (when viewing "All") or by main domain (when viewing specific account)
  const groupedDomains = useMemo(() => {
    // When viewing "All" accounts, group by hosting AWS account row (id / slug), not companyTenantId.
    // Domain rows may store company_staging_001 in accountId after tenant unification — that must not
    // collapse all domains under one account; resolveHostingAccountIdForDomain maps by real account id or domain lists.
    if (isAllAccountsSelected) {
      const groups = {};

      accountsForGrouping.forEach((account) => {
        const accountId = account.id;
        const accountDomainsList = allDomains.filter(
          (domain) => resolveHostingAccountIdForDomain(domain, accountsForGrouping) === String(accountId)
        );

        console.log(
          `🔍 Account ${accountId} (${account.alias || account.name}) has ${accountDomainsList.length} domains:`,
          accountDomainsList.map((d) => d.domain)
        );
        
        // Create group for this account
        // Use account alias/name/ID as group identifier, with fallback
        const groupName = account.alias || account.name || `Account ${accountId}`;
        const domains = accountDomainsList
          .map(domain => ({
            ...domain,
            isMainDomain: true, // All domains in account view are treated as main domains
            isSubdomain: false
          }))
          .sort((a, b) => (a.domain || '').toLowerCase().localeCompare((b.domain || '').toLowerCase()));
        groups[accountId] = {
          mainDomain: groupName,
          accountId: accountId,
          accountName: account.name || `Account ${accountId}`,
          accountAlias: account.alias || account.name || `Account ${accountId}`,
          domains,
          isMainDomain: true,
          totalDomains: accountDomainsList.length,
          verifiedCount: accountDomainsList.filter(d => isEffectiveAuthVerified(d)).length,
          pendingCount: accountDomainsList.filter(d => !isEffectiveAuthVerified(d)).length
        };
      });
      
      return Object.values(groups);
    }
    
    // When viewing a specific account, group by main domain (original logic)
    const groups = {};
    
    allDomains.forEach(domain => {
      // Extract main domain (e.g., "subdomain.example.com" -> "example.com")
      const parts = domain.domain.split('.');
      const mainDomain = parts.length > 2 ? parts.slice(-2).join('.') : domain.domain;
      
      if (!groups[mainDomain]) {
        groups[mainDomain] = {
          mainDomain,
          domains: [],
          isMainDomain: false,
          totalDomains: 0,
          verifiedCount: 0,
          pendingCount: 0
        };
      }
      
      // Check if this is the main domain itself
      const isMainDomain = domain.domain === mainDomain;
      if (isMainDomain) {
        groups[mainDomain].isMainDomain = true;
      }
      
      groups[mainDomain].domains.push({
        ...domain,
        isMainDomain,
        isSubdomain: !isMainDomain
      });
      
      groups[mainDomain].totalDomains++;
      if (isEffectiveAuthVerified(domain)) {
        groups[mainDomain].verifiedCount++;
      } else {
        groups[mainDomain].pendingCount++;
      }
    });
    
    // Sort domains alphabetically by domain name within each group
    return Object.values(groups).map(g => ({
      ...g,
      domains: [...g.domains].sort((a, b) =>
        (a.domain || '').toLowerCase().localeCompare((b.domain || '').toLowerCase())
      )
    }));
  }, [allDomains, isAllAccountsSelected, accountsForGrouping, domainRoutingByDomain, pools]);

  // Filter and search domains
  const filteredGroups = useMemo(() => {
    return groupedDomains.filter(group => {
      // When viewing "All" accounts, filter out empty groups (accounts with 0 domains)
      // unless they match the search term
      if (isAllAccountsSelected && group.totalDomains === 0) {
        // Only show empty groups if they match the search term
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          const matchesAccount = group.accountName?.toLowerCase().includes(searchLower) || 
                                  group.accountAlias?.toLowerCase().includes(searchLower) ||
                                  group.accountId?.toString().includes(searchLower);
          return matchesAccount;
        }
        // Hide empty groups by default
        return false;
      }
      
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        // When viewing "All", also search by account name/alias
        if (isAllAccountsSelected && (group.accountName?.toLowerCase().includes(searchLower) || 
            group.accountAlias?.toLowerCase().includes(searchLower))) {
          return true;
        }
        // Search by domain name
        const hasMatchingDomain = group.domains.some(domain => 
          domain.domain.toLowerCase().includes(searchLower)
        );
        if (!hasMatchingDomain) return false;
      }
      
      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'verified' && group.verifiedCount === 0) return false;
        if (statusFilter === 'pending' && group.pendingCount === 0) return false;
      }
      
      return true;
    });
  }, [groupedDomains, searchTerm, statusFilter, isAllAccountsSelected]);

  const totalDomainCount = useMemo(
    () => filteredGroups.reduce((sum, g) => sum + (g.domains?.length || 0), 0),
    [filteredGroups]
  );

  // Helper functions for domain management
  const toggleGroupExpansion = (mainDomain) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(mainDomain)) {
      newExpanded.delete(mainDomain);
    } else {
      newExpanded.add(mainDomain);
    }
    setExpandedGroups(newExpanded);
  };

  const openEmailAuthWizard = (domainObj) => {
    setEmailAuthWizardDomain(domainObj);
    setShowEmailAuthWizard(true);
  };

  const toggleDomainSelection = (domainId) => {
    const newSelected = new Set(selectedDomains);
    if (newSelected.has(domainId)) {
      newSelected.delete(domainId);
    } else {
      newSelected.add(domainId);
    }
    setSelectedDomains(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const selectAllInGroup = (group) => {
    const newSelected = new Set(selectedDomains);
    group.domains.forEach(domain => {
      newSelected.add(domain.id);
    });
    setSelectedDomains(newSelected);
    setShowBulkActions(true);
  };

  const clearSelection = () => {
    setSelectedDomains(new Set());
    setShowBulkActions(false);
  };

  const getStatusBadge = (status) => {
    const badgeContent = (() => {
      switch (status) {
        case 'pending':
          return <Badge bg="warning"><XCircle className="me-1" />Pending</Badge>;
        case 'connected':
          return <Badge bg="success"><CheckCircle className="me-1" />Connected</Badge>;
        case 'disabled':
          return <Badge bg="secondary">Disabled</Badge>;
        case 'error':
          return <Badge bg="warning"><XCircle className="me-1" />Attention</Badge>;
        default:
          return <Badge bg="secondary">Unknown</Badge>;
      }
    })();
    
    // Add tooltip based on status
    const tooltipText = (() => {
      switch (status) {
        case 'pending':
          return 'Domain authentication is not fully verified yet (DKIM/SPF/DMARC).';
        case 'connected':
          return 'Domain is active/usable inside Kross Global (control-plane). Email authentication (DKIM/SPF/DMARC) is shown separately.';
        case 'disabled':
          return 'Domain is disabled in Kross Global (control-plane).';
        case 'error':
          return 'Domain needs attention (control-plane issue). Email authentication (DKIM/SPF/DMARC) is shown separately.';
        default:
          return `Domain status: ${status}`;
      }
    })();
    
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{tooltipText}</Tooltip>}
      >
        <span>{badgeContent}</span>
      </OverlayTrigger>
    );
  };

  const tableColumnStyles = {
    status: { width: '140px', textAlign: 'center', whiteSpace: 'nowrap' },
    dns: { width: '220px' },
    accounts: { width: '140px', textAlign: 'center', whiteSpace: 'nowrap' },
    lastUsed: { width: '110px', textAlign: 'center', whiteSpace: 'nowrap' },
    actions: { width: '260px' },
  };

  // Helper function to format provider names
  function formatProviderName(provider) {
    const providerMap = {
      'aws': 'AWS',
      // (legacy provider removed)
      'ses': 'SES',
      // Inbound can be Postmark or SES; keep 'custom' as Postmark for backwards-compat UI labels.
      'custom': 'Postmark',
      'postmark': 'Postmark',
      'mailgun': 'Mailgun',
      'sendgrid': 'Twilio SendGrid',
      'elastic_email': 'Elastic Email',
      'elasticemail': 'Elastic Email',
      'twilio': 'Twilio SendGrid',
      'twilio_sendgrid': 'Twilio SendGrid',
      'mailreach': 'Mailreach',
      'smtp_com': 'SMTP.com',
      'smtpcom': 'SMTP.com',
      'mailchannels': 'MailChannels'
    };
    return providerMap[provider] || provider || 'Unknown';
  }

  function normalizeAuthStatus(status) {
    return String(status || '').toLowerCase();
  }

  function isVerifiedAuthStatus(status) {
    const normalized = normalizeAuthStatus(status);
    return normalized === 'verified' || normalized === 'success' || normalized === 'configured' || normalized === 'pass';
  }

  /** Display name for Upstream relay credentials panel title. */
  function providerCredsLabel(providerType) {
    const m = { SMTP_COM: 'SMTP.com', MAILCHANNELS: 'MailChannels', MAILGUN: 'Mailgun', SENDGRID: 'Twilio SendGrid', ELASTIC_EMAIL: 'Elastic Email' };
    return m[String(providerType || '').toUpperCase()] || formatProviderName(String(providerType || '').toLowerCase()) || providerType || 'Provider';
  }

  function extractRelaySuffixFromPool(pool) {
    const direct = String(pool?.relayAccountSuffix || '').trim();
    if (RELAY_ACCOUNT_SUFFIX_PATTERN.test(direct)) return direct;
    const name = String(pool?.name || '');
    const tagged = name.match(/\[([A-Za-z0-9]{4})\]\s*$/);
    return tagged?.[1] || '';
  }

  function stripRelaySuffixTagFromPoolName(name) {
    return String(name || '').replace(/\s*\[[A-Za-z0-9]{4}\]\s*$/, '').trim();
  }

  function providerSelectionLabel(providerType, poolsForProvider = []) {
    const base = providerCredsLabel(providerType);
    const suffixes = Array.from(
      new Set(
        (Array.isArray(poolsForProvider) ? poolsForProvider : [])
          .map((pool) => extractRelaySuffixFromPool(pool))
          .filter((suffix) => RELAY_ACCOUNT_SUFFIX_PATTERN.test(String(suffix || '')))
      )
    );
    if (suffixes.length === 1) return `${base}${suffixes[0]}`;
    if (suffixes.length > 1) return `${base} (${suffixes.join(', ')})`;
    return base;
  }

  function poolSelectionLabel(pool) {
    const provider = String(pool?.providerType || '').toUpperCase();
    const providerBase = providerCredsLabel(provider);
    const suffix = extractRelaySuffixFromPool(pool);
    const relayAlias = suffix ? `${providerBase}${suffix}` : providerBase;
    const cleanPoolName = stripRelaySuffixTagFromPoolName(pool?.name || pool?.poolId || '');
    if (!cleanPoolName || cleanPoolName.toLowerCase() === relayAlias.toLowerCase()) return relayAlias;
    return `${relayAlias} - ${cleanPoolName}`;
  }

  function isLikelyEmailAddress(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

  function isValidMailChannelsAccountId(value) {
    const trimmed = String(value || '').trim();
    if (!trimmed) return false;
    if (trimmed.includes('@') || isLikelyEmailAddress(trimmed)) return false;
    return /^[A-Za-z0-9][A-Za-z0-9_-]{2,63}$/.test(trimmed);
  }

  function getOutboundProviderForDomain(domain) {
    const domainName = domain?.domain;
    const policy = domainRoutingByDomain[domainName];
    const purpose = uiPurposeFromStoredDomainType(domain?.domainType || domain?.domain_type || 'business');
    const preferredRouteKey = purpose === 'cold_outreach' ? 'cold_outreach' : (purpose === 'newsletter' ? 'newsletter' : 'transactional');
    const primaryPoolId =
      policy?.routes?.[preferredRouteKey]?.primaryPoolId ||
      policy?.routes?.cold_outreach?.primaryPoolId ||
      policy?.routes?.transactional?.primaryPoolId ||
      policy?.routes?.newsletter?.primaryPoolId;
    const pool = pools.find((p) => p.poolId === primaryPoolId);
    const providerType = String(pool?.providerType || '').toUpperCase();
    if (providerType === 'SES') {
      return { providerType: '', providerLabel: '' };
    }
    const providerLabel = providerType ? formatProviderName(providerType.toLowerCase()) : '';
    return { providerType, providerLabel };
  }

  function getEffectiveAuthStatuses(domain) {
    const { providerType, providerLabel } = getOutboundProviderForDomain(domain);
    const isSmtpCom = providerType === 'SMTP_COM';
    const dbAuthProvider = String(
      domain.authProvider
      || domain.dkimProvider
      || domain.spfProvider
      || ((domain.mailgunDkimSelector && String(domain.mailgunDkimSelector).trim()) ? 'mailgun' : '')
    ).toLowerCase();
    const hasVerifiedSnapshot = Boolean(domain.authVerifiedAt && dbAuthProvider);
    const dkimStatus =
      (isSmtpCom && !isVerifiedAuthStatus(domain.dkimStatus))
      || (hasVerifiedSnapshot && !isVerifiedAuthStatus(domain.dkimStatus))
        ? 'verified'
        : domain.dkimStatus;
    const spfStatus =
      hasVerifiedSnapshot && !isVerifiedAuthStatus(domain.spfStatus)
        ? 'verified'
        : domain.spfStatus;
    const dmarcStatus =
      hasVerifiedSnapshot && !isVerifiedAuthStatus(domain.dmarcStatus)
        ? 'verified'
        : domain.dmarcStatus;
    // Show the provider label based on the selected outbound relay (routing policy).
    // This matches how users reason about DKIM/SPF alignment: it should reflect the active relay.
    const authProviderLabel = providerLabel || (dbAuthProvider ? (formatProviderName(dbAuthProvider) || dbAuthProvider) : '');
    return {
      dkimStatus,
      spfStatus,
      dmarcStatus,
      providerType,
      providerLabel,
      authProviderLabel,
      isSmtpCom,
    };
  }

  function isEffectiveAuthVerified(domain) {
    // If backend says domain is connected/verified/active, treat as verified so we don't show "Pending" when DKIM/SPF/DMARC come back in a different shape
    const lifecycle = String(domain.lifecycleStatus || domain.status || '').toLowerCase();
    if (lifecycle === 'connected' || lifecycle === 'verified' || lifecycle === 'active') return true;
    const auth = getEffectiveAuthStatuses(domain);
    return (
      isVerifiedAuthStatus(auth.dkimStatus) &&
      isVerifiedAuthStatus(auth.spfStatus) &&
      isVerifiedAuthStatus(auth.dmarcStatus)
    );
  }

  /** True only when DKIM, SPF, and DMARC are verified. No lifecycle shortcut — use for Authentication badge only. */
  function isAuthenticationConfigured(domain) {
    const provider = String(domain.authProvider || domain.dkimProvider || domain.spfProvider || '').toLowerCase();
    if (domain.authVerifiedAt && provider) return true;
    const auth = getEffectiveAuthStatuses(domain);
    return (
      isVerifiedAuthStatus(auth.dkimStatus) &&
      isVerifiedAuthStatus(auth.spfStatus) &&
      isVerifiedAuthStatus(auth.dmarcStatus)
    );
  }

  function isAuthenticationInProgress(domain) {
    const auth = getEffectiveAuthStatuses(domain);
    const provider = String(domain.authProvider || domain.dkimProvider || domain.spfProvider || '').toLowerCase();
    return Boolean(
      (provider && provider !== 'keep' && provider !== 'custom') ||
      String(domain.mailgunDkimSelector || '').trim() ||
      String(domain.mailchannelsDkimSelector || '').trim() ||
      isVerifiedAuthStatus(auth.dkimStatus) ||
      isVerifiedAuthStatus(auth.spfStatus) ||
      isVerifiedAuthStatus(auth.dmarcStatus)
    );
  }

  function getEffectiveStatus(domain) {
    return isEffectiveAuthVerified(domain) ? 'connected' : 'pending';
  }

  const getDnsStatusBadge = (status, recordType = '', options = {}) => {
    const providerLabel = options.providerLabel;
    const note = options.note;
    const badgeContent = (() => {
      switch (status) {
        case 'verified':
          return <Badge bg="success" className="me-1">✓</Badge>;
        case 'configured':
          return <Badge bg="success" className="me-1">✓</Badge>;
        case 'pending':
          return <Badge bg="warning" className="me-1">⏳</Badge>;
        case 'failed':
          return <Badge bg="danger" className="me-1">✗</Badge>;
        default:
          return <Badge bg="secondary" className="me-1">?</Badge>;
      }
    })();
    
    // Add tooltip based on status and record type
    let tooltipText = (() => {
      const recordName = recordType ? `${recordType} ` : '';
      switch (status) {
        case 'verified':
          return `${recordName}DNS record is verified and active`;
        case 'configured':
          return `${recordName}DNS record is configured`;
        case 'pending':
          return `${recordName}DNS record verification is in progress. This may take 5-60 minutes.`;
        case 'failed':
          return `${recordName}DNS record verification failed. Please check your DNS records.`;
        default:
          return `${recordName}DNS record status: ${status}`;
      }
    })();
    if (providerLabel) {
      tooltipText += ` (via ${providerLabel})`;
    }
    if (note) {
      tooltipText += ` ${note}`;
    }
    
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{tooltipText}</Tooltip>}
      >
        <span>{badgeContent}</span>
      </OverlayTrigger>
    );
  };

  const handleAddDomain = () => {
    // If viewing a specific account, pre-select it
    if (!isAllAccountsSelected && selectedAccount && selectedAccount.id !== 'all') {
      setSelectedAccountForDomain(selectedAccount.id);
    } else {
      // If viewing "All", start with no selection (user must choose)
      setSelectedAccountForDomain('');
    }
    setShowAddModal(true);
  };

  const handleSearchDomain = async () => {
    if (!newDomain.trim()) return;

    // If viewing "All" accounts, require account selection
    if (isAllAccountsSelected && !selectedAccountForDomain) {
      alert('Please select an account to add the domain to.');
      return;
    }

    // Client-side validation - only check basic domain format
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
    if (!domainRegex.test(newDomain.trim())) {
      alert('Please enter a valid domain format (e.g., example.com)');
      return;
    }

    setIsSearchingDomain(true);
    setDomainSearchResult(null);
    setRoute53Result(null);
    
    try {
      // Step 1: Check if domain is already in our AWS account (via Domain Manager data)
      // Use RTK Query to get domains (includes auth automatically)
      const domainsResult = await dispatch(marketingApi.endpoints.getDomains.initiate());
      if (domainsResult.data && domainsResult.data.data && domainsResult.data.data.domains) {
        const existingDomain = domainsResult.data.data.domains.find(d => d.domain === newDomain.trim());
        if (existingDomain) {
          // Domain is already in our AWS account
          setDomainSearchResult({
            status: 'in_aws_account',
            message: 'Domain is already configured in your AWS account',
            domain: newDomain.trim(),
            awsStatus: existingDomain.status,
            verificationStatus: existingDomain.verificationStatus || 'pending',
            dkimStatus: existingDomain.dkimStatus,
            spfStatus: existingDomain.spfStatus,
            dmarcStatus: existingDomain.dmarcStatus
          });
          setIsSearchingDomain(false);
          return;
        }
      }
      
      // Step 2: Check Route 53 registered domains across all accounts (if account is selected)
      // This is important because domains can be registered in Route 53 but not yet in our manager.
      let foundInRoute53 = false;
      if (isAllAccountsSelected && selectedAccountForDomain) {
        // Check the selected account's Route 53 registered domains
        try {
          const route53OwnershipResult = await dispatch(marketingApi.endpoints.route53Ownership.initiate({ 
            domain: newDomain.trim(), 
            accountId: selectedAccountForDomain 
          }));
          if (route53OwnershipResult.data && route53OwnershipResult.data.data && route53OwnershipResult.data.data.owned) {
            foundInRoute53 = true;
            setDomainSearchResult({
              domain: newDomain.trim(),
              status: 'in_route53',
              message: `Domain is registered in Route 53 for the selected account. You can add it to Domain Manager.`,
              hostedZoneId: route53OwnershipResult.data.data.hostedZoneId,
              zoneName: route53OwnershipResult.data.data.zoneName
            });
          }
        } catch (route53Error) {
          console.log('Route 53 ownership check failed:', route53Error);
          // Continue with discovery if Route 53 check fails
        }
      } else if (!isAllAccountsSelected && selectedAccount && selectedAccount.id !== 'all') {
        // Check the current account's Route 53 registered domains
        try {
          const route53OwnershipResult = await dispatch(marketingApi.endpoints.route53Ownership.initiate({ 
            domain: newDomain.trim(), 
            accountId: selectedAccount.id 
          }));
          if (route53OwnershipResult.data && route53OwnershipResult.data.data && route53OwnershipResult.data.data.owned) {
            foundInRoute53 = true;
            setDomainSearchResult({
              domain: newDomain.trim(),
              status: 'in_route53',
              message: `Domain is registered in Route 53 for ${selectedAccount.alias || selectedAccount.name}. You can add it to Domain Manager.`,
              hostedZoneId: route53OwnershipResult.data.data.hostedZoneId,
              zoneName: route53OwnershipResult.data.data.zoneName
            });
          }
        } catch (route53Error) {
          console.log('Route 53 ownership check failed:', route53Error);
          // Continue with discovery if Route 53 check fails
        }
      }

      // Step 3: If not found in Route 53, check availability using backend
      if (!foundInRoute53) {
        try {
          const discoveryResult = await dispatch(marketingApi.endpoints.domainDiscovery.initiate(newDomain.trim()));
          if (discoveryResult.data && discoveryResult.data.data) {
            setDomainSearchResult(discoveryResult.data.data);
          } else {
            // Fallback: Assume domain is registered but not in AWS
            setDomainSearchResult({
              domain: newDomain.trim(),
              status: 'registered',
              message: 'Domain appears to be registered but not in your AWS account. You can claim it by adding a TXT record.',
              ipAddress: 'Unknown'
            });
          }
        } catch (discoveryError) {
          console.log('Domain discovery failed, using fallback:', discoveryError);
          // Fallback: Assume domain is registered but not in AWS
          setDomainSearchResult({
            domain: newDomain.trim(),
            status: 'registered',
            message: 'Domain appears to be registered but not in your AWS account. You can claim it by adding a TXT record.',
            ipAddress: 'Unknown'
          });
        }
      }
        
      // Step 4: If domain is registered but not in our AWS, offer to claim (only if not already checked)
      if (domainSearchResult && domainSearchResult.status === 'registered' && !foundInRoute53) {
        try {
          const accountId = isAllAccountsSelected && selectedAccountForDomain 
            ? selectedAccountForDomain 
            : (!isAllAccountsSelected && selectedAccount && selectedAccount.id !== 'all' ? selectedAccount.id : undefined);
          const route53Result = await dispatch(marketingApi.endpoints.route53Ownership.initiate({ 
            domain: newDomain.trim(),
            accountId: accountId
          }));
          if (route53Result.data && route53Result.data.data) {
            setRoute53Result(route53Result.data.data);
          }
        } catch (claimError) {
          console.log('Route 53 ownership check failed:', claimError);
        }
      }
    } catch (error) {
      console.error('Error checking domain:', error);
      alert('Error checking domain. Please try again.');
    } finally {
      setIsSearchingDomain(false);
    }
  };

  const handleSubmitDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;

    // Show confirmation modal for safety
    setShowConfirmationModal(true);
  };

  const handleConfirmDomain = async () => {
    if (confirmationText !== 'CONFIRM') {
      alert('You must type "CONFIRM" exactly to proceed.');
      return;
    }
    if (!mxAcknowledged) {
      alert('Please acknowledge the MX record warning before proceeding.');
      return;
    }

    setIsConfirming(true);
    try {
      // Step 1: Domain Confirmation (safety check)
      await domainConfirmation({ 
        domain: newDomain.trim(), 
        confirmation: confirmationText 
      }).unwrap();

      // Step 2: Add domain to Kross Global (inbound is always Postmark-managed)
      // Include account ID if viewing "All" accounts
      const domainData = {
        domain: newDomain.trim(),
        inboundMxAcknowledged: true,
        inboundMxAcknowledgedAt: new Date().toISOString(),
      };
      if (isAllAccountsSelected && selectedAccountForDomain) {
        domainData.accountId = selectedAccountForDomain;
      } else if (!isAllAccountsSelected && selectedAccount && selectedAccount.id !== 'all') {
        domainData.accountId = selectedAccount.id;
      }
      await createDomain(domainData).unwrap();
      
      // console.log('Domain added successfully');
      
      // Close modals and refresh
      setShowConfirmationModal(false);
      setShowAddModal(false);
      setNewDomain('');
      setSelectedAccountForDomain('');
      setMxAcknowledged(false);
      setDomainSearchResult(null);
      setRoute53Result(null);
      setConfirmationText('');
      loadDomains();
    } catch (error) {
      console.error('Error adding domain:', error);
      if (error.data?.message) {
        alert(`Error: ${error.data.message}`);
      } else {
        alert('Error adding domain. Please try again.');
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const handleAddToDomainManager = async () => {
    if (!domainSearchResult?.domain) return;

    // If viewing "All" accounts, require account selection
    if (isAllAccountsSelected && !selectedAccountForDomain) {
      alert('Please select an account to add the domain to.');
      return;
    }
    if (!mxAcknowledged) {
      alert('Please acknowledge the MX record warning before proceeding.');
      return;
    }

    setIsAddingToManager(true);
    try {
      // Include account ID if viewing "All" accounts
      const domainData = { 
        domain: domainSearchResult.domain,
        hostProvider: selectedHostProvider,
        // Inbound is always Postmark-managed.
        receivingProvider: INBOUND_PROVIDER,
        inboundMxAcknowledged: true,
        inboundMxAcknowledgedAt: new Date().toISOString(),
      };
      
      if (isAllAccountsSelected && selectedAccountForDomain) {
        domainData.accountId = selectedAccountForDomain;
      } else if (!isAllAccountsSelected && selectedAccount && selectedAccount.id !== 'all') {
        domainData.accountId = selectedAccount.id;
      }

      // Use RTK Query mutation to add domain (includes auth automatically)
      const result = await createDomain(domainData).unwrap();
      
      if (result.success || result.data) {
        alert('Domain successfully added! Inbound email is managed by Postmark. You can now configure SPF, DKIM, and DMARC records.');
        
        // Close modal and refresh
        setShowAddModal(false);
        setNewDomain('');
        setSelectedAccountForDomain('');
        setMxAcknowledged(false);
        setDomainSearchResult(null);
        setRoute53Result(null);
        loadDomains(); // Refresh domain list
      }
    } catch (error) {
      console.error('Error adding domain to manager:', error);
      
      // Handle specific error cases
      const errorMessage = error?.data?.message || error?.message || 'Failed to add domain to manager';
      // Don't show the DNS verification error if domain is registered in Route 53 Domains
      // The platform will automatically handle DNS verification
      if (errorMessage.includes('not ready for email sending') && !domainSearchResult?.status?.includes('in_route53')) {
        alert('Domain needs DNS verification first. Please ensure the domain is properly configured in your DNS settings before adding to the manager.');
      } else {
        alert(`Failed to add domain to manager: ${errorMessage}`);
      }
    } finally {
      setIsAddingToManager(false);
    }
  };

  const handleClaimDomain = async () => {
    if (!domainSearchResult?.domain) return;
    
    setIsClaiming(true);
    try {
      // Check if domain has been claimed (has TXT record)
      const claimResponse = await fetch(`${getApiUrl()}/domains/${domainSearchResult.domain}/check-claim`, {
        method: 'GET'
      });
      
      if (claimResponse.ok) {
        const claimResult = await claimResponse.json();
        if (claimResult.data.claimed) {
          // Domain is claimed, add to manager
          await handleAddToDomainManager();
        } else {
          alert('Domain not yet claimed. Please add the TXT record and try again.');
        }
      } else {
        alert('Unable to check domain claim status. Please try again.');
      }
    } catch (error) {
      console.error('Error checking domain claim:', error);
      alert('Error checking domain claim. Please try again.');
    } finally {
      setIsClaiming(false);
    }
  };

  const handleEnableDKIM = async (domain) => {
    if (!domain) return;
    
    // Find the domain object to get its accountId
    const domainObj = allDomains.find(d => d.domain === domain);
    const accountId = domainObj?.accountId;
    
    if (!window.confirm(`Enable DKIM for ${domain}? This will add the required provider DKIM DNS records in Route 53.`)) {
      return;
    }
    
    setEnablingDKIMForDomain(domain);
    try {
      const result = await enableDKIM({ domain, accountId }).unwrap();
      
      // Add domain to enablingDKIMDomains map to track until verified
      setEnablingDKIMDomains(prev => {
        const newMap = new Map(prev);
        newMap.set(domain, true);
        // Persist to localStorage immediately
        const domainsArray = Array.from(newMap.keys());
        localStorage.setItem('enablingDKIMDomains', JSON.stringify(domainsArray));
        return newMap;
      });
      
      if (result.data?.manualSetupRequired) {
        // Manual setup required - show DNS records
        const dnsRecords = result.data.dnsRecords || [];
        const recordsText = dnsRecords.map(r => `${r.name} CNAME ${r.value}`).join('\n');
        alert(`DKIM enabled for ${domain}!\n\nPlease add these DNS records manually:\n\n${recordsText}\n\nThe badge will show "Enabling..." until verification completes (5-60 minutes).`);
      } else {
        // Automatic setup successful
        alert(`✅ DKIM enabled and DNS records added for ${domain}!\n\nVerification will complete in 5-60 minutes after DNS propagation. The badge will show "Enabling..." until verification completes.`);
      }
      
      // Refresh domain list
      loadDomains();
    } catch (error) {
      console.error('Error enabling DKIM:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to enable DKIM. Please try again.';
      alert(`Error: ${errorMessage}`);
      // Remove from tracking if there was an error
      setEnablingDKIMDomains(prev => {
        const newMap = new Map(prev);
        newMap.delete(domain);
        // Update localStorage
        if (newMap.size > 0) {
          const domainsArray = Array.from(newMap.keys());
          localStorage.setItem('enablingDKIMDomains', JSON.stringify(domainsArray));
        } else {
          localStorage.removeItem('enablingDKIMDomains');
        }
        return newMap;
      });
    } finally {
      setEnablingDKIMForDomain(null);
    }
  };

  const handleConfigureMailFrom = async (domain) => {
    // CRITICAL: Version check to ensure new code is loaded
    console.log(`🔍 VERSION CHECK - handleConfigureMailFrom v2.0 - ${new Date().toISOString()}`);
    
    if (!domain) return;
    
    // Prevent multiple clicks
    if (configuringMailFromForDomain === domain) return;
    
    // Find the domain object to get its accountId and region
    const domainObj = allDomains.find(d => d.domain === domain);
    console.log(`🔍 DEBUG - Looking for domain: ${domain}`);
    console.log(`🔍 DEBUG - Found domainObj:`, domainObj);
    console.log(`🔍 DEBUG - domainObj.accountId:`, domainObj?.accountId);
    console.log(`🔍 DEBUG - allDomains length:`, allDomains.length);
    console.log(`🔍 DEBUG - allDomains:`, allDomains.map(d => ({ domain: d.domain, accountId: d.accountId })));
    
    let accountId = domainObj?.accountId;
    
    // If domain doesn't have accountId, try to find it from account mapping
    if (!accountId) {
      console.log(`⚠️ Domain object doesn't have accountId, trying account mapping...`);
      // Find which account this domain belongs to by checking account domains
      const matchingAccount = allAccounts.find(account => {
        const accountDomains = account.domains || [];
        return accountDomains.some(accDomain => 
          domain.toLowerCase() === accDomain.toLowerCase() ||
          domain.toLowerCase().endsWith(`.${accDomain.toLowerCase()}`)
        );
      });
      
      if (matchingAccount) {
        // Use accountId (AWS account ID) if available, otherwise use id (which might also be the AWS account ID)
        accountId = matchingAccount.accountId || matchingAccount.id;
        console.log(`🔍 Found accountId ${accountId} for domain ${domain} from account mapping (account: ${matchingAccount.name || matchingAccount.alias})`);
      } else {
        // FALLBACK: If no matching account found, try using selectedAccount if it's not "all"
        if (selectedAccount && selectedAccount.id && selectedAccount.id !== 'all') {
          accountId = selectedAccount.accountId || selectedAccount.id;
          console.log(`🔍 Using selectedAccount accountId ${accountId} as fallback for domain ${domain}`);
        } else {
          console.warn(`⚠️ Could not find accountId for domain ${domain}`);
        }
      }
    } else {
      console.log(`✅ Found accountId ${accountId} directly from domain object`);
    }
    
    // Log the accountId being used for debugging
    console.log(`🔍 DEBUG - All accounts:`, allAccounts);
    console.log(`🔍 DEBUG - Selected account:`, selectedAccount);
    
    const region = domainObj?.region || selectedAccount?.region || 'us-east-1'; // Default to us-east-1 if not specified
    
    if (accountId) {
      console.log(`✅ FINAL - Using accountId ${accountId} for domain ${domain} in region ${region}`);
    } else {
      console.error(`❌ FINAL - No accountId found for domain ${domain} - will use default account (THIS WILL FAIL FOR SUB-ACCOUNTS!)`);
    }
    
    const mailFromDomain = `mail.${domain}`;
    const customDomain = window.prompt(
      `Configure MAIL FROM domain for ${domain}?\n\nThis will set up SPF alignment. Default is ${mailFromDomain}.\n\nEnter a custom subdomain (e.g., mail.${domain} or email.${domain}) or press OK to use default:`,
      mailFromDomain
    );
    
    if (!customDomain) return; // User cancelled
    
    // Validate it's a subdomain
    if (!customDomain.endsWith(`.${domain}`) || customDomain === domain) {
      alert(`Error: MAIL FROM domain must be a subdomain of ${domain} (e.g., mail.${domain})`);
      return;
    }
    
    // Set processing state
    setConfiguringMailFromForDomain(domain);
    
    // CRITICAL: Log what we're about to send
    console.log(`🚀 About to call configureMailFrom with:`, {
      domain,
      mailFromDomain: customDomain,
      accountId,
      region,
      accountIdType: typeof accountId,
      accountIdTruthy: !!accountId
    });
    
    // CRITICAL: Ensure accountId is a string (not undefined/null)
    if (!accountId) {
      console.error(`❌ CRITICAL ERROR: accountId is missing for domain ${domain}!`);
      console.error(`❌ This will cause the backend to use the wrong AWS account!`);
      alert(`Error: Could not determine AWS account for domain ${domain}. Please ensure the domain is associated with an AWS account.`);
      return;
    }
    
    try {
      const result = await configureMailFrom({ 
        domain, 
        mailFromDomain: customDomain,
        accountId: accountId, // Explicitly pass accountId
        region: region || 'us-east-1' // Default region if not specified
      }).unwrap();
      
      // Store the configured MAIL FROM domain in local state to persist processing state
      setConfiguredMailFromDomains(prev => {
        const newMap = new Map(prev);
        newMap.set(domain, customDomain);
        return newMap;
      });
      
      if (result.data?.manualSetupRequired) {
        // Manual setup required - show DNS records
        const dnsRecords = result.data.dnsRecords || [];
        const recordsText = dnsRecords.map(r => `${r.name} ${r.type} ${r.value}`).join('\n');
        alert(`MAIL FROM domain configured for ${domain}!\n\nPlease add these DNS records manually:\n\n${recordsText}`);
      } else {
        // Automatic setup successful
        alert(`✅ MAIL FROM domain configured and DNS records added for ${domain}!\n\nVerification will complete in 5-60 minutes after DNS propagation.`);
      }
      
      // Refresh domain list to get updated status
      await loadDomains();
    } catch (error) {
      console.error('Error configuring MAIL FROM domain:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to configure MAIL FROM domain. Please try again.';
      alert(`Error: ${errorMessage}`);
      // Remove from configured list on error
      setConfiguredMailFromDomains(prev => {
        const newMap = new Map(prev);
        newMap.delete(domain);
        return newMap;
      });
    } finally {
      // Clear processing state after a short delay to allow UI to update
      setTimeout(() => {
        setConfiguringMailFromForDomain(null);
      }, 1000);
    }
  };

  const handleVerifyDomain = async (domain) => {
    if (!domain) return;
    
    // Prevent double-clicking
    if (verifyingDomain === domain || verifyingDomains.has(domain)) {
      console.log('⚠️ Verification already in progress for', domain);
      return;
    }
    
    // Find the domain object to get its accountId
    const domainObj = allDomains.find(d => d.domain === domain);
    const accountId = domainObj?.accountId;
    
    if (!accountId) {
      console.warn(`⚠️ No accountId found for domain ${domain}, proceeding without cross-account support`);
    }
    
    // Set loading state immediately to disable button and show "Verifying..." badge
    setVerifyingDomain(domain);
    setIsVerifying(true);
    
    try {
      // Get auth token for the request
      const apiModule = await import('../services/api');
      await apiModule.waitForAuthFunctions();
      const tokenGetter = apiModule.getTokenGetter();
      const token = await tokenGetter?.() || '';
      
      // Step 1: Configure DNS records (SPF, DKIM, DMARC) in Route 53
      const domainObj = allDomains.find(d => d.domain === domain);
      const receivingProvider = domainObj?.receivingProvider || 'postmark';
      let receivingProviderConfigId = domainObj?.receivingProviderConfigId;
      
      let configureResponse;
      let responseData;
      
      // Call SES DNS configuration endpoint (ACS removed)
      configureResponse = await fetch(`${getApiUrl()}/domains/${domain}/configure-dns`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(accountId ? { accountId } : {})
      });
      
      if (configureResponse.ok) {
        // Read response body ONCE and store it
        responseData = await configureResponse.json();
        console.log('DNS configuration response:', responseData);
        
        // Add domain to verifyingDomains map to track until verified
        // Store timestamp to detect stuck verifications (>60 minutes)
        setVerifyingDomains(prev => {
          const newMap = new Map(prev);
          newMap.set(domain, Date.now()); // Store timestamp instead of just true
          // Persist to localStorage immediately (as object with timestamps)
          const domainsObj = Object.fromEntries(newMap);
          localStorage.setItem('verifyingDomains', JSON.stringify(domainsObj));
          console.log('💾 Saved verifyingDomains to localStorage:', domainsObj);
          return newMap;
        });
        
        // Check if nameservers need update
        if (responseData.nameserversNeedUpdate || responseData.data?.nameserversNeedUpdate) {
          setNameserverUpdateData({
            domain,
            currentNameservers: responseData.data?.currentNameservers || [],
            route53Nameservers: responseData.data?.route53Nameservers || [],
            canUpdateAutomatically: responseData.data?.canUpdateAutomatically || false,
            accountId
          });
          setShowNameserverUpdateModal(true);
          setIsVerifying(false);
          setVerifyingDomain(null);
          return; // Wait for user to choose if they want to update nameservers
        }
        
        // Check if existing records were found - show modal for user confirmation
        if (responseData.existingRecordsFound || responseData.data?.existingRecords?.length > 0) {
          setExistingRecordsData({
            domain,
            existingRecords: responseData.data?.existingRecords || [],
            accountId
          });
          setShowExistingRecordsModal(true);
          setIsVerifying(false);
          setVerifyingDomain(null);
          return; // Wait for user to choose how to handle existing records
        }
        
        // Check if DNS records were actually created or if manual setup is required
        if (responseData.manualSetupRequired || responseData.data?.manualSetupRequired) {
          const dnsRecords = responseData.dnsRecords || responseData.data?.dnsRecords || [];
          const recordsText = dnsRecords.map(r => `${r.name} ${r.type} ${r.value}`).join('\n');
          alert(`⚠️ DNS records need to be added manually!\n\nRoute 53 hosted zone not found or domain nameservers don't match.\n\nPlease add these DNS records to your DNS provider:\n\n${recordsText}\n\nAfter adding these records, verification will complete in 5-60 minutes.`);
        } else {
          alert('✅ DNS records created! SPF, DMARC, and provider DKIM (if provided) have been written to Route 53.\n\nDNS changes may take a few minutes to propagate. Use the Email Authentication (SPF/DKIM/DMARC) modal to verify records. Mail From is a separate step if needed.');
        }
        
        // Force immediate data refresh with multiple approaches
        try {
          // 1. RTK Query refetch immediately
          // console.log('🔄 Refreshing domain data...');
          loadDomains();
          
          // 2. Clear browser cache
          if ('caches' in window) {
            caches.keys().then(names => {
              names.forEach(name => {
                caches.delete(name);
              });
            });
          }
          
          // 3. Force hard refresh as backup
          setTimeout(() => {
            // console.log('🔄 Forcing page refresh...');
            window.location.reload();
          }, 1000);
        } catch (error) {
          console.error('Error refreshing data:', error);
          // Fallback to simple reload
          window.location.reload();
        }
      } else {
        // Handle error response - try to read error message
        let errorMessage = 'DNS configuration failed. Please try again.';
        try {
          const errorData = await configureResponse.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use status text
          errorMessage = configureResponse.statusText || errorMessage;
        }
        console.error('DNS configuration error:', errorMessage);
        alert(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error verifying domain:', error);
      const errorMessage = error?.message || 'Failed to verify domain. Please try again.';
      alert(`Error: ${errorMessage}`);
      // Remove from tracking if there was an error
      setVerifyingDomains(prev => {
        const newMap = new Map(prev);
        newMap.delete(domain);
        // Update localStorage (as object with timestamps)
        if (newMap.size > 0) {
          const domainsObj = Object.fromEntries(newMap);
          localStorage.setItem('verifyingDomains', JSON.stringify(domainsObj));
        } else {
          localStorage.removeItem('verifyingDomains');
        }
        return newMap;
      });
    } finally {
      setIsVerifying(false);
      setVerifyingDomain(null); // Clear the per-domain loading state
    }
  };

  // Handle user's choice for nameserver update
  const handleNameserverUpdateChoice = async (updateNameservers) => {
    if (!nameserverUpdateData) return;
    
    const { domain, accountId, canUpdateAutomatically } = nameserverUpdateData;
    
    if (!canUpdateAutomatically) {
      alert('⚠️ This domain is not registered with Route 53 Domains, so we cannot update nameservers automatically.\n\nPlease update the nameservers manually at your domain registrar to match the Route 53 hosted zone nameservers.');
      setShowNameserverUpdateModal(false);
      setNameserverUpdateData(null);
      return;
    }
    
    setShowNameserverUpdateModal(false);
    
    if (!updateNameservers) {
      // User chose not to update nameservers
      alert('⚠️ Nameservers were not updated. DNS records may not be visible to external receivers until nameservers are updated manually.\n\nYou can update nameservers later at your domain registrar.');
      setNameserverUpdateData(null);
      return;
    }
    
    // Set loading state
    setVerifyingDomain(domain);
    setIsVerifying(true);
    
    try {
      // Get auth token for the request
      const apiModule = await import('../services/api');
      await apiModule.waitForAuthFunctions();
      const tokenGetter = apiModule.getTokenGetter();
      const token = await tokenGetter?.() || '';
      
      // Re-call the API with updateNameservers flag
      const configureResponse = await fetch(`${getApiUrl()}/domains/${domain}/configure-dns`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          accountId: accountId || undefined,
          updateNameservers: true
        })
      });
      
      if (configureResponse.ok) {
        const responseData = await configureResponse.json();
        console.log('DNS configuration response after nameserver update:', responseData);
        
        // Add domain to verifyingDomains map (with timestamp)
        setVerifyingDomains(prev => {
          const newMap = new Map(prev);
          newMap.set(domain, Date.now());
          const domainsObj = Object.fromEntries(newMap);
          localStorage.setItem('verifyingDomains', JSON.stringify(domainsObj));
          return newMap;
        });
        
        // Show success message
        alert('✅ Nameservers updated successfully!\n\nDNS records are being created. Verification will complete in 5-60 minutes once DNS propagates.');
        
        // Refresh domain data
        loadDomains();
      } else {
        const errorData = await configureResponse.json().catch(() => ({ message: 'Unknown error' }));
        alert(`❌ Failed to update nameservers: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating nameservers:', error);
      alert(`❌ Error updating nameservers: ${error.message}`);
    } finally {
      setIsVerifying(false);
      setVerifyingDomain(null);
      setNameserverUpdateData(null);
    }
  };

  // Handle user's choice for existing DNS records
  const handleExistingRecordsChoice = async (choice) => {
    if (!existingRecordsData) return;
    
    const { domain, accountId } = existingRecordsData;
    setShowExistingRecordsModal(false);
    
    // Set loading state
    setVerifyingDomain(domain);
    setIsVerifying(true);
    
    try {
      // Get auth token for the request
      const apiModule = await import('../services/api');
      await apiModule.waitForAuthFunctions();
      const tokenGetter = apiModule.getTokenGetter();
      const token = await tokenGetter?.() || '';
      
      // Re-call the API with the user's choice
      const configureResponse = await fetch(`${getApiUrl()}/domains/${domain}/configure-dns`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          accountId: accountId || undefined,
          handleExistingRecords: choice // 'delete' or 'keep'
        })
      });
      
      if (configureResponse.ok) {
        const responseData = await configureResponse.json();
        console.log('DNS configuration response after handling existing records:', responseData);
        
        // Add domain to verifyingDomains map (with timestamp)
        setVerifyingDomains(prev => {
          const newMap = new Map(prev);
          newMap.set(domain, Date.now());
          const domainsObj = Object.fromEntries(newMap);
          localStorage.setItem('verifyingDomains', JSON.stringify(domainsObj));
          return newMap;
        });
        
        alert(`✅ DNS records ${choice === 'delete' ? 'created (existing records deleted)' : 'updated'}! SPF, DMARC, and provider DKIM have been written to Route 53. Changes may take a few minutes to propagate.`);
        
        // Refresh data
        loadDomains();
        setTimeout(() => window.location.reload(), 1000);
      } else {
        const errorData = await configureResponse.json();
        alert(`Error: ${errorData.message || 'Failed to configure DNS records'}`);
      }
    } catch (error) {
      console.error('Error handling existing records:', error);
      alert(`Error: ${error.message || 'Failed to configure DNS records'}`);
    } finally {
      setIsVerifying(false);
      setVerifyingDomain(null);
      setExistingRecordsData(null);
    }
  };

  // const handleVerifyDomain = (domainId) => {
  //   // TODO: Implement domain verification
  //   console.log('Verify domain:', domainId);
  // };

  const handleSetupDNS = (domain, hostProvider = 'aws') => {
    setSelectedDomain(domain);
    setShowDNSSetup(true);
    // Store host provider for DNS setup component
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('dnsSetupHostProvider', hostProvider);
    }
  };

  const handleCheckSandboxReadiness = async (domain) => {
    if (!domain) return;
    
    setSandboxVerificationDomain(domain);
    setShowSandboxVerificationModal(true);
    setSandboxVerificationResults(null);
    
    try {
      // Find the domain object to get its accountId
      const domainObj = allDomains.find(d => d.domain === domain);
      const accountId = domainObj?.accountId;
      const region = domainObj?.region || 'us-east-1';
      
      console.log(`🔍 Checking sandbox readiness for domain: ${domain}, accountId: ${accountId || 'default'}, region: ${region}`);
      
      // Get token using the same pattern as other handlers in this file
      const apiModule = await import('../services/api');
      const tokenGetter = apiModule.getTokenGetter();
      const token = tokenGetter ? await tokenGetter() : null;
      
      // CRITICAL FIX: Pass accountId and region as query parameters
      const queryParams = new URLSearchParams();
      if (accountId) {
        queryParams.append('accountId', accountId);
      }
      queryParams.append('region', region);
      
      const url = `${getApiUrl()}/email-accounts/sandbox-exit-readiness/${encodeURIComponent(domain)}?${queryParams.toString()}`;
      console.log(`🔍 API URL: ${url}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to check sandbox readiness: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('🔍 Sandbox readiness check response:', data);
      // Handle both direct response and wrapped response
      if (data.data) {
        setSandboxVerificationResults(data.data);
      } else {
        setSandboxVerificationResults(data);
      }
    } catch (error) {
      console.error('Error checking sandbox readiness:', error);
      setSandboxVerificationResults({
        error: error.message || 'Failed to check sandbox readiness'
      });
    }
  };

  const [updateDMARC, { isLoading: isUpdatingDMARC }] = useUpdateDMARCMutation();
  const [setupBounceComplaintHandling, { isLoading: isSettingUpBounceComplaint }] = useSetupBounceComplaintHandlingMutation();
  const [setupReceivingForDomain, { isLoading: isSettingUpReceivingForDomain }] = useSetupReceivingForDomainMutation();
  const [settingUpReceivingForDomainName, setSettingUpReceivingForDomainName] = useState(null);
  const [inboundProviderModalDomain, setInboundProviderModalDomain] = useState(null);
  const [inboundProviderSelection, setInboundProviderSelection] = useState('postmark'); // 'postmark' | 'ses'
  const [inboundProviderSesAllowed, setInboundProviderSesAllowed] = useState(false);
  const [inboundProviderApplyMx, setInboundProviderApplyMx] = useState(false); // Phase A default: no MX writes
  const [inboundProviderRecentResults, setInboundProviderRecentResults] = useState({});
  const normalizeDomainKey = (value) => String(value || '').trim().toLowerCase();

  const openInboundProviderModalForDomain = (domainObj) => {
    const domainName = domainObj?.domain;
    if (!domainName) return;
    const currentRaw = String(domainObj?.receivingProvider || 'postmark').trim().toLowerCase();
    const current = currentRaw === 'ses' ? 'ses' : 'postmark';
    // Gate SES client-side as a UX nicety (server still enforces).
    const accountId = String(domainObj?.accountId || '').trim();
    const sesAllowed =
      Boolean(domainObj?.isInRoute53) ||
      Boolean(domainObj?.hostedZoneId) ||
      (accountId && accountId !== 'default');
    setInboundProviderSesAllowed(sesAllowed);
    setInboundProviderSelection(sesAllowed ? current : 'postmark');
    setInboundProviderApplyMx(false);
    setInboundProviderModalDomain(domainObj);
  };

  const handleSetupReceivingForDomain = async (
    domainName,
    { isRepair = false, provider: providerOverride = null, applyMx: applyMxOverride = undefined } = {}
  ) => {
    if (!domainName) return;
    setSettingUpReceivingForDomainName(domainName); // Set before await to prevent double-submit
    try {
      const result = await setupReceivingForDomain({
        domain: domainName,
        provider: providerOverride || undefined,
        applyMx: typeof applyMxOverride === 'boolean' ? applyMxOverride : undefined,
      }).unwrap();
      const d = result?.data ?? {};
      const status = d.status;
      const addresses = d.addresses ?? d.emailAddressesUsed ?? 0;
      const resultDomainKey = normalizeDomainKey(d.domain || domainName);
      if (resultDomainKey) {
        setInboundProviderRecentResults((prev) => ({
          ...prev,
          [resultDomainKey]: {
            ...d,
            // Normalize for case-insensitive row/modal matching.
            domain: resultDomainKey,
          },
        }));
      }

      const providerRaw = String(d.inboundProvider || providerOverride || 'postmark').trim().toLowerCase();
      const provider = providerRaw === 'ses' ? 'ses' : 'postmark';
      const providerLabel = provider === 'ses' ? 'SES' : 'Postmark';
      const expectedMx =
        d.mxValueExpected ||
        (provider === 'ses' ? 'inbound-smtp.us-east-1.amazonaws.com.' : 'inbound.postmarkapp.com.');

      if (status === 'failed') {
        const reason = d.reason || 'Setup failed';
        const isDnsError = /DNS update failed|inbound mail will NOT flow|hosted zone|MX record/i.test(reason);
        if (isRepair) console.info('[kg:inbound-repair]', { domain: domainName, outcome: 'fail', reason });
        toast.error(isDnsError ? reason : `Setup receiving failed for ${domainName}: ${reason}`);
        return;
      }

      const mxUpdated = d.mxUpdated === true || d.mxRecords?.[domainName] === true;
      const mxValueObserved = d.mxValueObserved ?? null;
      const mxSuffix = mxValueObserved ? ` (Route53: ${mxValueObserved})` : '';
      const mxMatchesExpectedPublic = d.mxMatchesExpectedPublic === true;
      const expectedRecord =
        d.mxRecordExpected || (d.mxValueExpected ? `10 ${d.mxValueExpected}` : `10 ${expectedMx}`);

      if (mxMatchesExpectedPublic) {
        toast.success(`Inbound active for ${domainName} (${providerLabel} MX verified).`);
      } else if (mxUpdated) {
        toast.info(`MX applied in Route53 for ${domainName}${mxSuffix}. DNS is propagating (expected: ${expectedRecord}).`);
      } else {
        toast.info(`Inbound saved as ${providerLabel} for ${domainName}. Pending DNS (expected: ${expectedRecord}).`);
      }

      // Refresh list so the selected provider is visible immediately.
      loadDomains();
    } catch (error) {
      const d = error?.data;
      const reason = d?.reason ?? error?.data?.message ?? error?.message ?? 'Setup failed';
      toast.error(`Setup receiving failed for ${domainName}: ${reason}`);
    } finally {
      setSettingUpReceivingForDomainName(null);
    }
  };

  const handleSetupBounceComplaint = async (domain) => {
    if (!domain) return;
    
    // Find the domain object to get its accountId
    const domainObj = allDomains.find(d => d.domain === domain);
    const accountId = domainObj?.accountId;
    const region = domainObj?.region || selectedAccount?.region || 'us-east-1';
    
    // Get user email from context or prompt
    const userEmail = window.prompt(
      `Enter the email address where you want to receive bounce and complaint notifications for ${domain}:`,
      `admin@${domain}`
    );
    
    if (!userEmail) return; // User cancelled
    
    try {
      await setupBounceComplaintHandling({
        emailAddress: userEmail,
        accountId: accountId,
        region: region
      }).unwrap();
      
      alert(`Bounce/complaint handling setup initiated for ${domain}. Please check your email (${userEmail}) to confirm the SNS subscriptions.`);
      
      // Refresh sandbox check after setup
      setTimeout(() => {
        handleCheckSandboxReadiness(domain);
      }, 2000);
    } catch (error) {
      console.error('Error setting up bounce/complaint handling:', error);
      alert(`Failed to setup bounce/complaint handling: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleEditDMARC = (domain) => {
    setDmarcEditingDomain(domain);
    // Extract current DMARC email from domain data if available
    // Default to dmarc@domain.com
    setDmarcEmail(`dmarc@${domain}`);
    setDmarcPolicy('quarantine'); // Default policy
    setShowDMARCModal(true);
  };

  const handleSaveDMARC = async () => {
    if (!dmarcEditingDomain || !dmarcEmail) {
      alert('Please enter a DMARC email address');
      return;
    }

    try {
      const domainObj = allDomains.find(d => d.domain === dmarcEditingDomain);
      const accountId = domainObj?.accountId;

      const result = await updateDMARC({
        domain: dmarcEditingDomain,
        dmarcEmail,
        dmarcPolicy,
        accountId,
        region: 'us-east-1'
      }).unwrap();

      if (result?.data?.autoApplied === false) {
        alert(
          `⚠️ DMARC DNS auto-apply could not run for ${dmarcEditingDomain}.\n\n` +
          `The record appears to be configured already, or this domain is not managed by our Route 53 account.\n` +
          `Please apply the DNS record manually if needed.`
        );
      } else {
        alert(
          `✅ DMARC settings updated successfully for ${dmarcEditingDomain}!\n\n` +
          `Policy: ${dmarcPolicy}\nReports to: ${dmarcEmail}\n\n` +
          `DNS changes may take a few minutes to propagate.`
        );
      }
      setShowDMARCModal(false);
      setDmarcEditingDomain(null);
      setDmarcEmail('');
      setDmarcPolicy('quarantine');
      
      // Refresh domains to show updated status
      await loadDomains();
    } catch (error) {
      console.error('Error updating DMARC:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to update DMARC settings';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleCloseDNSSetup = () => {
    setShowDNSSetup(false);
    setSelectedDomain('');
  };

  const handleBulkSetup = async (group) => {
    if (!group || !group.domains || group.domains.length === 0) return;
    
    // Check if bulk setup is already in progress
    if (bulkSettingUpGroup === group.mainDomain) {
      alert(`Bulk setup is already in progress for ${group.mainDomain}. Please wait for it to complete.`);
      return;
    }
    
    // Check domain statuses before starting
    const domainsToProcess = [];
    const alreadySetup = [];
    
    group.domains.forEach(domain => {
      const isVerified = domain.status === 'verified' || domain.verificationStatus === 'verified';
      const hasDKIM = domain.dkimStatus === 'verified' || domain.dkimStatus === 'success';
      const hasSPF = domain.spfStatus === 'verified' || domain.spfStatus === 'success';
      const hasDMARC = domain.dmarcStatus === 'verified' || domain.dmarcStatus === 'success';
      
      // Check if domain is fully set up
      if (isVerified && hasDKIM && hasSPF && hasDMARC) {
        alreadySetup.push({
          domain: domain.domain,
          reason: 'Fully configured (DKIM, SPF, DMARC all verified)'
        });
      } else {
        domainsToProcess.push(domain);
      }
    });
    
    // If all domains are already set up
    if (domainsToProcess.length === 0 && alreadySetup.length > 0) {
      alert(`✅ All domains in ${group.mainDomain} are already fully set up!\n\nAll ${alreadySetup.length} domain(s) have:\n- DKIM enabled and verified\n- SPF configured and verified\n- DMARC configured and verified`);
      return;
    }
    
    // Show confirmation with status summary
    let confirmMessage = `Bulk Setup for ${group.mainDomain}?\n\n`;
    if (alreadySetup.length > 0) {
      confirmMessage += `ℹ️  ${alreadySetup.length} domain(s) already fully set up (will be skipped)\n`;
    }
    if (domainsToProcess.length > 0) {
      confirmMessage += `⚙️  ${domainsToProcess.length} domain(s) need setup:\n`;
      confirmMessage += `  - Enable DKIM\n`;
      confirmMessage += `  - Setup DNS records\n`;
      confirmMessage += `  - Verify domains\n`;
    }
    confirmMessage += `\nThis may take a few minutes. Continue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    // If no domains need processing, just show message
    if (domainsToProcess.length === 0) {
      return;
    }
    
    setBulkSettingUpGroup(group.mainDomain);
    setBulkSetupProgress({ current: 0, total: domainsToProcess.length, domain: '' });
    
    const results = {
      success: [],
      failed: [],
      manualSetup: [],
      skipped: alreadySetup,
      alreadyConfigured: []
    };
    
    try {
      // Process each domain sequentially to avoid overwhelming the API
      for (let i = 0; i < domainsToProcess.length; i++) {
        const domain = domainsToProcess[i];
        setBulkSetupProgress({ 
          current: i + 1, 
          total: domainsToProcess.length, 
          domain: domain.domain 
        });
        
        try {
          // Check current status again before processing
          const isVerified = domain.status === 'verified' || domain.verificationStatus === 'verified';
          const hasDKIM = domain.dkimStatus === 'verified' || domain.dkimStatus === 'success';
          
          // Step 1: Enable DKIM (only if not already enabled)
          if (!hasDKIM) {
            try {
              const dkimResult = await enableDKIM(domain.domain).unwrap();
              
              if (dkimResult.data?.manualSetupRequired) {
                results.manualSetup.push({
                  domain: domain.domain,
                  reason: 'DKIM DNS records need manual setup',
                  records: dkimResult.data.dnsRecords || []
                });
              }
            } catch (dkimError) {
              console.error(`Error enabling DKIM for ${domain.domain}:`, dkimError);
              const errorMessage = dkimError.data?.message || dkimError.message || 'Unknown error';
              
              // Check if error is because DKIM is already enabled
              if (errorMessage.toLowerCase().includes('already') || 
                  errorMessage.toLowerCase().includes('exists') ||
                  errorMessage.toLowerCase().includes('enabled')) {
                results.alreadyConfigured.push({
                  domain: domain.domain,
                  operation: 'DKIM',
                  message: 'DKIM already enabled'
                });
              } else {
                results.failed.push({
                  domain: domain.domain,
                  operation: 'Enable DKIM',
                  error: errorMessage
                });
                continue; // Skip to next domain if DKIM fails
              }
            }
          } else {
            results.alreadyConfigured.push({
              domain: domain.domain,
              operation: 'DKIM',
              message: 'DKIM already verified'
            });
          }
          
          // Step 2: Setup DNS records (verify) - only if not already verified
          if (!isVerified) {
            try {
              const verifyResponse = await fetch(`${getApiUrl()}/domains/${domain.domain}/configure-dns`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
              });
              
              if (verifyResponse.ok) {
                results.success.push({
                  domain: domain.domain,
                  operations: ['DKIM Enabled', 'DNS Setup']
                });
              } else {
                const errorData = await verifyResponse.json().catch(() => ({}));
                const errorMessage = errorData.message || 'DNS setup failed';
                
                // Check if already configured
                if (errorMessage.toLowerCase().includes('already') || 
                    errorMessage.toLowerCase().includes('exists') ||
                    errorMessage.toLowerCase().includes('configured')) {
                  results.alreadyConfigured.push({
                    domain: domain.domain,
                    operation: 'DNS Setup',
                    message: errorMessage
                  });
                } else {
                  results.failed.push({
                    domain: domain.domain,
                    operation: 'DNS Setup',
                    error: errorMessage
                  });
                }
              }
            } catch (verifyError) {
              console.error(`Error setting up DNS for ${domain.domain}:`, verifyError);
              results.failed.push({
                domain: domain.domain,
                operation: 'DNS Setup',
                error: verifyError.message || 'Unknown error'
              });
            }
          } else {
            results.alreadyConfigured.push({
              domain: domain.domain,
              operation: 'DNS Verification',
              message: 'Domain already verified'
            });
          }
          
          // Small delay between domains to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 500));
          
        } catch (error) {
          console.error(`Error processing ${domain.domain}:`, error);
          results.failed.push({
            domain: domain.domain,
            operation: 'Bulk Setup',
            error: error.message || 'Unknown error'
          });
        }
      }
      
      // Refresh domain list
      await loadDomains();
      
      // Show results summary
      let resultMessage = `Bulk Setup Complete for ${group.mainDomain}!\n\n`;
      
      if (results.success.length > 0) {
        resultMessage += `✅ Successfully processed ${results.success.length} domain(s):\n`;
        results.success.forEach(r => {
          resultMessage += `  - ${r.domain}\n`;
        });
        resultMessage += '\n';
      }
      
      if (results.alreadyConfigured.length > 0) {
        resultMessage += `ℹ️  Already configured (${results.alreadyConfigured.length} domain(s)):\n`;
        results.alreadyConfigured.forEach(r => {
          resultMessage += `  - ${r.domain}: ${r.message}\n`;
        });
        resultMessage += '\n';
      }
      
      if (results.skipped.length > 0) {
        resultMessage += `⏭️  Skipped (already fully set up) - ${results.skipped.length} domain(s):\n`;
        results.skipped.forEach(r => {
          resultMessage += `  - ${r.domain}\n`;
        });
        resultMessage += '\n';
      }
      
      if (results.manualSetup.length > 0) {
        resultMessage += `⚠️  Manual setup required for ${results.manualSetup.length} domain(s):\n`;
        results.manualSetup.forEach(r => {
          resultMessage += `  - ${r.domain}: ${r.reason}\n`;
        });
        resultMessage += '\n';
      }
      
      if (results.failed.length > 0) {
        resultMessage += `❌ Failed for ${results.failed.length} domain(s):\n`;
        results.failed.forEach(r => {
          resultMessage += `  - ${r.domain} (${r.operation}): ${r.error}\n`;
        });
      }
      
      alert(resultMessage);
      
    } catch (error) {
      console.error('Error in bulk setup:', error);
      alert(`Error during bulk setup: ${error.message || 'Unknown error'}`);
    } finally {
      setBulkSettingUpGroup(null);
      setBulkSetupProgress({ current: 0, total: 0, domain: '' });
    }
  };

  const handleDeleteDomain = (domain) => {
    setDomainToDelete(domain);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!domainToDelete) return;

    try {
      await deleteDomain(domainToDelete).unwrap();
      // console.log('Domain deleted from app successfully:', domainToDelete);
      
      // Close modal and refresh
      setShowDeleteModal(false);
      setDomainToDelete('');
      loadDomains();
    } catch (error) {
      console.error('Error deleting domain from app:', error);
      // TODO: Show error message to user
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDomainToDelete('');
  };

  const handleOpenHardDelete = () => {
    if (!domainToDelete) return;
    setHardDeleteConfirmText('');
    setShowDeleteModal(false);
    setShowHardDeleteModal(true);
  };

  const handleCancelHardDelete = () => {
    setShowHardDeleteModal(false);
    setHardDeleteConfirmText('');
    // keep domainToDelete as context so user can re-open if needed
  };

  const handleConfirmHardDelete = async () => {
    if (!domainToDelete) return;
    const expected = `DELETE ${domainToDelete} PERMANENTLY`;
    if (hardDeleteConfirmText.trim() !== expected) return;

    try {
      await hardDeleteDomain({ domain: domainToDelete, confirmation: expected }).unwrap();
      setShowHardDeleteModal(false);
      setDomainToDelete('');
      loadDomains();
      alert(`✅ Hard delete complete for ${domainToDelete}`);
    } catch (error) {
      console.error('Error hard deleting domain:', error);
      const msg = error?.data?.message || error?.data?.error || error?.message || 'Hard delete failed';
      alert(`❌ ${msg}`);
    }
  };

  if (loading) {
    return (
      <Container className="py-4" style={{ position: 'relative', minHeight: '400px' }}>
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}
        >
          <div className="text-center">
            <ArrowClockwise size={32} className="text-primary mb-2" style={{ animation: 'spin 1s linear infinite' }} />
            <div className="text-muted">Loading domains...</div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <>
      <EMMSectionHeader
        title="Domains"
        showHelp
        onHelpClick={() => setShowHelpSidebar(true)}
      />
      <Container className="py-3">
      <div id="domains-build" data-build="domains-fix-20260117-2" style={{ display: 'none' }} />
      {/* Action bar - same spot and size as Clients "+ Client" button */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <Button
          variant="primary"
          size="sm"
          className="d-flex align-items-center"
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          onClick={handleAddDomain}
        >
          <Plus className="me-1" size={14} />
          Domain
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          className="d-flex align-items-center"
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          disabled={allTenantDomainNames.length === 0}
          onClick={() => setShowAllTenantDomainsModal(true)}
          title="List every domain across all connected AWS accounts"
        >
          <Clipboard className="me-1" size={14} />
          All domain names
        </Button>
        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
          {totalDomainCount} domain{totalDomainCount !== 1 ? 's' : ''}
        </span>
      </div>
      <Row className="mb-4">
        <Col>
          <p className="text-muted mb-0">
            {isAllAccountsSelected 
              ? 'View your email domains and DNS settings. DNS is read-only; inbound setup is safe and idempotent.' 
              : `Viewing domains for ${selectedAccount.name}. DNS is read-only; inbound setup is safe and idempotent.`
            }
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error.message || 'Failed to load domains. Please check your backend connection.'}
        </Alert>
      )}


      <Alert variant="info" className="mb-4">
        <strong>🛡️ DNS is read-only</strong><br/>
        This page displays your existing email domains. DNS records are read-only. Inbound setup actions (e.g. &quot;Setup receiving&quot;) are safe and idempotent.
      </Alert>

      {/* Search and Filter Controls */}
      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Body>
              <Row className="align-items-center">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Search />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search domains..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="verified">Verified Only</option>
                    <option value="pending">Pending Only</option>
                  </Form.Select>
                </Col>
                <Col md={3} className="text-end">
                  <div className="d-flex gap-2">
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={clearSelection}
                      disabled={selectedDomains.size === 0}
                    >
                      Clear Selection ({selectedDomains.size})
                    </Button>
                    {showBulkActions && (
                      <Dropdown>
                        <Dropdown.Toggle variant="primary" size="sm">
                          Bulk Actions ({selectedDomains.size})
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item>Enable DKIM for Selected</Dropdown.Item>
                          <Dropdown.Item>Setup DNS for Selected</Dropdown.Item>
                          <Dropdown.Item>Verify Selected</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  Domain Management ({filteredGroups.length} groups, {allDomains.length} total domains)
                </h5>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={loadDomains} disabled={loading}>
                    <ArrowClockwise className="me-1" />
                    {loading ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '30px', fontSize: '0.85rem' }}></th>
                    <th style={{ fontSize: '0.85rem' }}>Domain Group</th>
                    <th style={{ fontSize: '0.85rem', ...tableColumnStyles.status }}>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            <div>Connected: domain is active in Kross Global.</div>
                            <div>Email Auth: DKIM/SPF/DMARC DNS status (shown separately).</div>
                          </Tooltip>
                        }
                      >
                        <span style={{ cursor: 'help' }}>Status</span>
                      </OverlayTrigger>
                    </th>
                    <th style={{ fontSize: '0.85rem', ...tableColumnStyles.dns }}>DNS Records</th>
                    <th style={{ fontSize: '0.85rem', ...tableColumnStyles.accounts }}>Email Accounts</th>
                    <th style={{ fontSize: '0.85rem', ...tableColumnStyles.lastUsed }}>
                      <OverlayTrigger placement="top" overlay={<Tooltip>Last time a send from this domain was accepted by the relay (not delivery confirmation).</Tooltip>}>
                        <span style={{ cursor: 'help' }}>Last Used</span>
                      </OverlayTrigger>
                    </th>
                    <th style={{ fontSize: '0.85rem', ...tableColumnStyles.actions }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups.map((group) => (
                    <React.Fragment key={group.mainDomain}>
                      {/* Main Domain Group Header */}
                      <tr className="table-light domain-group-header" style={{ cursor: 'pointer' }}>
                        <td>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() => toggleGroupExpansion(group.mainDomain)}
                          >
                            {expandedGroups.has(group.mainDomain) ? 
                              <ChevronDown size={16} /> : 
                              <ChevronRight size={16} />
                            }
                          </Button>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 me-2"
                              onClick={() => selectAllInGroup(group)}
                              disabled={group.totalDomains === 0}
                            >
                              {group.totalDomains > 0 && group.domains.every(d => selectedDomains.has(d.id)) ? 
                                <CheckSquare size={16} /> : 
                                <Square size={16} />
                              }
                            </Button>
                            <div>
                              <strong style={{ fontSize: '0.85rem' }}>
                                {isAllAccountsSelected ? (group.accountAlias || group.accountName || `Account ${group.accountId}`) : group.mainDomain}
                              </strong>
                              <div className="text-muted small">
                                {group.totalDomains} domains • {group.verifiedCount} verified
                                {group.pendingCount > 0 && ` • ${group.pendingCount} pending`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={tableColumnStyles.status}>
                          <div className="d-flex gap-1">
                            {group.totalDomains === 0 ? (
                              <Badge bg="secondary">No domains</Badge>
                            ) : (
                              <>
                                {group.verifiedCount > 0 && <Badge bg="success">{group.verifiedCount} verified</Badge>}
                                {group.pendingCount > 0 && <Badge bg="warning">{group.pendingCount} pending</Badge>}
                              </>
                            )}
                          </div>
                        </td>
                        <td style={tableColumnStyles.dns}>
                          {group.totalDomains === 0 ? (
                            <Badge bg="secondary">No DNS</Badge>
                          ) : (
                            <Badge bg="info">Mixed</Badge>
                          )}
                        </td>
                        <td style={tableColumnStyles.accounts}>
                          <Badge bg={group.totalDomains === 0 ? "secondary" : "info"}>
                            {group.domains.reduce((sum, d) => sum + (d.emailCount || 0), 0)} accounts
                          </Badge>
                        </td>
                        <td style={tableColumnStyles.lastUsed}>
                          <span className="text-muted">Group</span>
                        </td>
                        <td style={tableColumnStyles.actions}>
                          <div className="d-flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline-primary"
                              onClick={() => handleBulkSetup(group)}
                              disabled={group.totalDomains === 0 || bulkSettingUpGroup === group.mainDomain || isEnablingDKIM || isVerifying}
                              title={group.totalDomains === 0 ? "No domains to setup" : `Setup all ${group.domains.length} domain(s) in this group`}
                            >
                              {bulkSettingUpGroup === group.mainDomain ? (
                                <>
                                  <Spinner size="sm" className="me-1" />
                                  <span>Setting up...</span>
                                  {bulkSetupProgress.total > 0 && (
                                    <span className="ms-1">
                                      ({bulkSetupProgress.current}/{bulkSetupProgress.total})
                                    </span>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Gear className="me-1" />Bulk Setup
                                </>
                              )}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline-danger" 
                              onClick={() => handleDeleteDomain(group.mainDomain)}
                              disabled={group.totalDomains === 0}
                              title={group.totalDomains === 0 ? "No domains to delete" : "Delete domain group"}
                            >
                              <XCircle className="me-1" />Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Subdomains (collapsible) */}
                      <Collapse in={expandedGroups.has(group.mainDomain)}>
                        <tr>
                          <td colSpan="7" className="p-0">
                            {group.totalDomains === 0 ? (
                              <div className="p-3 text-center text-muted">
                                <InfoCircle className="me-2" />
                                No domains configured for this account yet.
                              </div>
                            ) : (
                              <Table responsive className="mb-0">
                                <tbody>
                                  {(searchTerm ? group.domains.filter(d => d.domain.toLowerCase().includes(searchTerm.toLowerCase())) : group.domains).map((domain) => (
                                  <tr key={`${String(domain.accountId || 'default')}:${String(domain.id || domain.domain)}`} className={`domain-row ${domain.isMainDomain ? 'main-domain' : 'subdomain'}`}>
                                    <td style={{ width: '30px' }}>
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={() => toggleDomainSelection(domain.id)}
                                      >
                                        {selectedDomains.has(domain.id) ? 
                                          <CheckSquare size={16} /> : 
                                          <Square size={16} />
                                        }
                                      </Button>
                                    </td>
                                    <td>
                                      <div className={domain.isMainDomain ? 'fw-bold' : 'ms-3'} style={{ fontSize: '0.85rem' }}>
                                        {domain.isMainDomain ? '📌 ' : '└─ '}{domain.domain}
                                        {domain.isMainDomain && (
                                          <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>This is the main domain for this account group</Tooltip>}
                                          >
                                            <Badge bg="primary" className="ms-2">Main</Badge>
                                          </OverlayTrigger>
                                        )}
                                      </div>
                                      {domain.createdAt && (
                                        <div className="text-muted small">
                                          Added: {toLocaleDateStringInTimezone(domain.createdAt, undefined, undefined, tenantTimezone)}
                                        </div>
                                      )}
                                      <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                        <div>
                                          <strong>Hosted:</strong> {formatProviderName(domain.hostProvider)}
                                        </div>
                                        <div>
                                          <strong>MX Records:</strong> {formatProviderName(domain.receivingProvider || 'postmark')}
                                        </div>
                                      </div>
                                    </td>
                                    <td style={tableColumnStyles.status}>{getStatusBadge(getEffectiveStatus(domain))}</td>
                                    <td style={tableColumnStyles.dns}>
                                      {(() => {
                                        const auth = getEffectiveAuthStatuses(domain);
                                        const dkimOverride = auth.isSmtpCom && !isVerifiedAuthStatus(domain.dkimStatus);
                                        const healthEntry = domainHealthMap.get(String(domain.domain || '').trim().toLowerCase());
                                        const sesVerified = healthEntry?.details?.sesIdentityVerified;
                                        const sesStatus = String(healthEntry?.details?.sesIdentityStatus || '').trim();
                                        const sesDetail = String(healthEntry?.details?.sesIdentityDetail || '').trim();
                                        let sesBadge = null;
                                        if (healthEntry) {
                                          if (sesVerified === true) {
                                            sesBadge = (
                                              <OverlayTrigger placement="top" overlay={<Tooltip>{sesDetail || `SES identity verified in root account.`}</Tooltip>}>
                                                <Badge bg="success" style={{ fontSize: '0.65rem', padding: '0.18rem 0.4rem' }}>
                                                  <CheckCircle size={9} className="me-1" /> Verified
                                                </Badge>
                                              </OverlayTrigger>
                                            );
                                          } else if (sesVerified === false) {
                                            sesBadge = (
                                              <OverlayTrigger placement="top" overlay={<Tooltip>{sesDetail || `SES identity not verified (status: ${sesStatus || 'unknown'}). Inbound mail will bounce.`}</Tooltip>}>
                                                <Badge bg="danger" style={{ fontSize: '0.65rem', padding: '0.18rem 0.4rem' }}>
                                                  <XCircle size={9} className="me-1" /> Not verified
                                                </Badge>
                                              </OverlayTrigger>
                                            );
                                          } else {
                                            sesBadge = (
                                              <OverlayTrigger placement="top" overlay={<Tooltip>{sesDetail || 'SES identity status unknown.'}</Tooltip>}>
                                                <Badge bg="secondary" style={{ fontSize: '0.65rem', padding: '0.18rem 0.4rem' }}>Unknown</Badge>
                                              </OverlayTrigger>
                                            );
                                          }
                                        }
                                        return (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                                            <div className="small" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
                                              <span style={{ display: 'inline-block' }}>DKIM:</span>
                                              <span style={{ display: 'inline-flex' }}>
                                                {getDnsStatusBadge(auth.dkimStatus, 'DKIM', dkimOverride
                                                  ? { providerLabel: auth.authProviderLabel || '', note: 'Based on active SMTP.com outbound routing.' }
                                                  : auth.authProviderLabel ? { providerLabel: auth.authProviderLabel } : undefined)}
                                              </span>
                                              {auth.authProviderLabel && (
                                                <span className="text-muted" style={{ fontSize: '0.65rem' }}>
                                                  {auth.authProviderLabel}
                                                </span>
                                              )}
                                            </div>
                                            <div className="small" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
                                              <span style={{ display: 'inline-block' }}>SPF:</span>
                                              <span style={{ display: 'inline-flex' }}>{getDnsStatusBadge(auth.spfStatus, 'SPF')}</span>
                                            </div>
                                            <div className="small" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
                                              <span style={{ display: 'inline-block' }}>DMARC:</span>
                                              <span style={{ display: 'inline-flex' }}>{getDnsStatusBadge(auth.dmarcStatus, 'DMARC')}</span>
                                            </div>
                                            {sesBadge && (
                                              <div className="small" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
                                                <span style={{ display: 'inline-block' }}>SES Inbound:</span>
                                                <span style={{ display: 'inline-flex' }}>{sesBadge}</span>
                                              </div>
                                            )}
                                            {/*
                                              Per-row Domain Health badge — same source-of-truth as the nav icon and
                                              the IdentityHealthMonitoring page. Tooltip lists every active alert
                                              message so the user always knows *why* a domain is critical/warning,
                                              not just that it is. Clicking deep-links into the monitoring page.
                                            */}
                                            {healthEntry && (() => {
                                              const status = String(healthEntry.status || '').toLowerCase();
                                              const alerts = Array.isArray(healthEntry.alerts) ? healthEntry.alerts : [];
                                              const severityRank = { critical: 0, warning: 1, info: 2 };
                                              const sortedAlerts = [...alerts].sort(
                                                (a, b) => (severityRank[a.type] ?? 99) - (severityRank[b.type] ?? 99)
                                              );
                                              const hardBounces = Number(healthEntry?.metrics?.hardBounceCount || 0);
                                              const softBounces = Number(healthEntry?.metrics?.softBounceCount || 0);
                                              const sentVolume = Number(healthEntry?.metrics?.volume || 0);
                                              const variant = status === 'critical' ? 'danger'
                                                : status === 'warning' ? 'warning'
                                                : status === 'healthy' ? 'success'
                                                : 'secondary';
                                              const Icon = status === 'critical' ? XCircle
                                                : status === 'warning' ? ExclamationTriangle
                                                : status === 'healthy' ? CheckCircle
                                                : null;
                                              const labelText = status === 'critical' ? 'Critical'
                                                : status === 'warning' ? 'Warning'
                                                : status === 'healthy' ? 'Healthy'
                                                : 'Unknown';
                                              const tooltipBody = (
                                                <Tooltip id={`health-${domain.id || domain.domain}`} style={{ maxWidth: 360 }}>
                                                  <div style={{ textAlign: 'left' }}>
                                                    <div className="mb-1">
                                                      <strong>Health score:</strong> {Number(healthEntry.healthScore || 0)}%
                                                      {sentVolume > 0 && (
                                                        <span className="ms-2 text-muted" style={{ fontSize: '0.7rem' }}>
                                                          {sentVolume} sent / {hardBounces} hard / {softBounces} soft (24h)
                                                        </span>
                                                      )}
                                                    </div>
                                                    {sortedAlerts.length === 0 ? (
                                                      <div style={{ fontSize: '0.78rem' }}>No active alerts.</div>
                                                    ) : (
                                                      <div style={{ fontSize: '0.78rem' }}>
                                                        {sortedAlerts.slice(0, 5).map((a, idx) => (
                                                          <div key={idx} className="mb-1">
                                                            <strong>[{a.type}]</strong> {a.message}
                                                          </div>
                                                        ))}
                                                        {sortedAlerts.length > 5 && (
                                                          <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                            +{sortedAlerts.length - 5} more — open Domain Health Monitor
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                    <div className="mt-1 text-muted" style={{ fontSize: '0.7rem' }}>
                                                      Click to open Domain Health Monitor
                                                    </div>
                                                  </div>
                                                </Tooltip>
                                              );
                                              return (
                                                <div className="small" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
                                                  <span style={{ display: 'inline-block' }}>Health:</span>
                                                  <span style={{ display: 'inline-flex' }}>
                                                    <OverlayTrigger placement="top" overlay={tooltipBody}>
                                                      <Link
                                                        to="/health-monitoring"
                                                        style={{ textDecoration: 'none' }}
                                                        aria-label={`Open Domain Health Monitor for ${domain.domain}`}
                                                      >
                                                        <Badge bg={variant} style={{ fontSize: '0.65rem', padding: '0.18rem 0.4rem', cursor: 'pointer' }}>
                                                          {Icon && <Icon size={9} className="me-1" />}
                                                          {labelText}
                                                        </Badge>
                                                      </Link>
                                                    </OverlayTrigger>
                                                  </span>
                                                </div>
                                              );
                                            })()}
                                          </div>
                                        );
                                      })()}
                                    </td>
                                    <td style={tableColumnStyles.accounts}>
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={
                                          <Tooltip>
                                            {domain.emailCount === 0 ? 'No mailboxes provisioned yet.' : `${domain.emailCount} email account${domain.emailCount !== 1 ? 's' : ''} configured for this domain`}
                                          </Tooltip>
                                        }
                                      >
                                        <Badge bg="info">{domain.emailCount} accounts</Badge>
                                      </OverlayTrigger>
                                    </td>
                                    <td style={tableColumnStyles.lastUsed}>
                                      <OverlayTrigger placement="top" overlay={<Tooltip>Last time a send from this domain was accepted by the relay (not delivery confirmation). Shown in tenant timezone when configured.</Tooltip>}>
                                        <span style={{ cursor: 'help' }}>
                                          {domain.lastUsed
                                            ? toLocaleDateStringInTimezone(domain.lastUsed, undefined, undefined, tenantTimezone)
                                            : 'Never'}
                                        </span>
                                      </OverlayTrigger>
                                    </td>
                                    <td style={tableColumnStyles.actions}>
                                      <div className="d-flex flex-column gap-1 mb-1">
                                        {(() => {
                                          const authenticationNeedsAttention = domain.authenticationNeedsAttention === true;
                                          const authenticationConfigured = isAuthenticationConfigured(domain);
                                          const authenticationInProgress = isAuthenticationInProgress(domain);
                                          const openAuth = (e) => {
                                            e?.preventDefault?.();
                                            e?.stopPropagation?.();
                                            openEmailAuthWizard(domain);
                                          };
                                          if (authenticationNeedsAttention) {
                                            return (
                                              <OverlayTrigger placement="top" overlay={<Tooltip>Authentication records need attention. Click to fix.</Tooltip>}>
                                                <span className="d-inline-block receiving-badge-clickable" style={{ cursor: 'pointer' }} role="button" tabIndex={0} aria-label="Open authentication settings" onClick={openAuth} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEmailAuthWizard(domain); } }}>
                                                  <Badge bg="warning" text="dark" className="text-nowrap" style={{ fontSize: '0.7rem' }}>⚠️ Authentication needs attention</Badge>
                                                </span>
                                              </OverlayTrigger>
                                            );
                                          }
                                          if (authenticationConfigured) {
                                            return (
                                              <OverlayTrigger placement="top" overlay={<Tooltip>Authentication is configured (SPF, DKIM, DMARC). Click to view or update.</Tooltip>}>
                                                <span className="d-inline-block receiving-badge-clickable" style={{ cursor: 'pointer' }} role="button" tabIndex={0} aria-label="Open authentication settings" onClick={openAuth} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEmailAuthWizard(domain); } }}>
                                                  <Badge bg="success" className="text-nowrap" style={{ fontSize: '0.7rem' }}>✅ Authentication set</Badge>
                                                </span>
                                              </OverlayTrigger>
                                            );
                                          }
                                          return (
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={
                                                <Tooltip>
                                                  {authenticationInProgress
                                                    ? 'Authentication setup is in progress. Click to continue or manage settings.'
                                                    : 'Configure SPF, DKIM, and DMARC for this domain.'}
                                                </Tooltip>
                                              }
                                            >
                                              <span className="d-inline-block">
                                                <Button
                                                  size="sm"
                                                  variant={authenticationInProgress ? 'outline-primary' : 'outline-secondary'}
                                                  onClick={openAuth}
                                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                  aria-label={authenticationInProgress ? 'Continue authentication setup for this domain' : 'Set up authentication for this domain'}
                                                >
                                                  {authenticationInProgress ? 'Continue setup' : 'Set up authentication'}
                                                </Button>
                                              </span>
                                            </OverlayTrigger>
                                          );
                                        })()}
                                          {(() => {
                                            // Policy vs health:
                                            // - receivingProvider = selected inbound provider (Postmark/SES)
                                            // - mxMatchesExpectedPublic / receivingNeedsAttention = actual public DNS state (backend health)
                                            const domainKey = normalizeDomainKey(domain.id || domain.domain);
                                            const recentInboundResult = inboundProviderRecentResults[domainKey] || null;
                                            const hasRecentMxState = typeof recentInboundResult?.mxMatchesExpectedPublic === 'boolean';
                                            const effectiveReceivingProvider =
                                              recentInboundResult?.inboundProvider || domain.receivingProvider || 'postmark';
                                            const inboundConfigured =
                                              effectiveReceivingProvider != null && String(effectiveReceivingProvider).trim() !== '';
                                            const mxMatchesExpectedPublic = hasRecentMxState
                                              ? recentInboundResult.mxMatchesExpectedPublic === true
                                              : domain.mxMatchesExpectedPublic === true;
                                            const mxKnown = hasRecentMxState || typeof domain.mxMatchesExpectedPublic === 'boolean';
                                            const receivingNeedsAttention =
                                              hasRecentMxState
                                                ? !mxMatchesExpectedPublic
                                                : typeof domain.receivingNeedsAttention === 'boolean'
                                                  ? domain.receivingNeedsAttention
                                                  : inboundConfigured
                                                    ? (mxKnown ? !mxMatchesExpectedPublic : true)
                                                    : false;
                                            const inboundSet = inboundConfigured && mxMatchesExpectedPublic;
                                            const canSetup = settingUpReceivingForDomainName !== domain.domain;
                                            if (receivingNeedsAttention) {
                                              const providerLabel = formatProviderName(effectiveReceivingProvider || 'postmark');
                                              const expectedRecord =
                                                recentInboundResult?.mxRecordExpected ||
                                                domain.mxRecordExpected ||
                                                (domain.mxValueExpected ? `10 ${domain.mxValueExpected}` : null);
                                              return (
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={
                                                    <Tooltip>
                                                      {`Inbound provider: ${providerLabel}. Public MX does not match yet.`}
                                                      {expectedRecord ? <><br />Expected: <code>{expectedRecord}</code></> : null}
                                                      <br />
                                                      Click to choose provider and view DNS instructions.
                                                    </Tooltip>
                                                  }
                                                >
                                                  <span className={`d-inline-block ${canSetup ? 'receiving-badge-clickable' : ''}`} style={{ cursor: canSetup ? 'pointer' : 'default' }} role={canSetup ? 'button' : undefined} tabIndex={canSetup ? 0 : undefined} aria-label={canSetup ? 'Inbound provider options' : undefined} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (canSetup) openInboundProviderModalForDomain(domain); }} onKeyDown={(e) => { if (canSetup && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openInboundProviderModalForDomain(domain); } }}>
                                                    <Badge bg="warning" text="dark" className="text-nowrap" style={{ fontSize: '0.7rem' }}>{settingUpReceivingForDomainName === domain.domain ? '…' : '⚠️ Receiving needs attention'}</Badge>
                                                  </span>
                                                </OverlayTrigger>
                                              );
                                            }
                                            if (inboundSet) {
                                              const providerLabel = formatProviderName(effectiveReceivingProvider || 'postmark');
                                              return (
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={
                                                    <Tooltip>
                                                      {`Inbound is active for ${providerLabel} (public MX verified).`}
                                                      <br />
                                                      Click to choose provider, verify, or re-apply MX in Route53.
                                                    </Tooltip>
                                                  }
                                                >
                                                  <span className={`d-inline-block ${canSetup ? 'receiving-badge-clickable' : ''}`} style={{ cursor: canSetup ? 'pointer' : 'default' }} role={canSetup ? 'button' : undefined} tabIndex={canSetup ? 0 : undefined} aria-label={canSetup ? 'Inbound provider options' : undefined} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (canSetup) openInboundProviderModalForDomain(domain); }} onKeyDown={(e) => { if (canSetup && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openInboundProviderModalForDomain(domain); } }}>
                                                    <Badge bg="success" className="text-nowrap" style={{ fontSize: '0.7rem' }}>{settingUpReceivingForDomainName === domain.domain ? '…' : '✅ Inbound set'}</Badge>
                                                  </span>
                                                </OverlayTrigger>
                                              );
                                            }
                                            return (
                                              <OverlayTrigger placement="top" overlay={<Tooltip>Choose inbound provider (Postmark / SES) and view DNS instructions.</Tooltip>}>
                                                <span className="d-inline-block">
                                                  <Button size="sm" variant="outline-secondary" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openInboundProviderModalForDomain(domain); }} disabled={!canSetup} style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} aria-label="Set up inbound receiving for this domain">
                                                    {settingUpReceivingForDomainName === domain.domain ? '…' : 'Set up inbound'}
                                                  </Button>
                                                </span>
                                              </OverlayTrigger>
                                            );
                                          })()}
                                          {(() => {
                                            // Prefer Domains API (outboundConfigured / coldOutreachPrimaryPoolId when backend adds it); fallback to prefetched cache.
                                          const outboundConfiguredFromApi =
                                            domain.outboundConfigured === true ||
                                            (domain.coldOutreachPrimaryPoolId != null && String(domain.coldOutreachPrimaryPoolId).trim() !== '');
                                          const outboundPolicy = domainRoutingByDomain[domain.domain];
                                          const outboundConfiguredFromCache = Boolean(outboundPolicy?.routes?.cold_outreach?.primaryPoolId);
                                          const outboundConfigured = outboundConfiguredFromApi || outboundConfiguredFromCache;
                                          const outboundUnknown = !outboundPrefetchDone && !outboundConfiguredFromApi && !outboundConfiguredFromCache;
                                          const outboundNeedsAttention = domain.outboundNeedsAttention === true;
                                          const openOutbound = (e) => {
                                            e?.preventDefault?.();
                                            e?.stopPropagation?.();
                                            openOutboundModal(domain);
                                          };
                                          if (outboundNeedsAttention) {
                                            return (
                                              <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                  <Tooltip>Outbound configuration needs attention. Click to fix.</Tooltip>
                                                }
                                              >
                                                <span
                                                  className="d-inline-block receiving-badge-clickable"
                                                  style={{ cursor: 'pointer' }}
                                                  role="button"
                                                  tabIndex={0}
                                                  aria-label="Open outbound settings"
                                                  onClick={openOutbound}
                                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOutboundModal(domain); } }}
                                                >
                                                  <Badge bg="warning" text="dark" className="text-nowrap" style={{ fontSize: '0.7rem' }}>
                                                    ⚠️ Outbound needs attention
                                                  </Badge>
                                                </span>
                                              </OverlayTrigger>
                                            );
                                          }
                                          if (outboundUnknown) {
                                            return (
                                              <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                  <Tooltip>Loading outbound status… Click to open outbound settings.</Tooltip>
                                                }
                                              >
                                                <span
                                                  className="d-inline-block receiving-badge-clickable"
                                                  style={{ cursor: 'pointer' }}
                                                  role="button"
                                                  tabIndex={0}
                                                  aria-label="Open outbound settings"
                                                  onClick={openOutbound}
                                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOutboundModal(domain); } }}
                                                >
                                                  <Badge bg="secondary" className="text-nowrap" style={{ fontSize: '0.7rem' }}>
                                                    Outbound …
                                                  </Badge>
                                                </span>
                                              </OverlayTrigger>
                                            );
                                          }
                                          if (outboundConfigured) {
                                            return (
                                              <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                  <Tooltip>Outbound is configured. Click to view or change outbound settings.</Tooltip>
                                                }
                                              >
                                                <span
                                                  className="d-inline-block receiving-badge-clickable"
                                                  style={{ cursor: 'pointer' }}
                                                  role="button"
                                                  tabIndex={0}
                                                  aria-label="Open outbound settings"
                                                  onClick={openOutbound}
                                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOutboundModal(domain); } }}
                                                >
                                                  <Badge bg="success" className="text-nowrap" style={{ fontSize: '0.7rem' }}>
                                                    ✅ Outbound set
                                                  </Badge>
                                                </span>
                                              </OverlayTrigger>
                                            );
                                          }
                                          return (
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={
                                                <Tooltip>Configure outbound provider and credentials for this domain.</Tooltip>
                                              }
                                            >
                                              <span className="d-inline-block">
                                                <Button
                                                  size="sm"
                                                  variant="outline-secondary"
                                                  onClick={openOutbound}
                                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                  aria-label="Set up outbound for this domain"
                                                >
                                                  Set up outbound
                                                </Button>
                                              </span>
                                            </OverlayTrigger>
                                          );
                                        })()}
                                          {(() => {
                                            const trackingEnabled = domain.trackingEnabled === true;
                                            const rawTrackingStatus = String(domain.trackingStatus || '').trim().toLowerCase();
                                            const trackingStatus = trackingEnabled ? (rawTrackingStatus || 'missing') : 'not_configured';
                                            const openTracking = (e) => {
                                              e?.preventDefault?.();
                                              e?.stopPropagation?.();
                                              openOutboundModal(domain);
                                            };

                                            if (trackingStatus === 'verified') {
                                              return (
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={
                                                    <Tooltip>
                                                      Tracking domain is verified. Click to view or update tracking settings.
                                                    </Tooltip>
                                                  }
                                                >
                                                  <span
                                                    className="d-inline-block receiving-badge-clickable"
                                                    style={{ cursor: 'pointer' }}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label="Open tracking settings"
                                                    onClick={openTracking}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOutboundModal(domain); } }}
                                                  >
                                                    <Badge bg="success" className="text-nowrap" style={{ fontSize: '0.7rem' }}>
                                                      ✅ Tracking set
                                                    </Badge>
                                                  </span>
                                                </OverlayTrigger>
                                              );
                                            }

                                            if (trackingStatus === 'pending') {
                                              return (
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={
                                                    <Tooltip>
                                                      Tracking is enabled and DNS is still propagating. Click to recheck or update settings.
                                                    </Tooltip>
                                                  }
                                                >
                                                  <span
                                                    className="d-inline-block receiving-badge-clickable"
                                                    style={{ cursor: 'pointer' }}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label="Open tracking settings"
                                                    onClick={openTracking}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOutboundModal(domain); } }}
                                                  >
                                                    <Badge bg="warning" text="dark" className="text-nowrap" style={{ fontSize: '0.7rem' }}>
                                                      ⏳ Tracking pending
                                                    </Badge>
                                                  </span>
                                                </OverlayTrigger>
                                              );
                                            }

                                            if (trackingStatus === 'missing') {
                                              return (
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={
                                                    <Tooltip>
                                                      Tracking domain record is missing. Click to configure tracking for this domain.
                                                    </Tooltip>
                                                  }
                                                >
                                                  <span
                                                    className="d-inline-block receiving-badge-clickable"
                                                    style={{ cursor: 'pointer' }}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label="Open tracking settings"
                                                    onClick={openTracking}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOutboundModal(domain); } }}
                                                  >
                                                    <Badge bg="warning" text="dark" className="text-nowrap" style={{ fontSize: '0.7rem' }}>
                                                      ⚠️ Tracking needs setup
                                                    </Badge>
                                                  </span>
                                                </OverlayTrigger>
                                              );
                                            }

                                            return (
                                              <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                  <Tooltip>
                                                    Tracking domain is not configured yet. Click to set up tracking for this domain.
                                                  </Tooltip>
                                                }
                                              >
                                                <span
                                                  className="d-inline-block receiving-badge-clickable"
                                                  style={{ cursor: 'pointer' }}
                                                  role="button"
                                                  tabIndex={0}
                                                  aria-label="Open tracking settings"
                                                  onClick={openTracking}
                                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOutboundModal(domain); } }}
                                                >
                                                  <Badge bg="warning" text="dark" className="text-nowrap" style={{ fontSize: '0.7rem' }}>
                                                    ⚠️ Tracking not set
                                                  </Badge>
                                                </span>
                                              </OverlayTrigger>
                                            );
                                          })()}
                                          {(() => {
                                            const forwardingEnabled = domain.forwardingEnabled === true;
                                            const hasDestination = Boolean(domain.forwardingDestinationId);
                                            const openForwarding = (e) => {
                                              e?.preventDefault?.();
                                              e?.stopPropagation?.();
                                              openForwardingModal(domain);
                                            };

                                            if (forwardingEnabled && hasDestination) {
                                              return (
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={
                                                    <Tooltip>
                                                      Forwarding is enabled for this domain. Click to edit destination and behavior.
                                                    </Tooltip>
                                                  }
                                                >
                                                  <span
                                                    className="d-inline-block receiving-badge-clickable"
                                                    style={{ cursor: 'pointer' }}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label="Open forwarding settings"
                                                    onClick={openForwarding}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openForwardingModal(domain); } }}
                                                  >
                                                    <Badge bg="success" className="text-nowrap" style={{ fontSize: '0.7rem' }}>
                                                      ↪ Forwarding on
                                                    </Badge>
                                                  </span>
                                                </OverlayTrigger>
                                              );
                                            }

                                            if (forwardingEnabled && !hasDestination) {
                                              return (
                                                <OverlayTrigger
                                                  placement="top"
                                                  overlay={
                                                    <Tooltip>
                                                      Forwarding is enabled but no destination is selected yet. Click to fix.
                                                    </Tooltip>
                                                  }
                                                >
                                                  <span
                                                    className="d-inline-block receiving-badge-clickable"
                                                    style={{ cursor: 'pointer' }}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label="Open forwarding settings"
                                                    onClick={openForwarding}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openForwardingModal(domain); } }}
                                                  >
                                                    <Badge bg="warning" text="dark" className="text-nowrap" style={{ fontSize: '0.7rem' }}>
                                                      ⚠️ Forwarding needs destination
                                                    </Badge>
                                                  </span>
                                                </OverlayTrigger>
                                              );
                                            }

                                            return (
                                              <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                  <Tooltip>
                                                    Configure domain forwarding (on/off, destination, and path/query behavior).
                                                  </Tooltip>
                                                }
                                              >
                                                <span className="d-inline-block">
                                                  <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={openForwarding}
                                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                    aria-label="Set up forwarding for this domain"
                                                  >
                                                    Set up forwarding
                                                  </Button>
                                                </span>
                                              </OverlayTrigger>
                                            );
                                          })()}
                                          <span
                                            className="d-inline-block receiving-badge-clickable"
                                            style={{ cursor: 'pointer' }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label="Delete this domain"
                                            onClick={() => handleDeleteDomain(domain.domain)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDeleteDomain(domain.domain); } }}
                                          >
                                            <Badge
                                              bg="danger"
                                              className="text-nowrap"
                                              style={{ fontSize: '0.7rem' }}
                                            >
                                              <XCircle size={10} className="me-1" />
                                              Delete
                                            </Badge>
                                          </span>
                                      </div>
                                      {/* Inline Email Auth actions removed: use the wizard modal only. */}
                                    </td>
                                  </tr>
                                  ))}
                                </tbody>
                              </Table>
                            )}
                          </td>
                        </tr>
                      </Collapse>
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {filteredGroups.length === 0 && (
        <Row>
          <Col>
            <Card className="text-center py-5">
              <Card.Body>
                <Globe size={48} className="text-muted mb-3" />
                <h5>No domains configured</h5>
                <p className="text-muted">Add your first domain to start sending emails</p>
                <Button variant="primary" onClick={handleAddDomain}>
                  <Plus className="me-1" />Add Domain
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Per-domain Email Auth wizard (no bulk) */}
      <EmailAuthWizardModal
        show={showEmailAuthWizard}
        onHide={() => setShowEmailAuthWizard(false)}
        domainObj={emailAuthWizardDomain}
        onAuthSuccess={() => loadDomains()}
        tenantDmarcEmail={userPreferencesData?.preferences?.dmarcCollectionEmail || ''}
      />

      {/* Per-domain Outbound Routing */}
      <Modal
        show={showOutboundModal}
        onHide={() => {
          setShowOutboundModal(false);
          setOutboundDomainObj(null);
          setTrackingSubdomainDraft('inst');
          setTrackingProviderDraft(TRACKING_PROVIDER_DEFAULT);
          setRevealedSmtpPassword(null);
          setRevealedImapPassword(null);
          setRevealedSmtpComPassword(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>
            {String(outboundDomainType).toLowerCase() === 'cold_outreach'
              ? 'Outbound Routing — Cold Outreach'
              : 'Domain Purpose'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-2">
            <div><strong>Inbound (MX)</strong> stays Postmark (managed by Kross Global).</div>
            {String(outboundDomainType).toLowerCase() === 'cold_outreach' ? (
              <div><strong>Outbound</strong> selects a Send Pool for <code>cold_outreach</code>.</div>
            ) : String(outboundDomainType).toLowerCase() === 'business' ? (
              <div><strong>Outbound</strong> selects a Send Pool for <strong>Transactional</strong> (e.g. Mailgun).</div>
            ) : (
              <div><strong>Outbound</strong> for Newsletter uses the day-to-day provider.</div>
            )}
          </div>

          {outboundError && (
            <Alert variant="danger" className="py-2">
              {outboundError}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Domain</Form.Label>
            <Form.Control value={outboundDomainObj?.domain || ''} disabled />
          </Form.Group>

          {(() => {
            const trackingEnabled = outboundDomainObj?.trackingEnabled === true;
            const statusRaw = String(outboundDomainObj?.trackingStatus || '').trim().toLowerCase();
            const trackingStatus = trackingEnabled ? (statusRaw || 'pending') : 'not_configured';
            const trackingProvider = normalizeTrackingProvider(
              trackingProviderDraft || outboundDomainObj?.trackingProvider || TRACKING_PROVIDER_DEFAULT
            );
            const trackingSubdomain = normalizeTrackingSubdomainDraft(trackingSubdomainDraft || outboundDomainObj?.trackingSubdomain || 'inst') || 'inst';
            const trackingDomain = outboundDomainObj?.domain ? `${trackingSubdomain}.${outboundDomainObj.domain}` : '';
            const trackingTarget = getTrackingCnameTargetForProvider(trackingProvider);
            const trackingObservedValues = Array.isArray(outboundDomainObj?.trackingObservedValues) ? outboundDomainObj.trackingObservedValues : [];
            const trackingLastCheckedAtLabel = formatTrackingLastCheckedAt(outboundDomainObj?.trackingLastCheckedAt);
            const trackingDomainKey = String(outboundDomainObj?.domain || '').trim().toLowerCase();
            const autoRecheckAppliesToDomain =
              String(trackingAutoRecheckState?.domain || '').trim().toLowerCase() === trackingDomainKey;
            const autoRecheckNextSeconds =
              trackingAutoRecheckState?.nextDelayMs > 0 ? Math.ceil(trackingAutoRecheckState.nextDelayMs / 1000) : 0;
            const subdomainValid = isValidTrackingSubdomain(trackingSubdomain);

            let statusBadge = <Badge bg="secondary">Not configured</Badge>;
            if (trackingStatus === 'verified') {
              statusBadge = <Badge bg="success">Verified</Badge>;
            } else if (trackingStatus === 'pending') {
              statusBadge = <Badge bg="warning" text="dark">Pending DNS</Badge>;
            } else if (trackingStatus === 'missing') {
              statusBadge = <Badge bg="danger">DNS record missing</Badge>;
            }

            return (
              <Card className="mb-3" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
                <Card.Body className="py-2">
                  <div className="d-flex align-items-start justify-content-between mb-2">
                    <div>
                      <div style={{ fontWeight: 600 }}>Custom Tracking Domain</div>
                      <div className="small text-muted">
                        Last checked: {trackingLastCheckedAtLabel}
                      </div>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-1">
                      <div className="d-flex align-items-center gap-2">
                        {statusBadge}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => refreshTrackingDomainStatus({ force: true })}
                          disabled={!outboundDomainObj?.domain || trackingDomainStatusQuery.isFetching}
                          style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                          title="Re-check tracking DNS status"
                        >
                          {trackingDomainStatusQuery.isFetching ? 'Checking…' : 'Recheck'}
                        </Button>
                      </div>
                      {autoRecheckAppliesToDomain && trackingAutoRecheckState?.active ? (
                        <div className="small text-muted">
                          Checking DNS propagation automatically (attempt {trackingAutoRecheckState.attempt}/{trackingAutoRecheckState.maxAttempts})
                          {autoRecheckNextSeconds > 0 ? `, next check in ~${autoRecheckNextSeconds}s.` : '.'}
                        </div>
                      ) : null}
                      {autoRecheckAppliesToDomain && !trackingAutoRecheckState?.active && trackingAutoRecheckState?.stopped ? (
                        <div className="small text-muted">
                          {trackingAutoRecheckState.reason || 'Automatic checks paused. Click Recheck to check again.'}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="small mt-2">
                    <strong>Required DNS Record</strong>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <Form.Label className="small mb-0">Tracking Provider</Form.Label>
                    <Form.Select
                      size="sm"
                      value={trackingProvider}
                      onChange={(e) => setTrackingProviderDraft(normalizeTrackingProvider(e.target.value))}
                      style={{ maxWidth: 180 }}
                    >
                      {TRACKING_PROVIDER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <Form.Label className="small mb-0">Subdomain</Form.Label>
                    <Form.Control
                      size="sm"
                      value={trackingSubdomainDraft}
                      onChange={(e) => setTrackingSubdomainDraft(String(e.target.value || '').toLowerCase())}
                      placeholder="inst"
                      autoComplete="off"
                      style={{ maxWidth: 180 }}
                    />
                  </div>
                  <div
                    className="small text-muted mt-1 mb-1"
                    style={{ fontSize: '0.8rem', lineHeight: 1.25, whiteSpace: 'nowrap' }}
                  >
                    <strong style={{ marginRight: 4 }}>Default:</strong>
                    <code
                      style={{
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      inst.{outboundDomainObj?.domain || 'yourdomain.com'}
                    </code>
                  </div>
                  <div
                    className="small text-muted mb-1"
                    style={{ fontSize: '0.8rem', lineHeight: 1.25 }}
                  >
                    <strong style={{ marginRight: 4 }}>Tracking domain:</strong>
                    <code
                      style={{
                        fontSize: '0.75rem',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}
                    >
                      {trackingDomain || '—'}
                    </code>
                  </div>
                  <div
                    className="small text-muted mb-1"
                    style={{ fontSize: '0.8rem', lineHeight: 1.25, whiteSpace: 'nowrap' }}
                  >
                    <strong style={{ marginRight: 4 }}>CNAME value:</strong>
                    <code
                      style={{
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {trackingTarget || 'prox.itrackly.com'}
                    </code>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => copyToClipboard('Tracking domain', trackingDomain)}
                      disabled={!trackingDomain}
                      style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                    >
                      Copy Tracking Domain
                    </Button>
                  </div>

                  {!subdomainValid ? (
                    <div className="small text-danger mt-2">
                      Subdomain must be a single DNS label (letters, numbers, hyphen only), for example <code>inst</code>.
                    </div>
                  ) : null}

                  <div
                    className="small text-muted mt-3"
                    style={{ fontSize: '0.8rem', lineHeight: 1.25 }}
                  >
                    <strong style={{ marginRight: 4 }}>Note:</strong>
                    If this domain is hosted in Route53, KG will auto-apply the CNAME when you click Enable Tracking.
                  </div>

                  <div className="d-flex gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={handleEnableTrackingDomain}
                      disabled={!outboundDomainObj?.domain || isConfiguringTrackingDomain || !subdomainValid}
                    >
                      {isConfiguringTrackingDomain
                        ? 'Saving…'
                        : (trackingEnabled ? 'Save Tracking' : 'Enable Tracking')}
                    </Button>
                    {trackingEnabled ? (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={handleDisableTrackingDomain}
                        disabled={isConfiguringTrackingDomain}
                      >
                        Disable Tracking
                      </Button>
                    ) : null}
                  </div>
                </Card.Body>
              </Card>
            );
          })()}

          <Form.Group className="mb-3">
            <Form.Label>Domain Purpose</Form.Label>
            <Form.Select value={outboundDomainType} onChange={(e) => setOutboundDomainType(e.target.value)}>
              <option value="business">Transactional</option>
              <option value="newsletter">Newsletter</option>
              <option value="cold_outreach">Cold Outreach</option>
            </Form.Select>
            <div className="text-muted small mt-1">
              <div>
                <strong>Routing guardrail:</strong> Cold Outreach routing requires <code>cold_outreach</code>.
                If you switch purpose away from cold outreach and save, KG will close this modal and you won’t be shown cold routing controls.
              </div>
              <div>
                <strong>Infrastructure class:</strong> Transactional + Newsletter use <strong>Mailgun</strong> (for now). Cold Outreach uses <strong>SMTP.com / MailChannels / Twilio SendGrid / KG</strong>.
              </div>
              <div className="mt-1">
                <em>KG email uses Postmark inbound + kg-smtp submission + SMTP.com/MailChannels/Mailgun/Twilio SendGrid relay outbound, with DNS-only auth in Route53.</em>
              </div>
            </div>
          </Form.Group>

          {/* Connection Info Panel (Instantly / iOS Mail) — IMAP/SMTP shown for all KG-hosted domains (capability-based) */}
          {outboundDomainObj?.domain && (
            <Card className="mb-3" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <Card.Body className="py-2">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div style={{ fontWeight: 600 }}>Connection Info (Instantly / iOS Mail)</div>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => triggerGetDomainConnectionInfo(outboundDomainObj.domain)}
                    disabled={domainConnectionInfoQuery.isFetching}
                    style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                    title="Refresh connection info"
                  >
                    {domainConnectionInfoQuery.isFetching ? 'Refreshing…' : 'Refresh'}
                  </Button>
                </div>

                <Row className="g-3">
                  <Col md={6}>
                    <div className="small text-muted mb-1">
                      <strong>SMTP Submission (kg-smtp)</strong>
                    </div>
                    {domainConnectionInfoQuery?.data?.data?.smtpSubmission ? (
                      <>
                        <div className="small">
                          <div><strong>Host:</strong> {domainConnectionInfoQuery.data.data.smtpSubmission.host}</div>
                          <div>
                            <strong>Ports:</strong>{' '}
                            {domainConnectionInfoQuery.data.data.smtpSubmission.ports?.submission ?? 587} /{' '}
                            {domainConnectionInfoQuery.data.data.smtpSubmission.ports?.smtps ?? 465}
                          </div>
                          <div><strong>Security:</strong> STARTTLS (587) / TLS (465)</div>
                          <div>
                            <strong>Username:</strong>{' '}
                            localpart@{outboundDomainObj.domain}
                          </div>
                          <div>
                            <strong>Password:</strong>{' '}
                            {(() => {
                              const p = domainConnectionInfoQuery.data.data.smtpSubmission.password;
                              if (!p) return 'Unknown';
                              if (p.exists === true) return '•••••••• (set)';
                              if (p.exists === false) return 'Not set';
                              return 'Unknown';
                            })()}
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => copyToClipboard('SMTP Host', domainConnectionInfoQuery.data.data.smtpSubmission.host)}
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                          >
                            Copy Host
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                              copyToClipboard('SMTP Username', `localpart@${outboundDomainObj.domain}`);
                            }}
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                          >
                            Copy Username
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                              if (smtpEditOpen) cancelEditSmtpCreds();
                              else beginEditSmtpCreds();
                            }}
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                          >
                            {smtpEditOpen ? 'Cancel' : 'Edit'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleRevealSecret('smtp')}
                            disabled={!domainConnectionInfoQuery?.data?.data?.reveal?.allowed || isRevealingSecret}
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                            title={domainConnectionInfoQuery?.data?.data?.reveal?.allowed ? 'Reveal SMTP password (audited)' : 'Password reveal disabled'}
                          >
                            {isRevealingSecret ? 'Revealing…' : 'Reveal Password'}
                          </Button>
                        </div>

                        <Collapse in={smtpEditOpen}>
                          <div className="mt-2">
                            <div className="small text-muted mb-1">Edit SMTP Credentials (domain-level)</div>
                            <div className="small text-muted" style={{ marginTop: -2, marginBottom: 8 }}>
                              Username defaults to <code>admin@{outboundDomainObj.domain}</code>. Password is stored encrypted and is never shown unless you click Reveal.
                            </div>
                            <Form.Group className="mb-2">
                              <Form.Label className="small mb-1">SMTP Username</Form.Label>
                              <Form.Control
                                value={smtpUsernameDraft}
                                onChange={(e) => setSmtpUsernameDraft(e.target.value)}
                                placeholder={`admin@${outboundDomainObj.domain}`}
                                autoComplete="off"
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Label className="small mb-1">SMTP Password (set/update)</Form.Label>
                              <Form.Control
                                type="password"
                                value={smtpPasswordDraft}
                                onChange={(e) => setSmtpPasswordDraft(e.target.value)}
                                placeholder="Leave blank to keep current password"
                                autoComplete="new-password"
                              />
                            </Form.Group>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={saveSmtpCreds}
                                disabled={isSavingSmtpCreds}
                              >
                                {isSavingSmtpCreds ? 'Saving…' : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline-secondary" onClick={cancelEditSmtpCreds} disabled={isSavingSmtpCreds}>
                                Close
                              </Button>
                            </div>
                          </div>
                        </Collapse>
                        {revealedSmtpPassword && (
                          <div className="mt-2">
                            <div className="small text-muted mb-1">SMTP Password (auto-hides)</div>
                            <InputGroup>
                              <Form.Control value={revealedSmtpPassword} readOnly style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
                              <Button variant="outline-secondary" onClick={() => copyToClipboard('SMTP Password', revealedSmtpPassword)}>
                                Copy
                              </Button>
                            </InputGroup>
                          </div>
                        )}
                        {domainConnectionInfoQuery?.data?.data?.smtp?.host && (
                          <div className="small text-muted mt-2">
                            Upstream relay (provider): {domainConnectionInfoQuery.data.data.smtp.host}:{domainConnectionInfoQuery.data.data.smtp.port}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="small text-muted">
                        {domainConnectionInfoQuery.isFetching ? 'Loading…' : 'SMTP submission info not available for this domain.'}
                      </div>
                    )}
                  </Col>

                  <Col md={6}>
                    <div className="small text-muted mb-1">
                      <strong>IMAP (Mailbox access)</strong>
                    </div>
                    {/*
                      Prefer backend-provided IMAP host/port so the UI stays correct if we ever change infra.
                      Fallback to current defaults.
                    */}
                    {(() => {
                      const imap = domainConnectionInfoQuery?.data?.data?.imap;
                      const imapHost = imap?.host || 'imap.krossglobalmarketing.com';
                      const imapPort = imap?.port || 993;
                      const imapPasswordKind = imap?.password?.kind;
                      const imapPasswordExists = imap?.password?.exists === true;
                      const passwordLabel = imapPasswordKind === 'shared_domain'
                        ? (imapPasswordExists ? 'Shared (domain-wide)' : 'Not set')
                        : 'Shared (domain-wide)';
                      return (
                    <div className="small">
                      <div><strong>Host:</strong> {imapHost}</div>
                      <div><strong>Port:</strong> {imapPort}</div>
                      <div><strong>Security:</strong> TLS</div>
                      <div><strong>Username:</strong> localpart@{outboundDomainObj.domain}</div>
                      <div><strong>Password:</strong> {passwordLabel}</div>
                    </div>
                      );
                    })()}
                    {domainConnectionInfoQuery?.data?.data?.imap?.host && (
                      <>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                              const imapHost = domainConnectionInfoQuery?.data?.data?.imap?.host || 'imap.krossglobalmarketing.com';
                              copyToClipboard('IMAP Host', imapHost);
                            }}
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                          >
                            Copy Host
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() =>
                              copyToClipboard(
                                'IMAP username template',
                                `localpart@${outboundDomainObj.domain}`
                              )
                            }
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                            title="Replace localpart with the mailbox name (e.g. emmettbutler)"
                          >
                            Copy username template
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                              if (imapEditOpen) cancelEditImapCreds();
                              else beginEditImapCreds();
                            }}
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                          >
                            {imapEditOpen ? 'Cancel' : 'Edit'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleRevealSecret('imap')}
                            disabled={!domainConnectionInfoQuery?.data?.data?.reveal?.allowed || isRevealingSecret}
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.45rem' }}
                            title={domainConnectionInfoQuery?.data?.data?.reveal?.allowed ? 'Reveal IMAP shared password (audited)' : 'Password reveal disabled'}
                          >
                            {isRevealingSecret ? 'Revealing…' : 'Reveal Password'}
                          </Button>
                        </div>
                        <Collapse in={imapEditOpen}>
                          <div className="mt-2">
                            <div className="small text-muted mb-1">Edit IMAP shared password (domain-wide)</div>
                            <div className="small text-muted" style={{ marginTop: -2, marginBottom: 8 }}>
                              Used by Instantly / iOS Mail for mailbox access. Stored encrypted in SSM. To apply to the mail server, re-save Domain Purpose with Force re-provision after saving.
                            </div>
                            <Form.Group className="mb-2">
                              <Form.Label className="small mb-1">IMAP shared password (set/update)</Form.Label>
                              <Form.Control
                                type="password"
                                value={imapPasswordDraft}
                                onChange={(e) => setImapPasswordDraft(e.target.value)}
                                placeholder="Enter new IMAP shared password"
                                autoComplete="new-password"
                              />
                            </Form.Group>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={saveImapCreds}
                                disabled={isSavingImapCreds || !imapPasswordDraft?.trim()}
                              >
                                {isSavingImapCreds ? 'Saving…' : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline-secondary" onClick={cancelEditImapCreds} disabled={isSavingImapCreds}>
                                Close
                              </Button>
                            </div>
                          </div>
                        </Collapse>
                        {revealedImapPassword && (
                          <div className="mt-2">
                            <div className="small text-muted mb-1">IMAP Shared Password (auto-hides)</div>
                            <InputGroup>
                              <Form.Control value={revealedImapPassword} readOnly style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
                              <Button variant="outline-secondary" onClick={() => copyToClipboard('IMAP Password', revealedImapPassword)}>
                                Copy
                              </Button>
                            </InputGroup>
                          </div>
                        )}
                      </>
                    )}
                  </Col>
                </Row>

                {domainConnectionInfoQuery?.data?.data?.smtp?.username?.kind === 'template' && (
                  <div className="text-muted small mt-2">
                    <strong>Note:</strong> SMTP username varies per mailbox for this provider. Template:{' '}
                    <code>{domainConnectionInfoQuery.data.data.smtp.username.template}</code>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Upstream relay credentials — MailChannels/SendGrid/Elastic Email: pool-level (shared). SMTP.com/Mailgun: per-domain in SSM. */}
          {outboundDomainObj?.domain && (outboundProviderType === 'SMTP_COM' || outboundProviderType === 'MAILCHANNELS' || outboundProviderType === 'MAILGUN' || outboundProviderType === 'SENDGRID' || outboundProviderType === 'ELASTIC_EMAIL') && (
            <Card className="mb-3" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <Card.Body className="py-2">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div style={{ fontWeight: 600 }}>Upstream relay credentials ({providerCredsLabel(outboundProviderType)})</div>
                </div>
                {(() => {
                  // MailChannels/SendGrid/Elastic Email = pool creds; SMTP.com + Mailgun = domain creds (per-domain SSM).
                  const usePoolCreds = (outboundProviderType === 'MAILCHANNELS' || outboundProviderType === 'SENDGRID' || outboundProviderType === 'ELASTIC_EMAIL') && Boolean(outboundPrimaryPoolId);
                  const poolData = poolCredentialsQuery?.data?.data ?? poolCredentialsQuery?.data ?? {};
                  const smtpData = outboundSmtpComCredentialsQuery?.data?.data ?? outboundSmtpComCredentialsQuery?.data ?? {};
                  const mailgunData = outboundMailgunCredentialsQuery?.data?.data ?? outboundMailgunCredentialsQuery?.data ?? {};
                  const data = usePoolCreds ? poolData : (outboundProviderType === 'MAILGUN' ? mailgunData : smtpData);
                  const configured = data.configured ?? data.credentialsSet ?? data.exists ?? false;
                  const source = data.source;
                  const channel = data.channel;
                  const lastUpdatedAt = data.lastUpdatedAt;
                  const message = data.message;
                  const selectedProviderKey = normalizeRelayProviderKey(outboundProviderType);
                  const domainProviderKey = normalizeRelayProviderKey(outboundDomainObj?.relayProvider || outboundDomainObj?.authProvider || '');
                  const domainRelaySuffixFallback =
                    selectedProviderKey && selectedProviderKey === domainProviderKey
                      ? String(outboundDomainObj?.relayAccountSuffix || '').trim()
                      : '';
                  const relayAccountSuffixCurrentRaw = String(data?.relayAccountSuffix || domainRelaySuffixFallback || '').trim();
                  const relayAccountSuffixCurrent = RELAY_ACCOUNT_SUFFIX_PATTERN.test(relayAccountSuffixCurrentRaw) ? relayAccountSuffixCurrentRaw : '';
                  const relayAccountSuffixChanged = String(relayAccountSuffixDraft || '').trim() !== relayAccountSuffixCurrent;
                  const loadingCreds = usePoolCreds
                    ? (poolCredentialsQuery?.isFetching || poolCredentialsQuery?.isLoading)
                    : outboundProviderType === 'MAILGUN'
                    ? (outboundMailgunCredentialsQuery?.isFetching || outboundMailgunCredentialsQuery?.isLoading)
                    : (outboundSmtpComCredentialsQuery?.isFetching || outboundSmtpComCredentialsQuery?.isLoading);
                  const credsQueryError = usePoolCreds
                    ? poolCredentialsQuery?.error
                    : outboundProviderType === 'MAILGUN'
                      ? outboundMailgunCredentialsQuery?.error
                      : outboundSmtpComCredentialsQuery?.error;
                  const credsQueryErrorMessage = credsQueryError
                    ? extractApiErrorMessage(credsQueryError, 'Could not load relay credential status.')
                    : '';
                  const showStatusOnly = configured && !smtpComCredsEditOpen;
                  const savingCreds = usePoolCreds ? isSavingPoolCreds : (outboundProviderType === 'MAILGUN' ? isSavingMailgunCreds : isSavingSmtpComCreds);
                  const revealingCreds = usePoolCreds ? isRevealingPoolCreds : (outboundProviderType === 'MAILGUN' ? isRevealingMailgunCreds : isRevealingSmtpComCreds);
                  const canRevealAuditedRelaySecret = Boolean(domainConnectionInfoQuery?.data?.data?.reveal?.allowed);
                  const outboundProviderTypeUpper = String(outboundProviderType || '').toUpperCase();
                  const relayAliasProviderName = outboundProviderTypeUpper === 'SMTP_COM'
                    ? 'SMTP.com'
                    : outboundProviderTypeUpper === 'MAILGUN'
                      ? 'Mailgun'
                      : outboundProviderTypeUpper === 'SENDGRID'
                        ? 'Twilio SendGrid'
                      : outboundProviderTypeUpper === 'ELASTIC_EMAIL'
                        ? 'Elastic Email'
                      : outboundProviderTypeUpper === 'MAILCHANNELS'
                        ? 'MailChannels'
                        : providerCredsLabel(outboundProviderType);
                  const savedUsernameLabel = outboundProviderTypeUpper === 'MAILCHANNELS'
                    ? 'Account ID'
                    : outboundProviderTypeUpper === 'SENDGRID'
                      ? 'Username'
                    : outboundProviderTypeUpper === 'ELASTIC_EMAIL'
                      ? 'Username'
                    : outboundProviderTypeUpper === 'SMTP_COM'
                      ? 'Channel'
                      : 'SMTP username';

                  if (loadingCreds) {
                    return (
                      <div className="text-muted small">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading credentials for this domain…
                      </div>
                    );
                  }

                  if (!showStatusOnly && credsQueryError && !smtpComCredsEditOpen) {
                    return (
                      <>
                        <Alert variant="warning" className="py-2 mb-2">
                          <div className="small mb-1">
                            Relay status temporarily unavailable.
                          </div>
                          {credsQueryErrorMessage ? (
                            <div className="small text-muted">{credsQueryErrorMessage}</div>
                          ) : null}
                        </Alert>
                        <div className="d-flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={refreshOutboundRelayCredentialsStatus}
                          >
                            Retry status check
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => setSmtpComCredsEditOpen(true)}
                          >
                            Edit credentials anyway
                          </Button>
                        </div>
                      </>
                    );
                  }

                  if (showStatusOnly) {
                    return (
                      <>
                        <div className="mb-2">
                          <Badge bg="success" className="me-1">Credentials set</Badge>
                          <span className="text-muted small">
                            Source: {source === 'pool' ? 'Pool (SSM)' : (usePoolCreds ? 'Pool (SSM)' : (source === 'domain' ? 'Domain (SSM)' : 'SSM'))}
                          </span>
                          {channel && (
                            <span className="text-muted small ms-2">
                              {savedUsernameLabel}: {(channel.length > 80 || /AQIC/i.test(channel) || /^[A-Za-z0-9+/=\s]+$/.test(channel) && channel.length > 60) ? '(set)' : channel}
                            </span>
                          )}
                          {relayAccountSuffixCurrent && (
                            <span className="text-muted small ms-2">
                              Relay label: {`${relayAliasProviderName} ${relayAccountSuffixCurrent}`}
                            </span>
                          )}
                          {lastUpdatedAt && (
                            <span className="text-muted small ms-2">
                              Last updated: {toLocaleDateStringInTimezone(lastUpdatedAt, undefined, undefined, tenantTimezone)}
                            </span>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => setSmtpComCredsEditOpen(true)}
                          >
                            Replace credentials
                          </Button>
                          {(usePoolCreds || String(outboundProviderType || '').toUpperCase() === 'SMTP_COM' || String(outboundProviderType || '').toUpperCase() === 'MAILGUN') && (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() =>
                              handleRevealRelaySecret({
                                usePoolCreds,
                                providerType: outboundProviderType,
                                poolId: outboundPrimaryPoolId,
                                domain: outboundDomainObj?.domain,
                              })
                            }
                            disabled={revealingCreds || !canRevealAuditedRelaySecret}
                            title={canRevealAuditedRelaySecret ? 'Reveal (audited)' : 'Password reveal disabled'}
                          >
                            {revealingCreds ? <Spinner size="sm" /> : 'Reveal (audited)'}
                          </Button>
                          )}
                        </div>
                        {revealedSmtpComPassword && (
                          <div className="mt-2">
                            <div className="small text-muted mb-1">Upstream Relay Password (auto-hides)</div>
                            <InputGroup>
                              <Form.Control value={revealedSmtpComPassword} readOnly style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
                              <Button
                                variant="outline-secondary"
                                onClick={() => copyToClipboard(`${providerCredsLabel(outboundProviderType)} password`, revealedSmtpComPassword)}
                              >
                                Copy
                              </Button>
                            </InputGroup>
                          </div>
                        )}
                      </>
                    );
                  }

                  const copyText = outboundProviderType === 'MAILCHANNELS'
                    ? 'KG relays outbound mail through MailChannels when this domain uses a MailChannels pool. Use your MailChannels Account ID as the SMTP username (not an email address), plus the MailChannels SMTP password.'
                    : outboundProviderType === 'MAILGUN'
                    ? 'KG relays outbound mail through Mailgun when this domain uses a Mailgun send pool. Enter the Mailgun SMTP credentials for this domain: Username: typically postmaster@yourdomain.com. Password: the SMTP password generated in the Mailgun dashboard. Do not use your Mailgun console login password or the IMAP shared mailbox password.'
                    : outboundProviderType === 'SENDGRID'
                    ? 'KG relays outbound mail through Twilio SendGrid when this domain uses a Twilio SendGrid pool. Use SMTP username "apikey" (or your configured pool username) and your SendGrid API key as the SMTP password.'
                    : outboundProviderType === 'ELASTIC_EMAIL'
                    ? 'KG relays outbound mail through Elastic Email when this domain uses an Elastic Email pool. Enter the Elastic Email SMTP username and SMTP password for this relay account.'
                    : 'KG relays outbound mail to SMTP.com when this domain uses an SMTP.com pool. Credentials are per domain channel — use the Channel Login and Channel Password from SMTP.com → Manage Channels.';
                  return (
                    <>
                      <div className="text-muted small mb-2">
                        {copyText}
                      </div>
                      {/* Guardrail: SMTP.com uses channel login; MailChannels uses Account ID; SendGrid usually uses "apikey"; Mailgun uses SMTP login email. */}
                      <Form.Group className="mb-2">
                        <Form.Label className="small">
                          {outboundProviderTypeUpper === 'SMTP_COM'
                            ? 'Channel login (email)'
                            : outboundProviderTypeUpper === 'MAILCHANNELS'
                              ? 'MailChannels SMTP username (Account ID)'
                              : outboundProviderTypeUpper === 'SENDGRID'
                                ? 'SMTP username'
                              : outboundProviderTypeUpper === 'ELASTIC_EMAIL'
                                ? 'Elastic Email SMTP username'
                                : 'SMTP login (email)'}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder={
                            outboundProviderTypeUpper === 'MAILGUN'
                              ? 'e.g. postmaster@mg.yourdomain.com'
                              : outboundProviderTypeUpper === 'SMTP_COM'
                                ? 'e.g. admin@getkrosskast.com'
                                : outboundProviderTypeUpper === 'MAILCHANNELS'
                                  ? 'e.g. ee9aefhbcj99 (Account ID)'
                                  : outboundProviderTypeUpper === 'SENDGRID'
                                    ? 'Usually: apikey'
                                  : outboundProviderTypeUpper === 'ELASTIC_EMAIL'
                                    ? 'e.g. your-smtp-username'
                                  : 'e.g. SMTP username'
                          }
                          value={smtpComChannelLoginDraft}
                          onChange={(e) => setSmtpComChannelLoginDraft(e.target.value)}
                          size="sm"
                        />
                        {outboundProviderTypeUpper === 'MAILCHANNELS' && (
                          <Form.Text className="text-muted">
                            Use your MailChannels Account ID (for example <code>ee9aefhbcj99</code>). Do not enter an email address.
                          </Form.Text>
                        )}
                        {outboundProviderTypeUpper === 'SENDGRID' && (
                          <Form.Text className="text-muted">
                            For Twilio SendGrid SMTP, username is usually <code>apikey</code>.
                          </Form.Text>
                        )}
                        {outboundProviderTypeUpper === 'ELASTIC_EMAIL' && (
                          <Form.Text className="text-muted">
                            Use your Elastic Email SMTP username for this relay account.
                          </Form.Text>
                        )}
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label className="small">
                          {outboundProviderType === 'SMTP_COM'
                            ? 'Channel password'
                            : outboundProviderType === 'SENDGRID'
                              ? 'SMTP password (API key)'
                            : outboundProviderType === 'ELASTIC_EMAIL'
                              ? 'SMTP password'
                              : 'SMTP password'}
                        </Form.Label>
                        <Form.Control
                          type="password"
                          placeholder={
                            outboundProviderType === 'MAILGUN'
                              ? 'Paste Mailgun SMTP password'
                              : outboundProviderType === 'SMTP_COM'
                                ? 'Paste channel password'
                                : outboundProviderType === 'SENDGRID'
                                  ? 'Paste Twilio SendGrid API key'
                                : outboundProviderType === 'ELASTIC_EMAIL'
                                  ? 'Paste Elastic Email SMTP password'
                                  : 'Paste SMTP password'
                          }
                          value={smtpComChannelPasswordDraft}
                          onChange={(e) => setSmtpComChannelPasswordDraft(e.target.value)}
                          size="sm"
                        />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Relay account suffix (4 letters/numbers)</Form.Label>
                        <Form.Control
                          type="text"
                          inputMode="text"
                          maxLength={4}
                          placeholder="e.g. 083c"
                          value={relayAccountSuffixDraft}
                          onChange={(e) => {
                            const next = normalizeRelaySuffixInput(e.target.value);
                            setRelayAccountSuffixDraft(next);
                          }}
                          size="sm"
                        />
                        <Form.Text className="text-muted">
                          Shows as <code>{`${relayAliasProviderName} 083c`}</code> in domain pickers so duplicate relay accounts are easy to identify.
                        </Form.Text>
                      </Form.Group>
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={async () => {
                            const draftedUsername = String(smtpComChannelLoginDraft || '').trim();
                            const draftedRelayAccountSuffix = String(relayAccountSuffixDraft || '').trim();
                            const providerUpper = String(outboundProviderType || '').toUpperCase();
                            if (draftedRelayAccountSuffix && !RELAY_ACCOUNT_SUFFIX_PATTERN.test(draftedRelayAccountSuffix)) {
                              toast.error('Relay account suffix must be exactly 4 letters/numbers (example: 083c).');
                              return;
                            }
                            const hasRelayAccountSuffixChanged = draftedRelayAccountSuffix !== relayAccountSuffixCurrent;
                            const relayAccountSuffixForSave = hasRelayAccountSuffixChanged ? (draftedRelayAccountSuffix || null) : undefined;
                            if (providerUpper === 'MAILCHANNELS' && draftedUsername && !isValidMailChannelsAccountId(draftedUsername)) {
                              toast.error('MailChannels SMTP username must be your Account ID (for example ee9aefhbcj99), not an email address.');
                              return;
                            }
                            if (
                              (providerUpper === 'MAILCHANNELS' || providerUpper === 'SENDGRID' || providerUpper === 'ELASTIC_EMAIL') &&
                              !outboundPrimaryPoolId
                            ) {
                              toast.error('Create/select a Send Pool first. Go to Relay Accounts → Add relay account, then come back and pick the pool.');
                              return;
                            }
                            if (usePoolCreds && outboundPrimaryPoolId) {
                              try {
                                await putPoolCredentials({
                                  poolId: outboundPrimaryPoolId,
                                  username: draftedUsername || undefined,
                                  password: smtpComChannelPasswordDraft || undefined,
                                  domain: outboundDomainObj?.domain,
                                  relayAccountSuffix: relayAccountSuffixForSave,
                                }).unwrap();
                                if (typeof console !== 'undefined' && console.log) {
                                  console.log('[KG relay credentials]', { event: 'saved', provider: outboundProviderType, scope: 'pool', poolId: outboundPrimaryPoolId });
                                }
                                toast.success('Outbound relay credentials saved.');
                                setSmtpComChannelPasswordDraft('');
                                setSmtpComCredsEditOpen(false);
                                triggerGetPoolCredentials(outboundPrimaryPoolId);
                              } catch (e) {
                                toast.error(e?.data?.message || e?.message || 'Failed to save credentials');
                              }
                            } else {
                              const domain = outboundDomainObj?.domain;
                              if (!domain) return;
                              try {
                                if (outboundProviderType === 'MAILGUN') {
                                  await putOutboundMailgunCredentials({
                                    domain,
                                    username: draftedUsername || undefined,
                                    password: smtpComChannelPasswordDraft || undefined,
                                    relayAccountSuffix: relayAccountSuffixForSave,
                                  }).unwrap();
                                  if (typeof console !== 'undefined' && console.log) {
                                    console.log('[KG relay credentials]', { event: 'saved', provider: outboundProviderType, scope: 'domain', domain });
                                  }
                                  toast.success('Outbound relay credentials saved.');
                                  setSmtpComChannelPasswordDraft('');
                                  setSmtpComCredsEditOpen(false);
                                  triggerGetOutboundMailgunCredentials(domain);
                                } else {
                                  await putOutboundSmtpComCredentials({
                                    domain,
                                    username: draftedUsername || undefined,
                                    password: smtpComChannelPasswordDraft || undefined,
                                    scope: 'domain',
                                    relayAccountSuffix: relayAccountSuffixForSave,
                                  }).unwrap();
                                  if (typeof console !== 'undefined' && console.log) {
                                    console.log('[KG relay credentials]', { event: 'saved', provider: outboundProviderType, scope: 'domain', domain });
                                  }
                                  toast.success('Outbound relay credentials saved.');
                                  setSmtpComChannelPasswordDraft('');
                                  setSmtpComCredsEditOpen(false);
                                  triggerGetOutboundSmtpComCredentials(domain);
                                }
                              } catch (e) {
                                toast.error(e?.data?.message || e?.message || 'Failed to save credentials');
                              }
                            }
                          }}
                          disabled={savingCreds || (!smtpComChannelLoginDraft.trim() && !smtpComChannelPasswordDraft && !relayAccountSuffixChanged)}
                        >
                          {savingCreds ? <><Spinner size="sm" className="me-1" /> Saving…</> : 'Save credentials'}
                        </Button>
                        {configured && (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                              setSmtpComCredsEditOpen(false);
                              setSmtpComChannelPasswordDraft('');
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                        {configured && (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() =>
                              handleRevealRelaySecret({
                                usePoolCreds,
                                providerType: outboundProviderType,
                                poolId: outboundPrimaryPoolId,
                                domain: outboundDomainObj?.domain,
                              })
                            }
                            disabled={revealingCreds || !canRevealAuditedRelaySecret}
                            title={canRevealAuditedRelaySecret ? 'Reveal (audited)' : 'Password reveal disabled'}
                          >
                            {revealingCreds ? <Spinner size="sm" /> : 'Reveal (audited)'}
                          </Button>
                        )}
                      </div>
                      {revealedSmtpComPassword && (
                        <div className="mt-2">
                          <div className="small text-muted mb-1">Upstream Relay Password (auto-hides)</div>
                          <InputGroup>
                            <Form.Control value={revealedSmtpComPassword} readOnly style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
                            <Button
                              variant="outline-secondary"
                              onClick={() => copyToClipboard(`${providerCredsLabel(outboundProviderType)} password`, revealedSmtpComPassword)}
                            >
                              Copy
                            </Button>
                          </InputGroup>
                        </div>
                      )}
                    </>
                  );
                })()}
              </Card.Body>
            </Card>
          )}

          {outboundDomainObj?.domain && String(outboundProviderType || '').toUpperCase() === 'SENDGRID' && (
            <Card className="mb-3" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <Card.Body className="py-2">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div style={{ fontWeight: 600 }}>SendGrid DNS records</div>
                </div>

                {sendgridDnsSaved && !sendgridDnsEditOpen ? (
                  <>
                    <div className="mb-2">
                      <Badge bg="success" className="me-1">DNS records set</Badge>
                      <span className="text-muted small">
                        {sendgridDnsSavedRecords.length} record{sendgridDnsSavedRecords.length !== 1 ? 's' : ''} applied to Route 53
                      </span>
                      {sendgridDnsSavedAt && (
                        <span className="text-muted small ms-2">
                          Last updated: {new Date(sendgridDnsSavedAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div className="small mb-2">
                      {sendgridDnsSavedRecords.map((rec, idx) => (
                        <div key={idx} className="text-muted" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {rec.type} &nbsp; {rec.host} → {rec.value}
                        </div>
                      ))}
                    </div>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => {
                        let records = sendgridDnsSavedRecords.length > 0
                          ? sendgridDnsSavedRecords
                          : parseSendgridDnsRecords(outboundDomainObj);
                        if (!records.length) {
                          records = [
                            { type: 'CNAME', host: '', value: '' },
                            { type: 'CNAME', host: '', value: '' },
                            { type: 'CNAME', host: '', value: '' },
                            { type: 'TXT', host: '', value: '' },
                          ];
                        }
                        setSendgridDnsRecords(records.map(r => ({ ...r })));
                        setSendgridDnsEditOpen(true);
                      }}
                    >
                      Edit records
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-muted small mb-2">
                      Get these from SendGrid → Settings → Sender Authentication → Authenticate Your Domain → Install DNS Records (Manual Setup). Paste Host and Value for each record, then click Update Route 53.
                    </p>
                    <div className="small mb-2">
                      {sendgridDnsRecords.map((rec, idx) => {
                        const recordType = String(rec.type || '').toUpperCase().replace(/CNAMI/, 'CNAME') || 'CNAME';
                        return (
                        <div key={idx} className="d-flex flex-wrap align-items-center gap-2 mb-2">
                          <Form.Select
                            size="sm"
                            style={{ minWidth: 100 }}
                            value={recordType === 'TXT' ? 'TXT' : 'CNAME'}
                            onChange={(e) => {
                              const next = [...sendgridDnsRecords];
                              const v = e.target.value;
                              next[idx] = { ...next[idx], type: v === 'TXT' ? 'TXT' : 'CNAME' };
                              setSendgridDnsRecords(next);
                            }}
                            title="Record type: CNAME or TXT"
                          >
                            <option value="CNAME">CNAME</option>
                            <option value="TXT">TXT</option>
                          </Form.Select>
                          <Form.Control
                            size="sm"
                            placeholder="Host (e.g. em9561.krosskastradio.com)"
                            value={rec.host}
                            onChange={(e) => {
                              const next = [...sendgridDnsRecords];
                              next[idx] = { ...next[idx], host: e.target.value };
                              setSendgridDnsRecords(next);
                            }}
                            style={{ minWidth: 200, flex: 1 }}
                          />
                          <Form.Control
                            size="sm"
                            placeholder="Value"
                            value={rec.value}
                            onChange={(e) => {
                              const next = [...sendgridDnsRecords];
                              next[idx] = { ...next[idx], value: e.target.value };
                              setSendgridDnsRecords(next);
                            }}
                            style={{ minWidth: 180, flex: 1 }}
                          />
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => setSendgridDnsRecords(sendgridDnsRecords.filter((_, i) => i !== idx))}
                          >
                            Remove
                          </Button>
                        </div>
                        );
                      })}
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="mb-2"
                        onClick={() => setSendgridDnsRecords([...sendgridDnsRecords, { type: 'CNAME', host: '', value: '' }])}
                      >
                        Add record
                      </Button>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={isSavingSendgridDns || !sendgridDnsRecords.some((r) => r.host && r.value)}
                        onClick={async () => {
                          const domain = outboundDomainObj?.domain;
                          if (!domain) return;
                          const records = sendgridDnsRecords
                            .filter((r) => r.host && r.value)
                            .map((r) => {
                              const t = String(r.type || '').toUpperCase().replace(/CNAMI/, 'CNAME');
                              return { type: t === 'TXT' ? 'TXT' : 'CNAME', host: r.host.trim(), value: r.value.trim() };
                            });
                          if (records.length === 0) {
                            toast.error('Add at least one record with Host and Value.');
                            return;
                          }
                          try {
                            const res = await configureSendgridDns({ domain, records, accountId: outboundDomainObj?.accountId }).unwrap();
                            if (res?.success) {
                              toast.success(res?.data?.message || 'SendGrid DNS records updated in Route 53.');
                              const savedAt = res?.data?.sendgridDnsConfiguredAt || new Date().toISOString();
                              setSendgridDnsSavedRecords(records);
                              setSendgridDnsSavedAt(savedAt);
                              setSendgridDnsSaved(true);
                              setSendgridDnsEditOpen(false);
                              setOutboundDomainObj((prev) => prev ? {
                                ...prev,
                                sendgridDnsRecords: JSON.stringify(records),
                                sendgridDnsConfiguredAt: savedAt,
                              } : prev);
                            } else {
                              toast.error(res?.message || 'Update failed.');
                            }
                          } catch (e) {
                            toast.error(e?.data?.message || e?.message || 'Failed to update Route 53.');
                          }
                        }}
                      >
                        {isSavingSendgridDns ? <><Spinner size="sm" className="me-1" /> Updating…</> : 'Update Route 53'}
                      </Button>
                      {sendgridDnsSaved && sendgridDnsEditOpen && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setSendgridDnsEditOpen(false)}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          )}

          {(String(outboundDomainType).toLowerCase() === 'cold_outreach' || String(outboundDomainType).toLowerCase() === 'business') && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Outbound Provider</Form.Label>
                <Form.Select
                  value={outboundProviderType}
                  onChange={(e) => setOutboundProviderType(e.target.value)}
                >
                  <option value="">Select a provider…</option>
                  {providerRegistry
                    .filter((p) => {
                      const pt = String(p.providerType || '').toUpperCase();
                      if (pt === 'POSTMARK') return false;
                      const isTransactional = String(outboundDomainType).toLowerCase() === 'business';
                      // Transactional: show common relays, plus any providers with active transactional pools.
                      if (isTransactional) {
                        if (pt === 'MAILGUN' || pt === 'MAILCHANNELS' || pt === 'SMTP_COM' || pt === 'SENDGRID' || pt === 'ELASTIC_EMAIL') return true;
                        return activeTransactionalPools.some((pool) => String(pool.providerType || '').toUpperCase() === pt);
                      }
                      // Cold outreach: show providers relevant to cold_outreach.
                      if (pt === 'MAILCHANNELS' || pt === 'SMTP_COM' || pt === 'SENDGRID' || pt === 'ELASTIC_EMAIL') return true;
                      return activeColdOutreachPools.some((pool) => String(pool.providerType || '').toUpperCase() === pt);
                    })
                    .map((p) => {
                      const pt = String(p.providerType || '').toUpperCase();
                      const status = String(p.status || '').toUpperCase();
                      const poolsForProvider = activePoolsForPurpose.filter(
                        (pool) => String(pool.providerType || '').toUpperCase() === pt
                      );
                      const providerDisplay = providerSelectionLabel(pt, poolsForProvider);
                      const labelBase =
                        status === 'ACTIVE'
                          ? `${providerDisplay} (Active)`
                          : status === 'PENDING'
                          ? `${providerDisplay} (Pending)`
                          : `${providerDisplay} (Not ready)`;
                      const label =
                        pt === 'ELASTIC_EMAIL' && poolsForProvider.length === 0 && status !== 'ACTIVE' && status !== 'PENDING'
                          ? `${providerDisplay} (Create relay account first)`
                          : labelBase;
                      return (
                        <option key={pt} value={pt}>
                          {label}
                        </option>
                      );
                    })}
                </Form.Select>
                <div className="text-muted small mt-1">
                  MailChannels will remain visible while pending; once it’s activated and seeded, it will become selectable for routing.
                </div>
              </Form.Group>
                {String(outboundProviderType || '').toUpperCase() === 'SENDGRID' && (
                  <Alert variant="info" className="mt-2 mb-0 py-2 small">
                    <strong>Twilio SendGrid</strong> requires additional domain authentication <em>per domain</em>. After you authenticate each domain in SendGrid (Settings → Sender Authentication → Authenticate Your Domain), paste the DNS records below and use &quot;Update Route 53&quot; so KG can write them for you.
                  </Alert>
                )}

              <Form.Group className="mb-3">
                <Form.Label>Primary Send Pool</Form.Label>
                <Form.Select
                  value={outboundPrimaryPoolId}
                  onChange={(e) => setOutboundPrimaryPoolId(e.target.value)}
                  disabled={!outboundProviderType || poolsForSelectedProvider.length === 0}
                >
                  <option value="">Select a pool…</option>
                  {poolsForSelectedProvider.map((p) => (
                      <option key={p.poolId} value={p.poolId}>
                        {poolSelectionLabel(p)}
                      </option>
                    ))}
                </Form.Select>
                <div className="text-muted small mt-1">
                  {outboundProviderType && poolsForSelectedProvider.length === 0
                    ? (String(outboundDomainType).toLowerCase() === 'business'
                      ? 'No ACTIVE transactional pools are seeded for this provider yet.'
                      : 'No ACTIVE cold-outreach pools are seeded for this provider yet.')
                    : (String(outboundDomainType).toLowerCase() === 'business'
                      ? 'Select the pool used for Transactional (e.g. Mailgun Primary). Pools are hot-swappable without redeploys.'
                      : 'Select the pool used for cold_outreach. Pools are hot-swappable without redeploys.')}
                  <div className="mt-1">
                    <strong>What it does:</strong> {String(outboundDomainType).toLowerCase() === 'business'
                      ? 'When you send transactional mail from this domain, KG relays it through this pool (e.g. Mailgun).'
                      : 'When you send cold-outreach mail from this domain, KG relays it through this pool\'s provider (SMTP.com, MailChannels, etc.). You can use one pool for many domains (e.g. “SMTP.com Primary”) or a pool per domain (e.g. “SMTP.com krosskastchannels.com”) if you want separate IP/reputation per brand.'}
                  </div>
                </div>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Check
                  type="checkbox"
                  checked={outboundApplySpf}
                  onChange={(e) => setOutboundApplySpf(e.target.checked)}
                  disabled={!Boolean(outboundDomainObj?.isInRoute53 && outboundDomainObj?.hostedZoneId)}
                  label="Auto-update SPF for the activated provider (Route 53 domains only)"
                />
                {!Boolean(outboundDomainObj?.isInRoute53 && outboundDomainObj?.hostedZoneId) && (
                  <div className="text-muted small mt-1">
                    Auto-SPF is disabled because KG can’t confirm the hosted zone ID/API control for this domain yet. You can still do guided/manual SPF updates.
                  </div>
                )}
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOutboundModal(false)} disabled={isSavingOutboundRoute || isSavingDomainType}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveOutboundRoute} disabled={isSavingOutboundRoute || isSavingDomainType}>
            {isSavingOutboundRoute || isSavingDomainType ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              (String(outboundDomainType).toLowerCase() === 'cold_outreach' || String(outboundDomainType).toLowerCase() === 'business') ? 'Save' : 'Save Purpose'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Domain Purpose is edited inside the Cold Outreach routing modal. */}

      {/* Hidden Domains Section */}
      {hiddenDomainsData?.data?.hiddenDomains?.length > 0 && (
        <Row className="mt-4">
          <Col>
            <Card className="border-warning">
              <Card.Header className="bg-warning text-dark">
                <h5 className="mb-0">
                  <Shield className="me-2" />
                  Protected Domains (Hidden for Safety)
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover>
                  <thead className="table-light">
                    <tr>
                      <th style={{ fontSize: '0.85rem' }}>Domain</th>
                      <th style={{ fontSize: '0.85rem' }}>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>
                              <div>Connected: domain is active in Kross Global.</div>
                              <div>Email Auth: DKIM/SPF/DMARC DNS status (shown separately).</div>
                            </Tooltip>
                          }
                        >
                          <span style={{ cursor: 'help' }}>Status</span>
                        </OverlayTrigger>
                      </th>
                      <th style={{ fontSize: '0.85rem' }}>Protection Reason</th>
                      <th style={{ fontSize: '0.85rem' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hiddenDomainsData.data.hiddenDomains.map((domain, index) => (
                      <tr key={index}>
                        <td>
                          <div className="d-flex align-items-center">
                            <Globe className="me-2 text-muted" />
                            <span className="fw-bold">{domain.identity}</span>
                          </div>
                        </td>
                        <td>
                          <Badge bg={domain.verificationStatus === 'Success' ? 'success' : 'warning'}>
                            {domain.verificationStatus}
                          </Badge>
                        </td>
                        <td>
                          <span className="text-muted small">
                            {domain.reason}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button variant="outline-secondary" size="sm" disabled>
                              <Shield className="me-1" />
                              Protected
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="p-3 bg-light border-top">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <small className="text-muted">
                        <strong>{hiddenDomainsData.data.protectionInfo.totalHidden}</strong> domains protected
                      </small>
                    </div>
                    <div>
                      <small className="text-muted">
                        Last updated: {toLocaleStringInTimezone(hiddenDomainsData.data.protectionInfo.lastUpdated, undefined, undefined, tenantTimezone)}
                      </small>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Modal show={showAllTenantDomainsModal} onHide={() => setShowAllTenantDomainsModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <Clipboard className="me-2" />
            All domains ({allTenantDomainNames.length})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-2">
            Every domain registered in Kross Global across all connected AWS accounts (one per line). This list ignores the account filter above.
          </p>
          <Form.Control
            as="textarea"
            readOnly
            value={allTenantDomainsPlainText}
            rows={Math.min(18, Math.max(8, allTenantDomainNames.length))}
            className="font-monospace"
            style={{ fontSize: '0.85rem', resize: 'vertical' }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAllTenantDomainsModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={copyAllTenantDomainsToClipboard} disabled={!allTenantDomainsPlainText}>
            <Clipboard className="me-1" size={14} />
            Copy all
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Domain Modal */}
      <Modal show={showAddModal} onHide={() => {
        setShowAddModal(false);
        setNewDomain('');
        setSelectedAccountForDomain('');
        setMxAcknowledged(false);
        setDomainSearchResult(null);
        setRoute53Result(null);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>
            <Globe className="me-2" />
            Add New Domain
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <Shield className="me-2" />
            <strong>Domain Verification</strong><br/>
            The domain will be checked to ensure it exists and is registered. Only domains you own can be added.
          </Alert>

          <Alert variant="warning">
            <strong>Important: Email Delivery Change</strong>
            <div className="mt-2" style={{ fontSize: '0.92rem' }}>
              Changing your domain’s <strong>root MX records</strong> will immediately route all inbound email for this domain to Kross Global (Postmark → Unibox).
              If your domain currently receives email via Google Workspace, Microsoft 365, Zoho, or another provider, those inboxes may stop receiving new messages once MX is updated.
            </div>
            <div className="mt-2" style={{ fontSize: '0.92rem' }}>
              If you need a different setup (hybrid delivery / existing mailboxes), please contact support before proceeding.
            </div>
            <Form.Check
              className="mt-3"
              type="checkbox"
              id="mx-acknowledge"
              label="I understand that changing MX records will affect existing email delivery"
              checked={mxAcknowledged}
              onChange={(e) => setMxAcknowledged(e.target.checked)}
            />
          </Alert>
          
          {/* Host Provider Selector */}
          <Form.Group className="mb-3">
            <Form.Label>Select Hosting Provider <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={selectedHostProvider}
              onChange={(e) => setSelectedHostProvider(e.target.value)}
              required
            >
              <option value="aws">AWS</option>
              <option value="mailgun">Mailgun</option>
              <option value="mailreach">Mailreach</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Choose which hosting provider manages this domain (where the domain is hosted/managed).
            </Form.Text>
          </Form.Group>

          {/* Account Selector - Show for AWS, hide for other providers for now */}
          {selectedHostProvider === 'aws' && isAllAccountsSelected && (
            <Form.Group className="mb-3">
              <Form.Label>Select Account <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={selectedAccountForDomain}
                onChange={(e) => setSelectedAccountForDomain(e.target.value)}
                required
              >
                <option value="">-- Select an account --</option>
                {allAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.alias || account.name} ({account.id})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Choose which AWS account this domain should be added to.
              </Form.Text>
            </Form.Group>
          )}

          {/* Legacy provider removed */}

          {/* Inbound (MX) Provider (system-owned) */}
          <Form.Group className="mb-3">
            <Form.Label>Inbound Provider (MX Records)</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Badge bg="primary">Postmark (Default)</Badge>
              <span className="text-muted">Configurable later per domain (Postmark / SES)</span>
            </div>
          </Form.Group>
          
          <Form onSubmit={(e) => { e.preventDefault(); handleSearchDomain(); }}>
            <Form.Group className="mb-3">
              <Form.Label>Domain Name</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="example.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  required
                  disabled={isSearchingDomain}
                />
                <Button 
                  variant="primary" 
                  onClick={handleSearchDomain}
                  disabled={isSearchingDomain || !newDomain.trim() || (isAllAccountsSelected && !selectedAccountForDomain)}
                >
                  {isSearchingDomain ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="me-1" />
                      Search Domain
                    </>
                  )}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                Enter the domain you want to use for email sending (without www)
              </Form.Text>
            </Form.Group>
          </Form>

          {/* Domain Search Results */}
          {domainSearchResult && (
            <div className="mt-4">
              
              {/* Only show this alert if status is NOT in_route53 (we have a better one below with action buttons) */}
              {domainSearchResult.status !== 'in_route53' && (
                <Alert variant={
                  (domainSearchResult.status === 'in_aws_account' || domainSearchResult.status === 'owned' || domainSearchResult.status === 'verified_in_ses') ? 'success' :
                  domainSearchResult.status === 'registered' ? 'warning' :
                  domainSearchResult.status === 'available' ? 'info' : 'secondary'
                }>
                  <h6 className="mb-2">
                    {domainSearchResult.status === 'in_aws_account' && '✅ Domain Already in AWS Account'}
                    {domainSearchResult.status === 'verified_in_ses' && '✅ Domain Already in AWS Account'}
                    {domainSearchResult.status === 'owned' && '✅ Domain Already in AWS Account'}
                    {domainSearchResult.status === 'registered' && '⚠️ Domain is Registered (Not in AWS)'}
                    {domainSearchResult.status === 'available' && '🛒 Domain is Available for Purchase'}
                    {domainSearchResult.status === 'needs_claiming' && '🔗 Domain Needs Claiming'}
                  </h6>
                  <p className="mb-2">{domainSearchResult.message}</p>
                  {domainSearchResult.ipAddress && (
                    <small className="text-muted">IP Address: {domainSearchResult.ipAddress}</small>
                  )}
                  {domainSearchResult.awsStatus && (
                    <div className="mt-2">
                      <small className="text-muted">
                        <strong>AWS Status:</strong> {domainSearchResult.awsStatus}<br/>
                        <strong>Verification:</strong> {domainSearchResult.verificationStatus}<br/>
                        <strong>DKIM:</strong> {domainSearchResult.dkimStatus} | 
                        <strong>SPF:</strong> {domainSearchResult.spfStatus} | 
                        <strong>DMARC:</strong> {domainSearchResult.dmarcStatus}
                      </small>
                    </div>
                  )}
                </Alert>
              )}

              {/* Route 53 Ownership Results - Only show if domain is not already in AWS or Route 53 */}
              {!domainSearchResult.status || (domainSearchResult.status !== 'verified_in_ses' && domainSearchResult.status !== 'in_aws_account' && domainSearchResult.status !== 'owned' && domainSearchResult.status !== 'in_route53') ? (
                <>
                  {isSearchingDomain && domainSearchResult && domainSearchResult.status === 'registered' && !route53Result && (
                    <Alert variant="info" className="mt-3">
                      <Spinner size="sm" className="me-2" />
                      <strong>Checking Route 53 ownership...</strong>
                    </Alert>
                  )}
                  
                  {route53Result && (
                    <Alert variant={route53Result.inRoute53 ? 'success' : 'warning'} className="mt-3">
                      <h6 className="mb-2">
                        {route53Result.inRoute53 ? '✅ Domain Found in Your Route 53' : '⚠️ Domain Not in Your Route 53'}
                      </h6>
                      <p className="mb-2">{route53Result.message}</p>
                      {route53Result.hostedZones && route53Result.hostedZones.length > 0 && (
                        <small className="text-muted">
                          Hosted Zones: {route53Result.hostedZones.map(zone => zone.name).join(', ')}
                        </small>
                      )}
                    </Alert>
                  )}
                </>
              ) : null}

              {/* Action Buttons Based on Status */}
        {(domainSearchResult.status === 'in_aws_account' || domainSearchResult.status === 'owned' || domainSearchResult.status === 'verified_in_ses' || domainSearchResult.status === 'in_route53') && (
          <div className="w-100">
            <Alert variant="success" className="mb-3">
              <CheckCircle className="me-2" />
              <strong>
                {domainSearchResult.status === 'in_route53' && '✅ Domain Registered in Route 53!'}
                  {domainSearchResult.status === 'verified_in_ses' && '✅ Domain Already in AWS Account!'}
                {domainSearchResult.status === 'in_aws_account' && '✅ Domain Already in AWS Account!'}
                {domainSearchResult.status === 'owned' && '✅ Domain Owned by AWS Account!'}
              </strong><br/>
              {domainSearchResult.status === 'in_route53' 
                ? 'This domain is registered in Route 53 for the selected account. You can add it to AWS to start sending emails.'
                : domainSearchResult.status === 'verified_in_ses' 
                ? 'This domain is already managed in AWS. You can add it to Domain Manager to configure SPF, DKIM, and DMARC.'
                : 'This domain is owned by your AWS account and ready to be added to your domain manager.'}
            </Alert>

            {/* Informational message about what will be created automatically */}
            {domainSearchResult.status === 'in_route53' && (
              <Alert variant="info" className="mb-3">
                <InfoCircle className="me-2" />
                <strong>What will be created automatically:</strong>
                <ul className="mb-0 mt-2">
                  <li><strong>Route 53 Hosted Zone</strong> - If one doesn't exist, it will be created automatically</li>
                  {selectedHostProvider === 'aws' && (
                    <li>
                      <strong>DNS Setup Records</strong> - We will generate the SPF/DKIM/DMARC records for you (apply automatically if this domain is in your AWS account).
                    </li>
                  )}
                  <li>
                    <strong>MX Record</strong> - Default inbound is Postmark; MX should point to <code>inbound.postmarkapp.com</code> (you can switch inbound to SES later if AWS is connected).
                  </li>
                </ul>
                <small className="text-muted mt-2 d-block">
                  Domain setup will complete automatically once DNS propagates (usually within a few minutes).
                </small>
              </Alert>
            )}
            
            <div className="d-flex gap-2">
              <Button 
                variant="success" 
                onClick={handleAddToDomainManager}
                disabled={isAddingToManager}
              >
                {isAddingToManager ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="me-1" />
                    {domainSearchResult.status === 'in_route53' 
                      ? 'Add Domain'
                      : 'Add to Domain Manager'}
                  </>
                )}
              </Button>
              <Button 
                variant="outline-secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setNewDomain('');
                  setSelectedAccountForDomain('');
                  setDomainSearchResult(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {domainSearchResult.status === 'registered' && (
          <div className="w-100">
            <Card className="mb-3">
              <Card.Header>
                <h6 className="mb-0">🔗 Claim Domain Instructions</h6>
              </Card.Header>
              <Card.Body>
                <p>To claim this domain, add the following TXT record to your domain's DNS settings:</p>
                <div className="bg-light p-3 rounded mb-3">
                  <strong>Record Type:</strong> TXT<br/>
                  <strong>Name:</strong> @ (or leave blank for root domain)<br/>
                  <strong>Value:</strong> <code>{route53Result?.verificationCode || `kross-global-claim-${Date.now()}`}</code><br/>
                  <strong>TTL:</strong> 3600 (or default)
                </div>
                {route53Result?.hostedZones && route53Result.hostedZones.length > 0 && (
                  <Alert variant="success" className="mb-2">
                    <strong>✅ Domain Found in Route 53:</strong> {route53Result.hostedZones.map(zone => zone.name).join(', ')}
                  </Alert>
                )}
                <Alert variant="info" className="mb-0">
                  <strong>💡 Steps:</strong>
                  <ol className="mb-0 mt-2">
                    <li>Log into your domain host (GoDaddy, Namecheap, etc.)</li>
                    <li>Go to DNS Management / DNS Records</li>
                    <li>Add a new TXT record with the value above</li>
                    <li>Wait 5-60 minutes for DNS propagation</li>
                    <li>Click "Claim Domain" below to verify</li>
                  </ol>
                </Alert>
              </Card.Body>
            </Card>
            
            <div className="d-flex gap-2">
              <Button 
                variant="primary" 
                onClick={handleClaimDomain}
                disabled={isClaiming}
              >
                {isClaiming ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Checking...
                  </>
                ) : (
                  <>
                    <Shield className="me-1" />
                    Claim Domain
                  </>
                )}
              </Button>
              <Button 
                variant="outline-secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setNewDomain('');
                  setSelectedAccountForDomain('');
                  // inbound provider is system-owned (Postmark); nothing to reset
                  setDomainSearchResult(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {domainSearchResult.status === 'available' && (
          <div className="w-100">
            <Alert variant="info" className="mb-3">
              <InfoCircle className="me-2" />
              <strong>🛒 Domain is Available for Purchase</strong><br/>
              This domain is available for purchase through your domain registrar.
            </Alert>
            
            <div className="d-flex gap-2">
              <Button 
                variant="outline-primary"
                onClick={() => {
                  setShowAddModal(false);
                  setNewDomain('');
                  setSelectedAccountForDomain('');
                  // inbound provider is system-owned (Postmark); nothing to reset
                  setDomainSearchResult(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}


              {domainSearchResult.status === 'available' && (
                <div className="w-100">
                  <Alert variant="info" className="mb-3">
                    <Globe className="me-2" />
                    <strong>🛒 Domain is Available for Purchase!</strong><br/>
                    This domain is not registered and can be purchased.
                  </Alert>
                  
                  <div className="bg-light p-3 rounded mb-3">
                    <h6 className="mb-2">💰 Purchase Information:</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <strong>Domain:</strong> {domainSearchResult.domain}<br/>
                        <strong>Price:</strong> ${domainSearchResult.price || '12.99'}/year<br/>
                        <strong>Registrar:</strong> {domainSearchResult.registrar || 'GoDaddy'}
                      </div>
                      <div className="col-md-6">
                        <strong>Includes:</strong><br/>
                        • Domain registration<br/>
                        • DNS management<br/>
                        • Email forwarding<br/>
                        • SSL certificate
                      </div>
                    </div>
                  </div>
                  
                  <div className="d-flex gap-2">
                    <Button 
                      variant="success" 
                      size="lg"
                      onClick={() => {
                        // Handle domain purchase
                        alert('Domain purchase integration coming soon!');
                      }}
                    >
                      <Globe className="me-1" />
                      Purchase Domain (${domainSearchResult.price || '12.99'})
                    </Button>
                    <Button 
                      variant="outline-secondary"
                      onClick={() => {
                        setShowAddModal(false);
                        setDomainSearchResult(null);
                        setNewDomain('');
                        // inbound provider is system-owned (Postmark); nothing to reset
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {domainSearchResult.status === 'needs_claiming' && (
                <div className="d-flex gap-2">
                  <Button variant="warning" disabled>
                    <Shield className="me-1" />
                    Claim this Domain (Add TXT Record)
                  </Button>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowAddModal(false);
            setNewDomain('');
            setSelectedAccountForDomain('');
            // inbound provider is system-owned (Postmark); nothing to reset
            setDomainSearchResult(null);
          }}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DNS Setup Modal */}
      <Modal show={showDNSSetup} onHide={handleCloseDNSSetup} size="xl" fullscreen="lg-down">
        <Modal.Header closeButton>
          <Modal.Title>
            <Globe className="me-2" />
            DNS Setup for {selectedDomain}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedDomain && (() => {
            // Find domain object to get accountId
            const domainObj = allDomains.find(d => d.domain === selectedDomain);
            const hostProvider = typeof window !== 'undefined' ? sessionStorage.getItem('dnsSetupHostProvider') || 'aws' : 'aws';
            return (
              <DNSSetup 
                domain={selectedDomain} 
                onClose={handleCloseDNSSetup}
                hostProvider={hostProvider}
                accountId={domainObj?.accountId || null}
              />
            );
          })()}
        </Modal.Body>
      </Modal>

      {/* Domain Confirmation Modal */}
      <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <Shield className="me-2 text-warning" />
            Confirm Domain Addition
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <Shield className="me-2" />
            <strong>⚠️ SAFETY WARNING</strong><br/>
            This will create new DNS records and modify your AWS configuration.
          </Alert>
          
          <p><strong>Domain to add:</strong> {newDomain}</p>
          
          <Alert variant="info">
            <strong>🔍 What will happen:</strong>
            <ul className="mb-0 mt-2">
              <li>Domain will be added to Domain Manager</li>
              <li>DNS verification records will be created</li>
              <li>Email sending will be enabled for this domain</li>
              <li>This action cannot be undone</li>
            </ul>
          </Alert>

          <Form.Group className="mt-3">
            <Form.Label>
              <strong>Type "CONFIRM" to proceed:</strong>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="CONFIRM"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              required
            />
            <Form.Text className="text-muted">
              You must type exactly "CONFIRM" to proceed with domain addition.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmationModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={handleConfirmDomain}
            disabled={isConfirming || confirmationText !== 'CONFIRM'}
          >
            {isConfirming ? (
              <>
                <Spinner size="sm" className="me-2" />
                Confirming...
              </>
            ) : (
              <>
                <Shield className="me-1" />
                Confirm Domain Addition
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Inbound Provider (Postmark / SES) */}
      <Modal show={!!inboundProviderModalDomain} onHide={() => setInboundProviderModalDomain(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Inbound Provider</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <strong>Inbound only.</strong> This changes where your domain receives inbound mail (MX). Outbound sending is unaffected.
          </Alert>

          <div className="mb-2">
            <strong>Domain:</strong> {inboundProviderModalDomain?.domain || ''}
          </div>

          <Form>
            <Form.Check
              type="radio"
              id="inbound-provider-postmark"
              name="inboundProvider"
              label="Postmark (Inbound only)"
              value="postmark"
              checked={inboundProviderSelection === 'postmark'}
              onChange={() => setInboundProviderSelection('postmark')}
            />
            <Form.Check
              type="radio"
              id="inbound-provider-ses"
              name="inboundProvider"
              label="SES (Inbound only)"
              value="ses"
              checked={inboundProviderSelection === 'ses'}
              disabled={!inboundProviderSesAllowed}
              onChange={() => setInboundProviderSelection('ses')}
            />
            {!inboundProviderSesAllowed && (
              <Form.Text className="text-muted">
                SES requires AWS connected (bind the domain to an AWS account / Route53) before it can be enabled.
              </Form.Text>
            )}
          </Form>

          <hr />

          {(() => {
            const domainName = inboundProviderModalDomain?.domain || '';
            const domainKey = normalizeDomainKey(domainName);
            const isAwsHosted = Boolean(inboundProviderModalDomain?.isInRoute53) || Boolean(inboundProviderModalDomain?.hostedZoneId);
            const region = inboundProviderModalDomain?.region || selectedAccount?.region || 'us-east-1';
            const providerLabel = inboundProviderSelection === 'ses' ? 'SES' : 'Postmark';
            const isBusy = settingUpReceivingForDomainName === domainName;
            const currentResult = inboundProviderRecentResults[domainKey] || null;
            const mxMatchesExpectedPublic = currentResult?.mxMatchesExpectedPublic === true;
            const observedPublic = Array.isArray(currentResult?.mxRecordsObservedPublic) ? currentResult.mxRecordsObservedPublic : [];
            const mxUpdated = currentResult?.mxUpdated === true;
            const resultProviderRaw = String(currentResult?.inboundProvider || '').trim().toLowerCase();
            const expectedMxValue = inboundProviderSelection === 'ses'
              ? `inbound-smtp.${region}.amazonaws.com.`
              : 'inbound.postmarkapp.com.';
            const expectedRecordFromResult =
              currentResult && resultProviderRaw === inboundProviderSelection && typeof currentResult.mxRecordExpected === 'string'
                ? currentResult.mxRecordExpected
                : null;
            const expectedRecord = expectedRecordFromResult || `10 ${expectedMxValue}`;
            const sesResult = currentResult && resultProviderRaw === 'ses' ? currentResult : null;
            const sesIdentityStatusRaw = String(sesResult?.sesIdentityStatus || '').trim().toLowerCase();
            const sesIdentityVerified = sesResult?.sesIdentityVerified === true || sesIdentityStatusRaw === 'verified';
            const sesIdentityPending = Boolean(sesResult) && !sesIdentityVerified && !['error'].includes(sesIdentityStatusRaw);
            const sesIdentityError = sesIdentityStatusRaw === 'error';
            const sesTxtRecordExpected =
              typeof sesResult?.sesVerificationTxtRecordExpected === 'string'
                ? sesResult.sesVerificationTxtRecordExpected
                : null;
            const sesTxtObservedPublic = Array.isArray(sesResult?.sesVerificationTxtObservedPublic)
              ? sesResult.sesVerificationTxtObservedPublic
              : [];
            const sesTxtMatchesExpectedPublic = sesResult?.sesVerificationTxtMatchesExpectedPublic === true;
            const sesTxtApplied = sesResult?.sesVerificationTxtApplied === true;
            const sesIdentityMessage = String(sesResult?.sesIdentityMessage || '').trim();

            const inferActiveInboundProvider = () => {
              for (const r of observedPublic || []) {
                const ex = String(r?.exchange || '').toLowerCase();
                if (ex.includes('inbound.postmarkapp.com')) return 'postmark';
                if (ex.includes('inbound-smtp.') && ex.includes('.amazonaws.com')) return 'ses';
              }
              return '';
            };
            const activeProvider = inferActiveInboundProvider();

            return (
              <>
                <div className="mb-2">
                  <strong>MX record to set:</strong>
                </div>
                <div className="mb-3">
                  <code>{expectedRecord}</code>
                </div>

                <Form.Check
                  type="checkbox"
                  id="inbound-apply-mx"
                  label="Apply MX automatically (Route53 only)"
                  checked={inboundProviderApplyMx}
                  disabled={!isAwsHosted}
                  onChange={(e) => setInboundProviderApplyMx(Boolean(e.target.checked))}
                />
                {!isAwsHosted && (
                  <Form.Text className="text-muted d-block">
                    Manual DNS required (not hosted in Route53).
                  </Form.Text>
                )}

                {currentResult && (
                  <Alert
                    variant={mxMatchesExpectedPublic ? 'success' : (mxUpdated ? 'info' : 'warning')}
                    className="mt-3 mb-0"
                  >
                    {activeProvider && activeProvider !== inboundProviderSelection && (
                      <div className="mb-2">
                        <strong>Selected provider:</strong> {providerLabel}{' '}
                        <span className="text-muted">/</span>{' '}
                        <strong>Active MX:</strong> {activeProvider === 'ses' ? 'SES' : 'Postmark'}
                      </div>
                    )}

                    {mxMatchesExpectedPublic
                      ? `Active (public DNS verified for ${providerLabel}).`
                      : mxUpdated
                        ? `Propagating (Route53 updated; waiting for public DNS to match ${providerLabel}).`
                        : `Pending DNS (public MX does not match ${providerLabel} yet).`}
                    {!mxMatchesExpectedPublic && observedPublic.length > 0 && (
                      <div className="mt-2" style={{ fontSize: '0.85rem' }}>
                        <div><strong>Observed MX:</strong></div>
                        <div style={{ fontFamily: 'monospace' }}>
                          {observedPublic.slice(0, 5).map((r, idx) => (
                            <div key={idx}>{`${r.priority} ${r.exchange}`}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Alert>
                )}

                {inboundProviderSelection === 'ses' && (() => {
                  // When the user hasn't clicked Verify yet, surface the most recent
                  // status from the global Domain Health Monitor so the modal isn't
                  // misleading ("Not checked yet" even when the identity is verified).
                  const monitorEntry = !sesResult
                    ? domainHealthMap.get(String(domainName || '').trim().toLowerCase())
                    : null;
                  const monitorVerified = monitorEntry?.details?.sesIdentityVerified === true;
                  const monitorNotVerified = monitorEntry?.details?.sesIdentityVerified === false;
                  const monitorDetail = String(monitorEntry?.details?.sesIdentityDetail || '').trim();
                  const monitorOwningAccount = String(
                    monitorEntry?.details?.sesIdentityOwningAccount || ''
                  ).trim();
                  const monitorLastChecked = monitorEntry?.lastChecked || domainHealthMonitoringData?.timestamp || null;
                  const variant = sesResult
                    ? (sesIdentityVerified ? 'success' : (sesIdentityError ? 'danger' : 'warning'))
                    : monitorVerified
                      ? 'success'
                      : monitorNotVerified
                        ? 'danger'
                        : 'secondary';
                  const headlineLabel = sesResult
                    ? (sesIdentityVerified ? 'Verified' : sesIdentityError ? 'Error' : 'Pending verification')
                    : monitorVerified
                      ? 'Verified (per Domain Health Monitor)'
                      : monitorNotVerified
                        ? 'Not verified (per Domain Health Monitor)'
                        : 'Not checked yet.';
                  return (
                  <Alert
                    variant={variant}
                    className="mt-3 mb-0"
                  >
                    <div className="mb-1">
                      <strong>SES identity:</strong>{' '}
                      {headlineLabel}
                    </div>
                    {!sesResult ? (
                      monitorEntry ? (
                        <div style={{ fontSize: '0.9rem' }}>
                          {monitorDetail || (monitorVerified
                            ? 'SES domain identity is verified for inbound mail.'
                            : 'SES domain identity needs attention.')}
                          {monitorOwningAccount && (
                            <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
                              Owning AWS account: <code>{monitorOwningAccount}</code>
                            </div>
                          )}
                          {monitorLastChecked && (
                            <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                              Last checked: {monitorLastChecked}
                            </div>
                          )}
                          <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
                            Click <strong>Verify</strong> for a fresh live check, or <strong>Save</strong> to (re)apply settings.
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.9rem' }}>
                          Click <strong>Verify</strong> or <strong>Save</strong> to check/create SES domain identity.
                        </div>
                      )
                    ) : (
                      <>
                        <div style={{ fontSize: '0.9rem' }}>
                          {sesIdentityMessage || (sesIdentityVerified ? 'SES domain identity is verified.' : 'SES domain identity is pending DNS verification.')}
                          {sesTxtApplied ? ' TXT was applied in Route53.' : ''}
                        </div>
                        {!sesIdentityVerified && sesTxtRecordExpected && (
                          <div className="mt-2">
                            <div><strong>SES TXT record to set:</strong></div>
                            <code>{sesTxtRecordExpected}</code>
                          </div>
                        )}
                        {!sesIdentityVerified && sesTxtObservedPublic.length > 0 && !sesTxtMatchesExpectedPublic && (
                          <div className="mt-2" style={{ fontSize: '0.85rem' }}>
                            <div><strong>Observed SES TXT:</strong></div>
                            <div style={{ fontFamily: 'monospace' }}>
                              {sesTxtObservedPublic.slice(0, 5).map((value, idx) => (
                                <div key={idx}>{value}</div>
                              ))}
                            </div>
                          </div>
                        )}
                        {sesIdentityPending && !sesTxtRecordExpected && (
                          <div className="mt-2" style={{ fontSize: '0.85rem' }}>
                            SES verification token is pending. Try Verify again in a few seconds.
                          </div>
                        )}
                      </>
                    )}
                  </Alert>
                  );
                })()}

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button
                    variant="outline-secondary"
                    disabled={!domainName || isBusy}
                    onClick={() => {
                      if (!domainName) return;
                      handleSetupReceivingForDomain(domainName, { provider: inboundProviderSelection, applyMx: false });
                    }}
                  >
                    {isBusy ? '…' : 'Verify'}
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!domainName || isBusy || (inboundProviderSelection === 'ses' && !inboundProviderSesAllowed)}
                    onClick={() => {
                      if (!domainName) return;
                      handleSetupReceivingForDomain(domainName, {
                        provider: inboundProviderSelection,
                        applyMx: isAwsHosted ? inboundProviderApplyMx : false,
                      });
                    }}
                  >
                    {isBusy ? 'Saving…' : (inboundProviderApplyMx && isAwsHosted ? 'Save + Apply MX' : 'Save')}
                  </Button>
                </div>
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setInboundProviderModalDomain(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Domain Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete}>
        <Modal.Header closeButton>
          <Modal.Title>
            <XCircle className="me-2 text-danger" />
            Delete Domain from App
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success">
            <Shield className="me-2" />
            <strong>✅ Safe App-Only Operation</strong><br/>
            This will only delete the domain from this app - your AWS account and domain hosting remain completely untouched.
          </Alert>
          
          <p>
            Are you sure you want to delete the domain <strong>{domainToDelete}</strong> from this app?
          </p>
          
          <div className="text-muted">
            <small>
              This will:
              <ul className="mb-0 mt-2">
                <li>Remove the domain from this app's interface</li>
                <li>Delete app-only tracking and analytics</li>
                <li>Keep DNS hosting and registrar ownership unchanged</li>
                <li>Preserve all DNS records and email sending capability</li>
                <li>Keep the domain in your domain registrar/hosting</li>
                <li>Allow you to re-add it to the app later if needed</li>
              </ul>
            </small>
          </div>
          
          <Alert variant="info" className="mt-3">
            <strong>🔒 Infrastructure Protection:</strong> This operation will not touch your DNS hosting, registrar, or external mail providers.
          </Alert>

          <Alert variant="danger" className="mt-3">
            <strong>☠️ Permanent deletion (advanced)</strong>
            <div className="mt-2" style={{ fontSize: '0.9rem' }}>
              If you need to permanently wipe this domain’s data <strong>only for this specific domain</strong> (domain record, email accounts, and all email data),
              use Hard Delete. This cannot be undone.
            </div>
            <div className="mt-2">
              <Button variant="outline-danger" size="sm" onClick={handleOpenHardDelete}>
                Hard delete domain + all email data…
              </Button>
            </div>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting from App...
              </>
            ) : (
              <>
                <XCircle className="me-1" />
                Delete from App
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Hard Delete Domain (permanent) */}
      <Modal show={showHardDeleteModal} onHide={handleCancelHardDelete}>
        <Modal.Header closeButton>
          <Modal.Title>
            <XCircle className="me-2 text-danger" />
            Hard delete domain + all email data
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>⚠️ This is irreversible.</strong>
            <div className="mt-2">
              This will permanently delete <strong>only data associated to</strong>:
              <div className="mt-1"><strong>{domainToDelete}</strong></div>
              <ul className="mb-0 mt-2">
                <li>Domain Manager record for this domain</li>
                <li>All email accounts under this domain</li>
                <li>All email records under this domain (domain-scoped DynamoDB table)</li>
                <li>Best-effort deletion of raw email objects referenced by those records (S3)</li>
              </ul>
              <div className="mt-2">
                <strong>Protected domains are blocked server-side.</strong>
              </div>
            </div>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Type to confirm</Form.Label>
            <Form.Control
              type="text"
              placeholder={`DELETE ${domainToDelete} PERMANENTLY`}
              value={hardDeleteConfirmText}
              onChange={(e) => setHardDeleteConfirmText(e.target.value)}
            />
            <Form.Text className="text-muted">
              You must type exactly: <code>{`DELETE ${domainToDelete} PERMANENTLY`}</code>
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelHardDelete} disabled={isHardDeleting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmHardDelete}
            disabled={isHardDeleting || hardDeleteConfirmText.trim() !== `DELETE ${domainToDelete} PERMANENTLY`}
          >
            {isHardDeleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting permanently…
              </>
            ) : (
              <>
                <XCircle className="me-1" />
                Hard delete permanently
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Help Sidebar */}
      <Offcanvas
        show={showHelpModal}
        onHide={() => setShowHelpModal(false)}
        placement="end"
        style={{ width: '400px' }}
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="d-flex align-items-center">
            <QuestionCircle className="me-2 text-primary" size={20} />
            <span className="fw-bold">Domain Setup Guide</span>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          <Alert variant="info" className="mb-4">
            <strong>Required Order:</strong> These steps must be completed in sequence. Each button becomes clickable only after the previous step is complete. This ensures proper DNS record setup and prevents errors.
          </Alert>

          <h5 className="mb-3">Step-by-Step Setup Process</h5>
          
          <ListGroup variant="flush" className="mb-4">
            <ListGroup.Item>
              <div className="d-flex align-items-start">
                <Badge bg="primary" className="me-3 mt-1" style={{ fontSize: '1rem', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</Badge>
                <div className="flex-grow-1">
                  <h6 className="mb-2">
                    <Shield className="me-2" />
                    Verify (First)
                  </h6>
                  <p className="mb-1">
                    <strong>What it does (DNS-only):</strong>
                    <ol className="mb-0 mt-2 small">
                      <li><strong>Creates DNS records in Route 53</strong> – SPF, DMARC, and provider DKIM (e.g. SMTP.com) using the default Route53 client</li>
                      <li><strong>DNS-only auth</strong> – The Auth modal writes SPF/DKIM/DMARC records in Route 53 only; outbound is via your chosen relay (e.g. SMTP.com)</li>
                    </ol>
                  </p>
                  <p className="mb-0 text-muted small">
                    <strong>Why first:</strong> This step writes the foundational email authentication records (SPF, DKIM, DMARC) to Route 53 so your domain is ready for sending. For domains in the current AWS account, no AWS account table entry is required. <strong>After Apply, use Verify to confirm records are live.</strong>
                  </p>
                </div>
              </div>
            </ListGroup.Item>

            <ListGroup.Item>
              <div className="d-flex align-items-start">
                <Badge bg="success" className="me-3 mt-1" style={{ fontSize: '1rem', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</Badge>
                <div className="flex-grow-1">
                  <h6 className="mb-2">
                    <Shield className="me-2" />
                    Enable DKIM (Second)
                  </h6>
                  <p className="mb-1">
                    <strong>What it does:</strong> Adds provider DKIM records to Route 53 (for example SMTP.com or Mailgun records). DKIM helps prove your emails are legitimate.
                  </p>
                  <p className="mb-0 text-muted small">
                    <strong>Why second:</strong> After SPF and DMARC are in place (from Verify), DKIM adds cryptographic signing. <strong>Verify first, then apply DKIM if your provider supplies a DKIM key.</strong>
                  </p>
                </div>
              </div>
            </ListGroup.Item>

            <ListGroup.Item>
              <div className="d-flex align-items-start">
                <Badge bg="warning" className="me-3 mt-1" style={{ fontSize: '1rem', width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</Badge>
                <div className="flex-grow-1">
                  <h6 className="mb-2">
                    <Shield className="me-2" />
                    Configure Mail From (Third)
                  </h6>
                  <p className="mb-1">
                    <strong>What it does:</strong> Sets up a custom MAIL FROM domain (e.g., mail.yourdomain.com) for SPF alignment. Essential for deliverability.
                  </p>
                  <p className="mb-0 text-muted small">
                    <strong>Why third:</strong> Mail From requires SPF and DKIM in place first (from Verify). This step ensures SPF alignment for deliverability. <strong>Complete Verify and DKIM first.</strong>
                  </p>
                </div>
              </div>
            </ListGroup.Item>
          </ListGroup>

          <Alert variant="warning" className="mb-3">
            <h6 className="mb-2">⚠️ Important: You Must Wait Between Steps</h6>
            <p className="mb-0">
              <strong>Each button is disabled until the previous step completes.</strong> This is by design to ensure proper DNS record setup and prevent errors. The system will:
              <ul className="mb-0 mt-2">
                <li><strong>Disable "Enable DKIM"</strong> until Verify shows "Verified"</li>
                <li><strong>Disable "Configure Mail From"</strong> until Enable DKIM shows "Verified"</li>
                <li>Show processing status for each operation independently</li>
              </ul>
              <strong>Why wait?</strong>
              <ul className="mb-0 mt-2">
                <li><strong>Verify creates base DNS records</strong> (SPF, DMARC, verification token) that Enable DKIM needs</li>
                <li><strong>Enable DKIM needs the domain to be verified first</strong> - it won't work if Verify is still pending</li>
                <li><strong>Configure Mail From needs SPF records from Verify</strong> and DKIM to be enabled - it won't work if previous steps aren't complete</li>
              </ul>
              <strong>If you try to click them out of order:</strong> The buttons will be disabled (grayed out) and you'll see a tooltip explaining why. This prevents errors and stuck badges.
            </p>
          </Alert>

          <Alert variant="warning" className="mb-3">
            <h6 className="mb-2">⏱️ Processing Times</h6>
            <p className="mb-0">
              All three processes take <strong>5-60 minutes</strong> to complete after DNS records are created. The system automatically polls for status updates every 30 seconds, so you'll see the status update automatically when verification completes. You can refresh the page at any time - the processing state will persist.
            </p>
          </Alert>

          <Alert variant="success" className="mb-3">
            <h6 className="mb-2">💡 Tips</h6>
            <ul className="mb-0">
              <li>Hover over any button or badge to see detailed tooltips with current status</li>
              <li>The spinner badges (Enabling..., Verifying..., Processing Mail From) indicate active processes</li>
              <li>All processing states persist across page refreshes</li>
              <li>Buttons are disabled (grayed out) until the previous step completes - this prevents errors</li>
              <li>If a button is disabled, hover over it to see why (e.g., "Verify must complete first")</li>
            </ul>
          </Alert>

          <Alert variant="secondary" className="mb-3">
            <h6 className="mb-2">💾 About State Persistence</h6>
            <p className="mb-0 small">
              Processing states (spinning wheels) are stored locally in your browser to persist across page refreshes. If you clear your browser cache, the visual indicators may reset, but <strong>DNS and status checks continue</strong>. The system automatically checks the real status every 30 seconds and will restore the correct state. Your domains and DNS records are never affected by clearing cache.
            </p>
          </Alert>

          <Card className="border-primary mb-0" data-mx-record-section>
            <Card.Header className="bg-primary text-white d-flex align-items-center">
              <Envelope className="me-2" size={18} />
              <span className="fw-bold">MX Record Editing</span>
            </Card.Header>
            <Card.Body>
              <h6 className="mb-3">How to Edit MX Records for Any Domain</h6>
              <ol className="mb-3">
                <li className="mb-2">
                  <strong>Click the "DNS" button</strong> on any domain in the table
                </li>
                <li className="mb-2">
                  <strong>In the DNS Setup modal</strong>, find the "Email Receiving MX Record" section
                </li>
                <li className="mb-2">
                  <strong>Click "Edit"</strong> next to the MX record
                </li>
                <li className="mb-2">
                  <strong>Change the priority</strong> (default: 10) and <strong>value</strong> to match your selected inbound provider:
                  <div className="mt-1">
                    <code>Postmark: 10 inbound.postmarkapp.com.</code><br />
                    <code>SES (us-east-1): 10 inbound-smtp.us-east-1.amazonaws.com.</code>
                  </div>
                </li>
                <li className="mb-2">
                  <strong>Click "Save"</strong> to update the MX record in Route 53
                </li>
              </ol>
              
              <Alert variant="info" className="mb-3">
                <strong>✅ Inbound provider is per-domain:</strong> Default is Postmark. SES is available for AWS-connected domains. Click the “Inbound set” badge to choose a provider and see MX instructions.
              </Alert>

              <h6 className="mb-2">What Happens Automatically:</h6>
              <ul className="mb-0">
                <li>The backend <strong>finds the Route 53 hosted zone</strong> for the domain</li>
                <li>Updates the MX record with <strong>UPSERT</strong> (creates if missing, updates if exists)</li>
                <li><strong>Handles cross-account scenarios</strong> if the domain is in a different AWS account</li>
                <li>MX record changes typically take <strong>5-60 minutes</strong> to propagate</li>
              </ul>
            </Card.Body>
          </Card>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Nameserver Update Modal */}
      <Modal show={showNameserverUpdateModal} onHide={() => {
        setShowNameserverUpdateModal(false);
        setNameserverUpdateData(null);
        setIsVerifying(false);
        setVerifyingDomain(null);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <ExclamationTriangle className="me-2 text-warning" />
            Nameservers Need Update
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <strong>The domain's nameservers don't match the Route 53 hosted zone nameservers.</strong>
          </Alert>
          
          <p className="mb-3">
            For DNS records to be visible from the internet, the domain must use the Route 53 hosted zone nameservers. 
            Currently, the domain is using different nameservers, which means DNS records created in Route 53 won't be visible.
          </p>
          
          <h6 className="mb-2">Current Nameservers:</h6>
          <ListGroup className="mb-3">
            {nameserverUpdateData?.currentNameservers?.map((ns, index) => (
              <ListGroup.Item key={index}>
                <code>{ns}</code>
              </ListGroup.Item>
            ))}
          </ListGroup>
          
          <h6 className="mb-2">Route 53 Hosted Zone Nameservers:</h6>
          <ListGroup className="mb-4">
            {nameserverUpdateData?.route53Nameservers?.map((ns, index) => (
              <ListGroup.Item key={index} variant="success">
                <code>{ns}</code>
              </ListGroup.Item>
            ))}
          </ListGroup>
          
          {nameserverUpdateData?.canUpdateAutomatically ? (
            <Alert variant="info" className="mb-3">
              <strong>✅ Automatic Update Available</strong><br />
              This domain is registered with Route 53 Domains, so we can update the nameservers automatically.
            </Alert>
          ) : (
            <Alert variant="warning" className="mb-3">
              <strong>⚠️ Manual Update Required</strong><br />
              This domain is not registered with Route 53 Domains, so you'll need to update the nameservers manually at your domain registrar.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => handleNameserverUpdateChoice(false)}>
            Skip for Now
          </Button>
          {nameserverUpdateData?.canUpdateAutomatically && (
            <Button variant="primary" onClick={() => handleNameserverUpdateChoice(true)}>
              Update Nameservers Automatically
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Sandbox Exit Readiness Verification Modal */}
      <Modal show={showSandboxVerificationModal} onHide={() => {
        setShowSandboxVerificationModal(false);
        setSandboxVerificationDomain(null);
        setSandboxVerificationResults(null);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Shield className="me-2" />
            Sandbox Exit Readiness Check
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sandboxVerificationDomain && (
            <Alert variant="info" className="mb-3">
              <strong>Checking readiness for:</strong> {sandboxVerificationDomain}
            </Alert>
          )}
          
          {!sandboxVerificationResults ? (
            <div className="text-center py-4">
              <Spinner animation="border" className="me-2" />
              <span>Checking sandbox exit requirements...</span>
            </div>
          ) : sandboxVerificationResults.error ? (
            <Alert variant="danger">
              <strong>Error:</strong> {sandboxVerificationResults.error}
            </Alert>
          ) : (
            <div>
              {(() => {
                const checks = sandboxVerificationResults.checks || [];
                const failedChecks = checks.filter(c => c.status === 'error');
                const warningChecks = checks.filter(c => c.status === 'warning');
                const passedChecks = checks.filter(c => c.status === 'success');
                const allRequirementsMet = sandboxVerificationResults.allRequirementsMet !== false && failedChecks.length === 0;
                
                return (
                  <>
                    {/* Summary Section */}
                    {!allRequirementsMet && (failedChecks.length > 0 || warningChecks.length > 0) && (
                      <Alert variant="danger" className="mb-3">
                        <strong>❌ Missing Requirements:</strong>
                        <ul className="mb-0 mt-2">
                          {failedChecks.map((check, idx) => (
                            <li key={idx}><strong>{check.step}:</strong> {check.message}</li>
                          ))}
                          {warningChecks.map((check, idx) => (
                            <li key={`warn-${idx}`}><strong>{check.step}:</strong> {check.message}</li>
                          ))}
                        </ul>
                      </Alert>
                    )}
                    
                    {allRequirementsMet && (
                      <Alert variant="success" className="mb-3">
                        <strong>✅ All Requirements Met!</strong><br />
                        Your domain authentication checks are passing and routing is ready.
                      </Alert>
                    )}
                    
                    {/* Detailed Requirements List */}
                    <h6 className="mb-3">All Requirements:</h6>
                    <ListGroup>
                      {checks.map((check, index) => {
                        const isFailed = check.status === 'error';
                        const isWarning = check.status === 'warning';
                        const isSuccess = check.status === 'success';
                        const isBounceComplaintCheck = check.step === 'Bounce/Complaint Handling';
                        
                        return (
                          <ListGroup.Item 
                            key={index} 
                            className={`d-flex justify-content-between align-items-start ${isFailed ? 'list-group-item-danger' : isWarning ? 'list-group-item-warning' : ''}`}
                            style={isFailed ? { backgroundColor: '#f8d7da', borderColor: '#f5c6cb' } : isWarning ? { backgroundColor: '#fff3cd', borderColor: '#ffeaa7' } : {}}
                          >
                            <div className="flex-grow-1">
                              <strong className={isFailed ? 'text-danger' : isWarning ? 'text-warning' : 'text-success'}>
                                {check.step}
                              </strong>
                              <div className="mt-1">
                                <div className={isFailed ? 'text-danger' : isWarning ? 'text-warning' : 'text-success'}>
                                  {check.message}
                                </div>
                                {isBounceComplaintCheck && !isSuccess && (
                                  <div className="mt-2">
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      onClick={() => handleSetupBounceComplaint(sandboxVerificationDomain)}
                                      disabled={isSettingUpBounceComplaint}
                                    >
                                      {isSettingUpBounceComplaint ? (
                                        <>
                                          <Spinner size="sm" className="me-2" animation="border" />
                                          Setting up...
                                        </>
                                      ) : (
                                        <>
                                          <Plus className="me-1" size={14} />
                                          Set Up Bounce/Complaint Handling
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                )}
                                {check.details && Object.keys(check.details).length > 0 && (
                                  <div className="mt-2">
                                    <details>
                                      <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#6c757d' }}>
                                        View Details
                                      </summary>
                                      <pre style={{ fontSize: '0.75rem', backgroundColor: '#f5f5f5', padding: '0.5rem', borderRadius: '4px', marginTop: '0.5rem', marginBottom: 0 }}>
                                        {JSON.stringify(check.details, null, 2)}
                                      </pre>
                                    </details>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="ms-3">
                              {isSuccess ? (
                                <CheckCircle className="text-success" size={24} />
                              ) : isWarning ? (
                                <ExclamationTriangle className="text-warning" size={24} />
                              ) : (
                                <XCircle className="text-danger" size={24} />
                              )}
                            </div>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                    
                    {/* Summary Stats */}
                    {checks.length > 0 && (
                      <div className="mt-3 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                        <small>
                          <strong>Summary:</strong> {passedChecks.length} passed, {warningChecks.length} warnings, {failedChecks.length} failed
                        </small>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowSandboxVerificationModal(false);
            setSandboxVerificationDomain(null);
            setSandboxVerificationResults(null);
          }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DMARC Settings Modal */}
      <Modal show={showDMARCModal} onHide={() => {
        setShowDMARCModal(false);
        setDmarcEditingDomain(null);
        setDmarcEmail('');
        setDmarcPolicy('quarantine');
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Shield className="me-2" />
            Edit DMARC Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {dmarcEditingDomain && (
            <Alert variant="info" className="mb-3">
              <strong>Domain:</strong> {dmarcEditingDomain}
            </Alert>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>DMARC Email Address</strong>
                <small className="text-muted ms-2">(Where to send DMARC reports)</small>
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="dmarc@example.com"
                value={dmarcEmail}
                onChange={(e) => setDmarcEmail(e.target.value)}
              />
              <Form.Text className="text-muted">
                This email address will receive DMARC aggregate reports. Example: dmarc@{dmarcEditingDomain || 'yourdomain.com'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <strong>DMARC Policy</strong>
                <small className="text-muted ms-2">(How to handle emails that fail DMARC)</small>
              </Form.Label>
              <Form.Select
                value={dmarcPolicy}
                onChange={(e) => setDmarcPolicy(e.target.value)}
              >
                <option value="none">None - Monitor only (recommended for testing)</option>
                <option value="quarantine">Quarantine - Send to spam folder</option>
                <option value="reject">Reject - Block the email completely</option>
              </Form.Select>
              <Form.Text className="text-muted">
                <strong>None:</strong> Monitor emails but don't take action (best for testing)<br />
                <strong>Quarantine:</strong> Mark failed emails as spam<br />
                <strong>Reject:</strong> Block failed emails completely (use with caution)
              </Form.Text>
            </Form.Group>

            <Alert variant="warning" className="mb-0">
              <strong>⚠️ Important:</strong> DNS changes may take 5-60 minutes to propagate. 
              The DMARC record will be updated in Route 53 immediately, but it may take time for DNS servers worldwide to see the change.
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowDMARCModal(false);
            setDmarcEditingDomain(null);
            setDmarcEmail('');
            setDmarcPolicy('quarantine');
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveDMARC}
            disabled={isUpdatingDMARC || !dmarcEmail}
          >
            {isUpdatingDMARC ? 'Updating...' : 'Update DMARC'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Existing DNS Records Confirmation Modal */}
      <Modal show={showExistingRecordsModal} onHide={() => {
        setShowExistingRecordsModal(false);
        setExistingRecordsData(null);
        setIsVerifying(false);
        setVerifyingDomain(null);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <ExclamationTriangle className="me-2 text-warning" />
            Existing DNS Records Found
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <strong>Found {existingRecordsData?.existingRecords?.length || 0} existing DNS record(s)</strong> that may conflict with provider-auth setup for <strong>{existingRecordsData?.domain}</strong>.
          </Alert>
          
          <h6 className="mb-3">Existing Records:</h6>
          <ListGroup className="mb-4">
            {existingRecordsData?.existingRecords?.map((record, index) => (
              <ListGroup.Item key={index}>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <strong>{record.name}</strong> ({record.type})
                    {record.isGoogleRecord && (
                      <Badge bg="info" className="ms-2">Google DNS</Badge>
                    )}
                    <div className="mt-1">
                      <small className="text-muted">
                        {record.values.map((v, i) => (
                          <div key={i}>{v}</div>
                        ))}
                      </small>
                    </div>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>

          <Alert variant="info" className="mb-3">
            <h6 className="mb-2">How would you like to proceed?</h6>
            <ul className="mb-0">
              <li><strong>Delete existing records:</strong> Remove all existing DNS records and create fresh provider-auth records. Use this if you want to start fresh (e.g., domain was transferred to Route 53).</li>
              <li><strong>Keep and update:</strong> Keep existing records and update/add provider-auth records. Use this if you want to preserve existing DNS records (e.g., Google Workspace email).</li>
            </ul>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowExistingRecordsModal(false);
            setExistingRecordsData(null);
            setIsVerifying(false);
            setVerifyingDomain(null);
          }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleExistingRecordsChoice('delete')}>
            <XCircle className="me-1" />
            Delete Existing & Create Fresh
          </Button>
          <Button variant="primary" onClick={() => handleExistingRecordsChoice('keep')}>
            <CheckCircle className="me-1" />
            Keep & Update Records
          </Button>
        </Modal.Footer>
      </Modal>

      <DomainForwardingModal
        show={showForwardingModal}
        onHide={() => {
          setShowForwardingModal(false);
          setForwardingDomainObj(null);
        }}
        domain={forwardingDomainObj}
        onSaved={loadDomains}
      />

      {/* Help Sidebar */}
      <HelpSidebar 
        pageKey="domains"
        isOpen={showHelpSidebar}
        onToggle={() => setShowHelpSidebar(!showHelpSidebar)}
      />
    </Container>
    </>
  );
};

export default Domains;
